import React, { useEffect, useMemo, useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'

import cogoToast from 'cogo-toast'
import DOMPurify from 'dompurify'
import Moment from 'moment'
import PropTypes from 'prop-types'

import CasesService from '../../../services/CasesService'
import ProviderService from '../../../services/ProviderService'
import TasksService from '../../../services/TasksService'
import { ARCHETYPE_CATEGORY_PRIORITIES } from '../../../utils/Constants'
import { getToken } from '../../../utils/getToken'
import Permissions, { CAPABILITIES } from '../../../utils/Permissions'

import DocumentViewerPane from './DocumentViewerPane'
import { findMatchingEntity } from './entityMatcher'
import HitlReviewDrawer, { matchesCategory } from './HitlReviewDrawer'

import './HitlReviewDrawer.scss'

/**
 * Standardizes extracted date strings into user-facing mm/dd/YYYY format.
 */
export function formatProposedDate(val) {
  if (!val || (typeof val !== 'string' && typeof val !== 'number')) return ''
  const str = String(val).trim()
  if (!str) return ''
  // If already MM/DD/YYYY, standardize with 2-digit padding
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
    const [m, d, y] = str.split('/')
    return `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`
  }
  const m = Moment(
    str,
    [
      'YYYY-MM-DD',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DDTHH:mm:ssZ',
      'YYYY-MM-DDTHH:mm:ss.SSS',
      'YYYY-MM-DDTHH:mm:ss.SSSZ',
      'MM/DD/YYYY',
      'M/D/YYYY',
      Moment.ISO_8601,
    ],
    true,
  )
  if (m.isValid() && m.year() > 1900 && m.year() < 2100) {
    return m.format('MM/DD/YYYY')
  }
  const mFallback = Moment(str)
  if (mFallback.isValid() && mFallback.year() > 1900 && mFallback.year() < 2100) {
    return mFallback.format('MM/DD/YYYY')
  }
  return str
}

/**
 * Normalizes a text string for fuzzy clustering (strips punctuation, common titles, extra spaces).
 */
function normalizeTextKey(str = '') {
  return str
    .toLowerCase()
    .replace(
      /\b(dr|m\.?d\.?|d\.?o\.?|o\.?d\.?|p\.?t\.?|d\.?p\.?t\.?|d\.?c\.?|n\.?p\.?|p\.?a\.?-?c?|p\.?a\.?|inc|llc|pllc|facs|pc|corp|ltd|md|od|do|dc|pt)\b/gi,
      '',
    )
    .replace(/[^a-z0-9]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Checks if a diagnosis or finding string represents a negative / normal diagnostic result.
 */
export function isNegativeFindingText(text = '') {
  if (!text || typeof text !== 'string') return false
  const clean = text.trim().toLowerCase()
  const patterns = [
    /\bno\s+(acute|evidence\s+of|focal|intracranial|osseous|fracture|hemorrhage|herniation|mass|lesion|abnormalit|defect|stenosis|compression)/i,
    /\bnegative\s+for\b/i,
    /\bwithout\s+(acute|evidence\s+of|definite|significant)\b/i,
    /\bunremarkable\b/i,
    /\bnormal\s+(ct|mri|x-?ray|imaging|study|exam|scan)\b/i,
    /\bruled?\s*out\b/i,
    /\bdenies\s+(pain|headache|loss\s+of\s+consciousness|nausea)\b/i,
    /\bno\s+acute\s+(cervical|lumbar|thoracic|spine|bone|joint)\b/i,
  ]
  return patterns.some(p => p.test(clean))
}

/**
 * Detects whether extracted text is a radiology finding, MRI/CT/X-ray impression, or anatomical tear/imaging finding
 * rather than a true clinical diagnosis.
 */
export function isRadiologyImpressionText(text = '') {
  if (!text || typeof text !== 'string') return false
  const cleanText = text.trim().toLowerCase()
  if (cleanText.length < 3) return false

  // Modality keywords in the extracted text itself
  if (
    /\b(?:x-?rays?|radiograph(?:y|s)?|plain\s+films?|views?\s+of|mri|ct\s+scan|cat\s+scan|ultrasound|sonogram|arthrogram|fluoroscop\w*|emg|ncs|imaging\s+study|roentgenogram)\b/i.test(
      cleanText,
    )
  ) {
    return true
  }

  // Common imaging finding / anatomical tear / impression patterns
  const imagingPatterns = [
    /\b(?:complete\s+)?(?:full|partial)[- ]thickness\s+tear\b/i,
    /\b(?:supraspinatus|infraspinatus|subscapularis|teres\s+minor)\b/i,
    /\b(?:delaminating\s+tear|longitudinal\s+tear)\b/i,
    /\b(?:bone\s+marrow\s+edema|bone\s+contusion|joint\s+effusion)\b/i,
    /\b(?:axial|coronal|sagittal|weighted|t1|t2|flair|contrast\s+enhancement)\b/i,
    /\bimpression\s*:\s*/i,
    /\bfindings\s+consistent\s+with\b/i,
  ]

  return imagingPatterns.some(p => p.test(cleanText))
}

/**
 * Detects whether extracted text is an accident narrative or event description
 * rather than a true clinical diagnosis (e.g. "employee tripped over dain grate, injuring left knee and elbow").
 */
export function isAccidentNarrativeText(text = '', injuryDescriptions = []) {
  if (!text || typeof text !== 'string') return false
  const clean = text.trim().toLowerCase()
  if (clean.length < 5) return false

  // Check if it matches or is contained inside any extracted injury descriptions
  if (Array.isArray(injuryDescriptions) && injuryDescriptions.length > 0) {
    const ANATOMICAL_AND_COMMON_WORDS = new Set([
      'left',
      'right',
      'bilateral',
      'anterior',
      'posterior',
      'medial',
      'lateral',
      'knee',
      'elbow',
      'shoulder',
      'back',
      'ankle',
      'wrist',
      'hip',
      'leg',
      'arm',
      'hand',
      'foot',
      'neck',
      'head',
      'spine',
      'lumbar',
      'cervical',
      'thoracic',
      'upper',
      'lower',
      'joint',
      'extremity',
      'and',
      'the',
      'for',
      'with',
      'injury',
      'pain',
    ])
    const cleanWords = clean
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3)
    const nonAnatomicalClean = cleanWords.filter(w => !ANATOMICAL_AND_COMMON_WORDS.has(w))

    for (const desc of injuryDescriptions) {
      if (!desc) continue
      const cleanDesc = desc.trim().toLowerCase()
      // If the candidate text is substantially identical or is the entire narrative
      if (cleanDesc.length >= 15 && (cleanDesc === clean || (clean.length >= 25 && cleanDesc.includes(clean)))) {
        return true
      }
      // Check token overlap on non-anatomical, non-trivial words
      const descWords = cleanDesc
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length >= 3)
      const nonAnatomicalDesc = descWords.filter(w => !ANATOMICAL_AND_COMMON_WORDS.has(w))

      if (nonAnatomicalClean.length >= 2 && nonAnatomicalDesc.length >= 2) {
        const setDesc = new Set(nonAnatomicalDesc)
        const overlap = nonAnatomicalClean.filter(w => setDesc.has(w)).length
        if (overlap / nonAnatomicalClean.length >= 0.5) {
          return true
        }
      }
    }
  }

  // Check generic non-clinical bodily injury labels that are descriptions rather than diagnoses
  if (
    /\b(?:left|right|bilateral)?\s*(?:knee|elbow|shoulder|back|ankle|wrist|hip|leg|arm|hand|foot|neck|head)\s+injury\b/i.test(
      clean,
    )
  ) {
    return true
  }

  // Check common narrative patterns describing an accident event
  if (
    /\b(employee|worker|patient|claimant|individual|driver)\s+(tripped|fell|slipped|struck|hit|injured|twisted|involved|reported|states|was|were)\b/i.test(
      clean,
    )
  ) {
    return true
  }
  if (
    /\b(tripped over|tripped on|fell down|fell off|slipped on|slipped and|motor vehicle accident|car accident|hit by|struck by|lifting heavy|while working|while lifting|while carrying)\b/i.test(
      clean,
    )
  ) {
    return true
  }

  return false
}

/**
 * Identifies pre-existing or degenerative conditions that should be classified as non-industrial / incidental.
 */
export function isPreExistingConditionText(text = '', code = '') {
  const clean = `${text || ''} ${code || ''}`.trim().toLowerCase()
  const patterns = [
    /\b(cataract|glaucoma|presbyopia|macular\s+degeneration)\b/i,
    /\b(osteoarthritis|degenerative\s+disc|degenerative\s+joint|spondylosis|spondylolisthesis)\b/i,
    /\b(hypertension|hyperlipidemia|type\s+2\s+diabetes|essential\s+hypertension)\b/i,
    /\bpre-?existing\b/i,
    /\bnon-?industrial\b/i,
    /\bage-?related\b/i,
    /\bprior\s+to\s+injury\b/i,
  ]
  return patterns.some(p => p.test(clean))
}

/**
 * Capitalizes the first letter of each sentence while preserving acronyms and existing uppercase letters.
 * e.g. "employee tripped over a cord..." -> "Employee tripped over a cord..."
 */
export function toSentenceCase(text = '') {
  if (!text || typeof text !== 'string') return ''
  const trimmed = text.trim()
  if (!trimmed) return ''
  return trimmed.replace(/(^\s*|[.!?]\s+)([a-z])/g, (match, prefix, char) => `${prefix}${char.toUpperCase()}`)
}

export const isStatuteOrLegalCitation = str => {
  if (!str) return false
  return /\b(?:c\.?r\.?s\.?|statute|revised\s+statute|section|sec\.|rule|code|ann\.|tit\.|title)\b/i.test(str)
}

export const isWageOrStatuteQuestion = text => {
  if (!text) return false
  return /\b(?:wages?|paid\s+for\s+(?:the\s+)?doi|continued\s+per|c\.?r\.?s\.?|statute)\b/i.test(text)
}

export const cleanCapturedDate = rawStr => {
  if (!rawStr) return ''
  let cleaned = rawStr.replace(/\s*([/.-])\s*/g, '$1').trim()
  const truncatedYearMatch = cleaned.match(/^(\d{1,2}[/.-]\d{1,2}[/.-])202$/)
  if (truncatedYearMatch) {
    cleaned = `${truncatedYearMatch[1]}2025`
  }
  return cleaned
}

export const isValidCalendarDate = dateStr => {
  if (!dateStr) return false
  const s = dateStr.trim()
  if (isStatuteOrLegalCitation(s)) return false

  const slashDashMatch = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/)
  if (slashDashMatch) {
    const p1 = parseInt(slashDashMatch[1], 10)
    const p2 = parseInt(slashDashMatch[2], 10)
    const p3 = slashDashMatch[3]

    let year = parseInt(p3, 10)
    if (p3.length === 3) {
      if (p3 === '202' || p3 === '201' || p3 === '199') {
        year = p3.startsWith('20') ? 2020 : 1990
      } else {
        return false // e.g. 124 in 8-42-124
      }
    } else if (p3.length === 2) {
      year = year < 50 ? 2000 + year : 1900 + year
    }

    if (year < 1920 || year > 2050) return false
    const month = p1
    const day = p2
    if (month < 1 || month > 12) return false
    if (day < 1 || day > 31) return false
    return true
  }

  const isoMatch = s.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/)
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10)
    const month = parseInt(isoMatch[2], 10)
    const day = parseInt(isoMatch[3], 10)
    if (year < 1920 || year > 2050) return false
    if (month < 1 || month > 12) return false
    if (day < 1 || day > 31) return false
    return true
  }

  const parsed = Date.parse(s)
  if (!isNaN(parsed)) {
    const d = new Date(parsed)
    const y = d.getFullYear()
    return y >= 1920 && y <= 2050
  }

  return false
}

/**
 * Extracts explicit Date of Injury from text blocks when Bedrock omitted it or extracted null.
 * Strictly distinguishes Date of Injury from Date of Treatment, Date Employer Notified, DOB, etc.
 */
export function extractExplicitDateOfInjury(docOrDocs) {
  if (!docOrDocs) return null
  if (Array.isArray(docOrDocs)) {
    for (const d of docOrDocs) {
      if (!d) continue
      const res = extractExplicitDateOfInjury(d)
      if (res) return res
    }
    return null
  }

  const doc = docOrDocs

  const labelOnlyPattern =
    /\b(?:date\s*(?:and|\/|&)?\s*(?:time\s+)?of\s+injury(?:\s*[/,]\s*(?:illness|onset|exposure|accident))?|date\s*[/&]\s*time\s+of\s+injury|injury(?:\s*[/,]\s*illness)?\s+date|injury\s*[/&]\s*accident\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date|date\s+injury\s+occurred|date\s+injury\s+began|date\s+of\s+incident|incident\s+date|date\s+of\s+occurrence|occurrence\s+date)\b|(?:\bdoi(?:\s*[:#=\-\]]|\s*$|(?=\s*\([^)]*\))|(?=\s+\d))|(?<=^\s*)doi\b)/i

  const dateValuePattern =
    /\b(\d{1,2}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*(?:\d{4}|\d{2}|202)\b|\d{4}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*\d{1,2}|(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)[.]?\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4})\b/i

  const competingDateLabels =
    /\b(?:date\s+of\s+birth|birth\s*date|dob|date\s+of\s+hire|hire\s*date|date\s+employer\s+notified|employer\s+notified|date\s+employer|notified|date\s+of\s+treatment|treatment\s+date|date\s+of\s+death|notice\s+date|date\s+of\s+notice)\b/i

  // 1. Check line blocks if available
  if (doc.lineBlocks && Array.isArray(doc.lineBlocks)) {
    for (let i = 0; i < doc.lineBlocks.length; i++) {
      const block = doc.lineBlocks[i]
      const text = (block.text || '').trim()
      if (!text || isWageOrStatuteQuestion(text)) continue

      const labelMatch = text.match(labelOnlyPattern)
      let isHeaderMatch = Boolean(labelMatch)
      let headerText = text
      let searchStartIndex = i + 1
      const preferredColumnX = typeof block.left === 'number' ? block.left : null

      if (!isHeaderMatch && /^\s*injury(?:\s*[/,]\s*illness)?\s*$/i.test(text)) {
        for (let k = i + 1; k < Math.min(i + 15, doc.lineBlocks.length); k++) {
          const cand = doc.lineBlocks[k]
          if (cand.page && block.page && cand.page !== block.page) break
          const candText = (cand.text || '').trim()
          if (/^\s*date\s*$/i.test(candText)) {
            const dx = Math.abs((cand.left || 0) - (block.left || 0))
            if (dx < 0.15 || typeof cand.left !== 'number') {
              isHeaderMatch = true
              headerText = `${text} ${candText}`
              searchStartIndex = k + 1
              break
            }
          }
        }
      }

      if (isHeaderMatch) {
        if (labelMatch) {
          // First check if date is on the same line after the label
          const afterLabel = text.slice(labelMatch.index + labelMatch[0].length)
          const dateInLine = afterLabel.match(dateValuePattern)
          if (dateInLine && dateInLine[1]) {
            const textBeforeDate = afterLabel.slice(0, dateInLine.index)
            const candidateDate = cleanCapturedDate(dateInLine[1])
            if (
              !competingDateLabels.test(textBeforeDate) &&
              isValidCalendarDate(candidateDate) &&
              !isStatuteOrLegalCitation(afterLabel.slice(0, dateInLine.index + dateInLine[0].length))
            ) {
              const endIndex = labelMatch.index + labelMatch[0].length + dateInLine.index + dateInLine[0].length
              const matchedSnippet = text.slice(labelMatch.index, endIndex).trim()
              if (!isWageOrStatuteQuestion(matchedSnippet)) {
                return {
                  date: candidateDate,
                  snippet: matchedSnippet,
                  page: block.page,
                  lineNumber: block.lineNumber,
                  documentId: doc.id,
                  documentName: doc.fileName,
                }
              }
            }
          }

          // Check if date was on the same line before the label
          const beforeLabel = text.slice(0, labelMatch.index)
          const dateBefore = beforeLabel.match(dateValuePattern)
          if (dateBefore && dateBefore[1]) {
            const textAfterDate = beforeLabel.slice(dateBefore.index + dateBefore[0].length)
            const candidateDate = cleanCapturedDate(dateBefore[1])
            if (!competingDateLabels.test(textAfterDate) && isValidCalendarDate(candidateDate)) {
              const matchedSnippet = text.slice(dateBefore.index, labelMatch.index + labelMatch[0].length).trim()
              if (!isWageOrStatuteQuestion(matchedSnippet)) {
                return {
                  date: candidateDate,
                  snippet: matchedSnippet,
                  page: block.page,
                  lineNumber: block.lineNumber,
                  documentId: doc.id,
                  documentName: doc.fileName,
                }
              }
            }
          }
        }

        // Check subsequent blocks (up to 15 blocks ahead for multi-column or separated values)
        for (let j = searchStartIndex; j < Math.min(searchStartIndex + 15, doc.lineBlocks.length); j++) {
          const nextBlock = doc.lineBlocks[j]
          const nextText = (nextBlock.text || '').trim()
          if (!nextText || isWageOrStatuteQuestion(nextText)) continue
          if (nextBlock.page && block.page && nextBlock.page !== block.page) break

          const nextM = nextText.match(dateValuePattern)
          if (nextM && nextM[1]) {
            const textBeforeDate = nextText.slice(0, nextM.index)
            const candidateDate = cleanCapturedDate(nextM[1])
            if (
              competingDateLabels.test(textBeforeDate) ||
              competingDateLabels.test(nextText) ||
              !isValidCalendarDate(candidateDate) ||
              isStatuteOrLegalCitation(nextText)
            ) {
              continue
            }
            if (preferredColumnX !== null && typeof nextBlock.left === 'number') {
              const dx = Math.abs(nextBlock.left - preferredColumnX)
              if (dx > 0.15) {
                continue
              }
            }
            return {
              date: candidateDate,
              snippet: `${headerText.trim()}: ${candidateDate}`.trim(),
              page: nextBlock.page || block.page,
              lineNumber: nextBlock.lineNumber,
              documentId: doc.id,
              documentName: doc.fileName,
            }
          }
        }
      }
    }
  }

  // 2. Check doc.textContent (handling line-by-line, multi-line, and continuous single-line OCR streams)
  if (doc.textContent) {
    const rawContent = doc.textContent
    const lines = rawContent.split('\n')
    let currentPage = 1

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      const pageMatch = line.match(/^---\s*Page\s+(\d+)\s*---$/i)
      if (pageMatch) {
        currentPage = parseInt(pageMatch[1], 10)
        continue
      }
      const trimmed = line.trim()
      if (!trimmed || isWageOrStatuteQuestion(trimmed)) continue

      const labelMatch = trimmed.match(labelOnlyPattern)
      let isHeaderMatch = Boolean(labelMatch)
      let headerText = trimmed
      let searchStartIndex = i + 1

      if (!isHeaderMatch && /^\s*injury(?:\s*[/,]\s*illness)?\s*$/i.test(trimmed)) {
        for (let k = i + 1; k < Math.min(i + 15, lines.length); k++) {
          const candText = lines[k].trim()
          if (/^---\s*Page/i.test(candText)) break
          if (/^\s*date\s*$/i.test(candText)) {
            isHeaderMatch = true
            headerText = `${trimmed} ${candText}`
            searchStartIndex = k + 1
            break
          }
        }
      }

      if (isHeaderMatch) {
        if (labelMatch) {
          // Look in window after label (up to 250 characters)
          const windowAfter = trimmed.slice(
            labelMatch.index + labelMatch[0].length,
            labelMatch.index + labelMatch[0].length + 250,
          )
          const datesAfter = [...windowAfter.matchAll(new RegExp(dateValuePattern.source, 'gi'))]
          if (datesAfter.length > 0) {
            for (let dIdx = 0; dIdx < datesAfter.length; dIdx++) {
              const d = datesAfter[dIdx]
              const precedingAll = windowAfter.slice(0, d.index)
              const prevEnd = dIdx > 0 ? datesAfter[dIdx - 1].index + datesAfter[dIdx - 1][0].length : 0
              const immediatePreceding = windowAfter.slice(prevEnd, d.index)

              if (datesAfter.length === 1 && competingDateLabels.test(precedingAll)) {
                continue
              }
              if (competingDateLabels.test(immediatePreceding)) {
                continue
              }
              const candidateDate = cleanCapturedDate(d[1])
              if (!isValidCalendarDate(candidateDate) || isStatuteOrLegalCitation(immediatePreceding)) continue
              const snippetEnd = labelMatch.index + labelMatch[0].length + d.index + d[0].length
              let matchedSnippet = trimmed.slice(labelMatch.index, snippetEnd).replace(/\s+/g, ' ').trim()
              if (competingDateLabels.test(matchedSnippet)) {
                matchedSnippet = `${labelMatch[0]} ${candidateDate}`.trim()
              }
              if (isWageOrStatuteQuestion(matchedSnippet)) continue
              return {
                date: candidateDate,
                snippet: matchedSnippet,
                page: currentPage,
                lineNumber: i + 1,
                documentId: doc.id,
                documentName: doc.fileName,
              }
            }
          }

          // Look in window before label (up to 120 characters)
          const windowBefore = trimmed.slice(Math.max(0, labelMatch.index - 120), labelMatch.index)
          const datesBefore = [...windowBefore.matchAll(new RegExp(dateValuePattern.source, 'gi'))]
          if (datesBefore.length > 0) {
            const lastD = datesBefore[datesBefore.length - 1]
            const following = windowBefore.slice(lastD.index + lastD[0].length)
            const candidateDate = cleanCapturedDate(lastD[1])
            if (
              !competingDateLabels.test(following) &&
              isValidCalendarDate(candidateDate) &&
              !isStatuteOrLegalCitation(following)
            ) {
              const snippetStart = Math.max(0, labelMatch.index - 120) + lastD.index
              const matchedSnippet = trimmed
                .slice(snippetStart, labelMatch.index + labelMatch[0].length)
                .replace(/\s+/g, ' ')
                .trim()
              if (!isWageOrStatuteQuestion(matchedSnippet)) {
                return {
                  date: candidateDate,
                  snippet: matchedSnippet,
                  page: currentPage,
                  lineNumber: i + 1,
                  documentId: doc.id,
                  documentName: doc.fileName,
                }
              }
            }
          }
        }

        // Check subsequent lines if multi-line
        if (lines.length > 1) {
          for (let j = searchStartIndex; j < Math.min(searchStartIndex + 15, lines.length); j++) {
            const nextTrimmed = lines[j].trim()
            if (!nextTrimmed || isWageOrStatuteQuestion(nextTrimmed)) continue
            if (nextTrimmed.match(/^---\s*Page/i)) break

            const nextM = nextTrimmed.match(dateValuePattern)
            if (nextM && nextM[1]) {
              const textBeforeDate = nextTrimmed.slice(0, nextM.index)
              const candidateDate = cleanCapturedDate(nextM[1])
              if (
                competingDateLabels.test(textBeforeDate) ||
                competingDateLabels.test(nextTrimmed) ||
                !isValidCalendarDate(candidateDate) ||
                isStatuteOrLegalCitation(nextTrimmed)
              ) {
                continue
              }
              return {
                date: candidateDate,
                snippet: `${headerText} ${candidateDate}`.replace(/\s+/g, ' ').trim(),
                page: currentPage,
                lineNumber: j + 1,
                documentId: doc.id,
                documentName: doc.fileName,
              }
            }
          }
        }
      }
    }

    // 3. Fallback: Search full document text stream
    const globalLabelMatch = rawContent.match(labelOnlyPattern)
    if (globalLabelMatch) {
      const windowAfter = rawContent.slice(
        globalLabelMatch.index + globalLabelMatch[0].length,
        globalLabelMatch.index + globalLabelMatch[0].length + 300,
      )
      const datesAfter = [...windowAfter.matchAll(new RegExp(dateValuePattern.source, 'gi'))]
      if (datesAfter.length > 0) {
        for (let dIdx = 0; dIdx < datesAfter.length; dIdx++) {
          const d = datesAfter[dIdx]
          const precedingAll = windowAfter.slice(0, d.index)
          const prevEnd = dIdx > 0 ? datesAfter[dIdx - 1].index + datesAfter[dIdx - 1][0].length : 0
          const immediatePreceding = windowAfter.slice(prevEnd, d.index)

          if (datesAfter.length === 1 && competingDateLabels.test(precedingAll)) {
            continue
          }
          if (competingDateLabels.test(immediatePreceding)) {
            continue
          }
          const candidateDate = cleanCapturedDate(d[1])
          if (!isValidCalendarDate(candidateDate) || isStatuteOrLegalCitation(immediatePreceding)) continue
          const snippetEnd = globalLabelMatch.index + globalLabelMatch[0].length + d.index + d[0].length
          let snippet = rawContent.slice(globalLabelMatch.index, snippetEnd).replace(/\s+/g, ' ').trim()
          if (competingDateLabels.test(snippet)) {
            snippet = `${globalLabelMatch[0]} ${candidateDate}`.trim()
          }
          if (isWageOrStatuteQuestion(snippet)) continue

          let page = 1
          let lineNum = 1
          const linesUpToMatch = rawContent.slice(0, globalLabelMatch.index).split('\n')
          lineNum = linesUpToMatch.length
          for (const l of linesUpToMatch) {
            const pg = l.match(/^---\s*Page\s+(\d+)\s*---$/i)
            if (pg) page = parseInt(pg[1], 10)
          }

          return {
            date: candidateDate,
            snippet,
            page,
            lineNumber: lineNum,
            documentId: doc.id,
            documentName: doc.fileName,
          }
        }
      }
    }
  }

  return null
}

/**
 * Extracts a normalized pathology key for diagnosis clustering across multiple visits and documents.
 */
export function getDiagnosisPathologyKey(text = '', code = '') {
  const combined = `${text || ''} ${code || ''}`.toLowerCase()
  if (/subdural\s+hematoma/i.test(combined)) return 'subdural hematoma'
  if (/retinal\s+hemorrhage/i.test(combined)) return 'retinal hemorrhage'
  if (/concussion/i.test(combined)) return 'concussion'
  if (/cervical.*sprain|sprain.*cervical/i.test(combined)) return 'cervical sprain'
  if (/rotator\s+cuff/i.test(combined)) return 'rotator cuff'
  if (/lumbar.*sprain|sprain.*lumbar/i.test(combined)) return 'lumbar sprain'
  if (/forearm.*contusion|contusion.*forearm/i.test(combined)) return 'forearm contusion'
  if (/lower\s+leg.*abrasion|abrasion.*lower\s+leg/i.test(combined)) return 'lower leg abrasion'

  if (code && typeof code === 'string') {
    const cleanCode = code
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
    if (cleanCode.length >= 3) {
      return cleanCode.slice(0, 4)
    }
  }

  const clean = (text || '')
    .toLowerCase()
    .replace(/-?\s*[a-z\s.,]+,\s*(md|od|do|pt|dc)\s*\(\d{2}\/\d{2}\/\d{4}\)/gi, '')
    .replace(/\b(initial\s+encounter|subsequent\s+encounter|sequela)\b/gi, '')
    .replace(/\b(due\s+to\s+concussion|with\s+loss\s+of\s+consciousness.*)\b/gi, '')
    .replace(/\b(acute|chronic|thin\s+parafalcine|closed|traumatic)\b/gi, '')
    .replace(/\b(as\s+of\s+\d{2}\/\d{2}\/\d{4})\b/gi, '')
    .replace(/[^a-z0-9]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')

  return clean || 'unknown-diag'
}

/**
 * Normalizes provider names and credentials to cluster variants (e.g. "Kirk Matoba, OD" vs "Kirk Matoba").
 */
export function getProviderClusterKey(provName = '', npi = '') {
  if (npi && String(npi).trim().length >= 8) {
    return `npi-${String(npi).trim()}`
  }
  return normalizeTextKey(provName)
}

/**
 * Chooses the most canonical / authoritative diagnosis from a multi-occurrence cluster.
 */
export function pickBestCanonicalDiagnosis(items = []) {
  if (!items || items.length === 0) return null
  const withCode = items.find(i => i.attributes?.code && String(i.attributes.code).trim().length > 0)
  if (withCode) return withCode
  return [...items].sort((a, b) => (b.extractedValue || '').length - (a.extractedValue || '').length)[0]
}

/**
 * Chooses the most canonical provider entity from a cluster.
 */
export function pickBestCanonicalProvider(items = []) {
  if (!items || items.length === 0) return null
  const withNpi = items.find(i => i.attributes?.npi && String(i.attributes.npi).trim().length > 0)
  if (withNpi) return withNpi
  const withPractice = items.find(i => i.attributes?.practiceName)
  if (withPractice) return withPractice
  return [...items].sort((a, b) => (b.extractedValue || '').length - (a.extractedValue || '').length)[0]
}

/**
 * Detects whether a string represents a medical board certification, credential,
 * specialty qualification, or job title rather than an actual healthcare provider or facility.
 */
export function stripCertificationOrTitle(text = '') {
  if (!text || typeof text !== 'string') return ''
  const trimmed = text.trim()
  if (!trimmed) return ''

  // Form questions and administrative questionnaire fields
  if (
    /\?$/.test(trimmed) ||
    /\b(?:admitted\s+to\s+hospital|hospitalized|was\s+patient|treated\s+in\s+emergency|return(?:ed)?\s+to\s+work|first\s+aid|initial\s+treatment)\b/i.test(
      trimmed,
    )
  ) {
    return ''
  }

  // Clinical comparison / prior imaging narrative statements
  if (
    /\b(?:no\s+prior|available\s+for\s+comparison|prior\s+(?:imaging|studies|films|scans)|studies\s+(?:are\s+)?available|none\s+available|none\s+noted)\b/i.test(
      trimmed,
    )
  ) {
    return ''
  }

  // Pure modality names and document report titles
  if (
    /^(?:magnetic\s+resonance\s+imaging|computed\s+tomography|digital\s+radiography|x-?rays?|ultrasound|fluoroscopy|mammograph(?:y)?|nuclear\s+medicine|bone\s+scan|electromyograph(?:y)?|nerve\s+conduction)(\s*\([^)]*\))?(\s*report|\s*study|\s*examination|\s*findings)?$/i.test(
      trimmed,
    ) ||
    /\b(?:mri|ct|x-?ray|ultrasound|radiology|imaging)\s+report\b/i.test(trimmed)
  ) {
    return ''
  }

  const suffixRegex =
    /(?:,\s*|\s*-\s*|\s*\(\s*|\s+)\b(?:staff\s+(?:radiologist|physician|surgeon|doctor)|attending\s+(?:physician|surgeon|doctor)|board\s+(?:certified|certification|eligible)[^\n,()]*|certified\s+(?:in|by)\s+[^,()]+|subspecialty\s+certified\s+[^,()]+|fellow\s+of\s+[^,()]+|diplomate(?:\s+in\s+[^,()]+|\s+of\s+[^,()]+|\b)|accredit(?:ed|ation)(?:\s+(?:by|with))?(?:\s+(?:the\s+)?[^,()]+)?|acr(?:\s+(?:accredit(?:ed|ation)|certified|facility|center))?|american\s+(?:board|college|academy|association|society|institute)\s+of\s+[^,()]+|national\s+(?:board|committee|association|society|academy|college)\s+of\s+[^,()]+|state\s+board\s+of\s+[^,()]+|college\s+of\s+american\s+pathologists|joint\s+commission|carf|aaahc|clia|mqsa)\b[^\n,()]*\s*\)?/i

  const prefixRegex =
    /^\s*\b(?:staff\s+(?:radiologist|physician|surgeon|doctor)|attending\s+(?:physician|surgeon|doctor)|board\s+certified(?:\s+in\s+[^,()]+)?)\b(?:,\s*|\s*-\s*|\s*:\s*|\s+)/i

  const pureRegex =
    /^\s*\b(?:staff\s+(?:radiologist|physician|surgeon|doctor)|attending\s+(?:physician|surgeon|doctor)|board\s+(?:certified|certification|eligible)[^\n,()]*|certified\s+(?:in|by)\s+[^,()]+|subspecialty\s+certified\s+[^,()]+|fellow\s+of\s+[^,()]+|diplomate(?:\s+in\s+[^,()]+|\s+of\s+[^,()]+|\b)|accredit(?:ed|ation)(?:\s+(?:by|with)\s+(?:the\s+)?[^,()]+|\s*$)|(?:acr|joint\s+commission)\s+accredit(?:ed|ation)(?:\s+facility)?|american\s+(?:board|college|academy|association|society|institute)\s+of\s+[^,()]+|national\s+(?:board|committee|association|society|academy|college)\s+of\s+[^,()]+|state\s+board\s+of\s+[^,()]+|college\s+of\s+american\s+pathologists|joint\s+commission|carf|aaahc|clia|mqsa)\b[^\n,]*$/i

  const hasFacilityNoun =
    /\b(?:center|imaging|clinic|hospital|practice|partners|associates|group|health\s+system)\b/i.test(trimmed)
  const isExplicitAccreditationBadge =
    /\b(?:accredit(?:ed|ation)\s+(?:by|with)|acr\s+accredit|joint\s+commission\s+accredit)\b/i.test(trimmed)

  if (!hasFacilityNoun || isExplicitAccreditationBadge) {
    if (pureRegex.test(trimmed)) return ''
  }

  return trimmed
    .replace(suffixRegex, '')
    .replace(prefixRegex, '')
    .replace(/^[,:\s-]+|[,:\s-]+$/g, '')
    .trim()
}

/**
 * Detects whether a string represents a medical board certification, credential,
 * specialty qualification, or job title rather than an actual healthcare provider or facility.
 */
export function isCertificationOrCredential(text = '') {
  if (!text || typeof text !== 'string') return false
  const clean = text.trim()
  if (!clean) return false

  const stripped = stripCertificationOrTitle(clean)
  if (!stripped || stripped.length < 2) return true

  return false
}

/**
 * Detects whether a provider name represents an Organization or Individual based on medical and clinical keywords.
 */
export function detectProviderClassification(name = '') {
  if (!name || typeof name !== 'string') return 'Individual'
  const clean = name.trim()
  if (isCertificationOrCredential(clean)) return 'Individual'
  const lower = clean.toLowerCase()

  const orgKeywords = [
    'hospital',
    'clinic',
    'center',
    'centre',
    'rehabilitation',
    'rehab',
    'orthopedic',
    'orthopaedic',
    'medical',
    'health',
    'surgery',
    'surgical',
    'imaging',
    'radiology',
    'diagnostics',
    'laboratory',
    'lab',
    'therapy',
    'physical therapy',
    'associates',
    'group',
    'care',
    'urgent care',
    'emergency',
    'family practice',
    'institute',
    'partners',
    'physicians',
    'services',
    'sports medicine',
    'neuroscience',
    'cardiology',
  ]

  if (orgKeywords.some(kw => lower.includes(kw))) {
    return 'Organization'
  }
  return 'Individual'
}

/**
 * Compares two claim or policy numbers, ignoring formatting, prefixes (e.g. "Claim #"), dashes, and whitespace.
 */
export function isEquivalentClaimOrPolicy(valA, valB) {
  if (!valA || !valB) return false
  const clean = s =>
    String(s)
      .toLowerCase()
      .replace(/^(?:claim|policy|wc|file|id)?\s*(?:#|no\.?|number|:)?\s*/i, '')
      .replace(/[^a-z0-9]/g, '')
  const a = clean(valA)
  const b = clean(valB)
  return a.length > 0 && a === b
}

/**
 * Compares two date values to determine if they represent the same calendar day.
 */
export function isEquivalentDate(dateA, dateB) {
  if (!dateA || !dateB) return false
  const strA = String(dateA).trim()
  const strB = String(dateB).trim()
  if (strA === strB) return true
  const mA = Moment(strA, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'YYYY/MM/DD', Moment.ISO_8601], true)
  const mB = Moment(strB, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'YYYY/MM/DD', Moment.ISO_8601], true)
  if (mA.isValid() && mB.isValid()) {
    return mA.isSame(mB, 'day')
  }
  return false
}

/**
 * Compares two personal/claimant names, ignoring order, case, honorific titles, and credentials.
 */
export function isEquivalentName(nameA, nameB) {
  if (!nameA || !nameB) return false
  const clean = s =>
    String(s)
      .toLowerCase()
      .replace(/^(?:dr\.?|doctor|mr\.?|ms\.?|mrs\.?)\s+/i, '')
      .replace(/,\s*(?:md|m\.d\.|do|d\.o\.|dc|pt|phd|np|pa|pa-c|facs|jr\.?|sr\.?|ii|iii|iv)\b/i, '')
      .replace(/[^a-z0-9]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  const a = clean(nameA)
  const b = clean(nameB)
  if (a === b) return true
  const wordsA = a.split(' ').sort().join(' ')
  const wordsB = b.split(' ').sort().join(' ')
  return wordsA.length > 0 && wordsA === wordsB
}

/**
 * Compares two postal addresses, standardizing street abbreviation, suite, and punctuation.
 */
export function isEquivalentAddress(addrA, addrB) {
  if (!addrA || !addrB) return false
  const toStr = a => {
    if (typeof a === 'string') return a
    if (typeof a === 'object') {
      return [a.line1 || a.Line1, a.line2 || a.Line2, a.city || a.City, a.state || a.State, a.zip || a.Zip]
        .filter(Boolean)
        .join(' ')
    }
    return ''
  }
  const clean = s =>
    toStr(s)
      .toLowerCase()
      .replace(/-\d{4}\b/g, '')
      .replace(/\b(?:suite|ste\.?|apt\.?|apartment|unit|#|fl\.?|floor|p\.?o\.?\s+box|box)\b/gi, '')
      .replace(/\bstreet\b/gi, 'st')
      .replace(/\broad\b/gi, 'rd')
      .replace(/\bavenue\b/gi, 'ave')
      .replace(/\bboulevard\b/gi, 'blvd')
      .replace(/\bdrive\b/gi, 'dr')
      .replace(/\blane\b/gi, 'ln')
      .replace(/\bhighway\b/gi, 'hwy')
      .replace(/\bcalifornia\b/gi, 'ca')
      .replace(/[^a-z0-9]/g, '')
  const cA = clean(addrA)
  const cB = clean(addrB)
  return cA.length >= 6 && cA === cB
}

/**
 * Compares two telephone numbers by their digits.
 */
export function isEquivalentPhone(phoneA, phoneB) {
  if (!phoneA || !phoneB) return false
  const dA = String(phoneA).replace(/\D/g, '')
  const dB = String(phoneB).replace(/\D/g, '')
  if (dA === dB && dA.length >= 7) return true
  const dA10 = dA.length === 11 && dA.startsWith('1') ? dA.slice(1) : dA
  const dB10 = dB.length === 11 && dB.startsWith('1') ? dB.slice(1) : dB
  return dA10.length === 10 && dA10 === dB10
}

/**
 * Compares two strings case-insensitively, ignoring alphanumeric differences.
 */
export function isEquivalentText(textA, textB) {
  if (!textA || !textB) return false
  const clean = s =>
    String(s)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  const a = clean(textA)
  const b = clean(textB)
  return a.length > 0 && a === b
}

/**
 * Compares provider name specificity: returns true if existingName is more specific or equally specific
 * compared to newName (e.g., "Marcus Brody, MD" vs "Dr. Brody").
 */
export function isMoreSpecificProviderName(existingName = '', newName = '') {
  if (!existingName) return false
  if (!newName) return true
  if (existingName.trim().toLowerCase() === newName.trim().toLowerCase()) return true

  // Check credentials (e.g. "MD", "DO", "DC", "PT", "PhD", "NP", "PA")
  const hasCreds = name => /\b(md|m\.d\.|do|d\.o\.|dc|pt|phd|np|pa|pa-c|facs)\b/i.test(name)
  const existingCreds = hasCreds(existingName)
  const newCreds = hasCreds(newName)
  if (existingCreds && !newCreds) return true
  if (newCreds && !existingCreds) return false

  // Check honorific title + single surname (e.g. "Dr. Brody", "Doctor Brody")
  const isHonorificSurnameOnly = name => /^(?:dr\.?|doctor)\s+[a-z]+$/i.test(name.trim())
  if (!isHonorificSurnameOnly(existingName) && isHonorificSurnameOnly(newName)) return true
  if (isHonorificSurnameOnly(existingName) && !isHonorificSurnameOnly(newName)) return false

  // Check full first + last vs single token
  const cleanTokens = name =>
    name
      .replace(/^(?:dr\.?|doctor)\s+/i, '')
      .replace(/,\s*(?:md|m\.d\.|do|d\.o\.|dc|pt|phd|np|pa|pa-c|facs)\b/i, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
  const existingTokens = cleanTokens(existingName)
  const newTokens = cleanTokens(newName)
  if (existingTokens.length > newTokens.length) return true
  if (newTokens.length > existingTokens.length) return false

  return existingName.length >= newName.length
}

/**
 * Scans document text lines and lineBlocks for healthcare facility and imaging organization names.
 */
export function detectOrganizationsFromDocument(docs = []) {
  const detected = new Set()
  const docList = (Array.isArray(docs) ? docs : [docs]).filter(Boolean)
  const orgPattern =
    /\b([A-Z0-9][A-Za-z0-9&,.\s-]{2,40}\s+(?:Imaging|Radiology|Hospital|Clinic|Center|Centre|Medical Group|Orthopedics|Healthcare|Associates|Health\s+System|Surgical\s+Center))\b/i

  docList.forEach(doc => {
    if (!doc) return
    if (Array.isArray(doc.lineBlocks)) {
      doc.lineBlocks.forEach(block => {
        // Examine header blocks (top 25% of any page) or first 100 blocks
        const isHeader = block.top === undefined || block.top < 0.25
        if (!isHeader) return
        const txt = (block.text || '').trim()
        if (txt.length >= 5 && txt.length <= 70) {
          if (txt.includes('?') || isCertificationOrCredential(txt)) return
          const m = txt.match(orgPattern)
          if (m && m[1]) {
            const clean = m[1]
              .replace(
                /^(?:report\s+from|records\s+from|prepared\s+by|location:?|facility:?|clinic:?|hospital:?|from|at|to|re:?)\s+/i,
                '',
              )
              .trim()
            if (
              clean.length >= 5 &&
              !clean.includes('?') &&
              !isCertificationOrCredential(clean) &&
              detectProviderClassification(clean) === 'Organization'
            ) {
              detected.add(clean)
            }
          }
        }
      })
    }
    const textContent = doc.textContent || doc.text || ''
    if (textContent) {
      const lines = textContent.split(/\r?\n/)
      const sampleLines = lines.slice(0, 500)
      sampleLines.forEach(line => {
        const txt = line.trim()
        if (txt.length >= 5 && txt.length <= 70) {
          if (txt.includes('?') || isCertificationOrCredential(txt)) return
          const m = txt.match(orgPattern)
          if (m && m[1]) {
            const clean = m[1]
              .replace(
                /^(?:report\s+from|records\s+from|prepared\s+by|location:?|facility:?|clinic:?|hospital:?|from|at|to|re:?)\s+/i,
                '',
              )
              .trim()
            if (
              clean.length >= 5 &&
              !clean.includes('?') &&
              !isCertificationOrCredential(clean) &&
              detectProviderClassification(clean) === 'Organization'
            ) {
              detected.add(clean)
            }
          }
        }
      })
    }
  })

  return Array.from(detected)
}

/**
 * Builds a unified list of unique organization names extracted from existing case records
 * and newly extracted providers to populate the provider-organization linking dropdown.
 */
export function getAvailableOrganizations(extractedData = {}, existingCase = {}, fallbackDocs = []) {
  const orgs = new Set()

  const existingProviders = [
    ...(existingCase.treatmentProviders || existingCase.TreatmentProviders || []),
    ...(existingCase.providers || existingCase.Providers || []),
    ...(existingCase.caseProviders || []),
  ]

  existingProviders.forEach(p => {
    const name = p.name || p.Name
    const practiceName = p.practiceName || p.PracticeName || p.companyName || p.organizationName
    if (name && isCertificationOrCredential(name)) return
    if (practiceName && isCertificationOrCredential(practiceName)) return
    const classification = p.classification || p.Classification || (name ? detectProviderClassification(name) : null)
    if (classification === 'Organization' && name) orgs.add(name)
    if (practiceName) orgs.add(practiceName)
  })

  const rawProviders =
    extractedData.providers ||
    extractedData.Providers ||
    extractedData.extensions?.providers ||
    extractedData.extensions?.Providers ||
    []
  rawProviders.forEach(p => {
    const name = p.name || p.Name
    const practiceName = p.practiceName || p.PracticeName || p.companyName
    const organizationName = p.organizationName || p.OrganizationName
    if (name && isCertificationOrCredential(name)) return
    if (practiceName && isCertificationOrCredential(practiceName)) return
    if (organizationName && isCertificationOrCredential(organizationName)) return
    const classification =
      p.classification || p.Classification || (name ? detectProviderClassification(name) : 'Organization')
    if (classification === 'Organization' && name) orgs.add(name)
    if (practiceName) orgs.add(practiceName)
    if (organizationName) orgs.add(organizationName)
  })

  // Also include any organizations detected directly in document text/lineblocks
  const docs = fallbackDocs || extractedData.documents || []
  const detectedDocOrgs = detectOrganizationsFromDocument(docs)
  detectedDocOrgs.forEach(org => orgs.add(org))

  return Array.from(orgs).filter(Boolean).sort()
}

/**
 * Locates the exact page and line number of a text snippet within a document's OCR/text stream.
 * Supports fallback terms to search for entity names/values when Bedrock omitted verbatim snippets.
 */
/**
 * Extracts a concise snippet (~150-220 characters max) around a matching query/term from a longer text block,
 * snapping cleanly to sentence or word boundaries so it never produces an entire page or paragraph.
 */
export function extractConciseSnippet(text = '', query = '', maxLen = 220) {
  if (!text || typeof text !== 'string') return ''
  const trimmed = text.trim()
  if (trimmed.length <= maxLen) return trimmed

  const lower = trimmed.toLowerCase()
  const cleanQuery = (query || '').trim().toLowerCase()
  let matchIdx = -1

  // 1. Exact query match
  if (cleanQuery) {
    matchIdx = lower.indexOf(cleanQuery)
  }

  // 2. Keyword match if multi-word query didn't match verbatim
  if (matchIdx === -1 && cleanQuery) {
    const words = cleanQuery.split(/\s+/).filter(w => w.length >= 3)
    for (const w of words) {
      const idx = lower.indexOf(w)
      if (idx !== -1) {
        matchIdx = idx
        break
      }
    }
  }

  // If no match position found, anchor at the beginning
  if (matchIdx === -1) matchIdx = 0

  const queryLen = cleanQuery ? cleanQuery.length : 0
  const targetRadius = Math.floor((maxLen - queryLen) / 2)
  let start = Math.max(0, matchIdx - targetRadius)
  let end = Math.min(trimmed.length, start + maxLen)

  if (end === trimmed.length) {
    start = Math.max(0, end - maxLen)
  }

  const isAbbreviation = (str, periodIdx) => {
    if (periodIdx < 2) return false
    const sub = str.slice(Math.max(0, periodIdx - 6), periodIdx + 1)
    return /\b(dr|mr|mrs|ms|jr|sr|vs|etc|inc|corp|co|no|md|do|rn|pt|dc)\.$/i.test(sub)
  }

  // Snap start boundary
  let prefix = ''
  if (start > 0) {
    let sentenceBreak = -1
    let pPeriod = trimmed.lastIndexOf('. ', matchIdx)
    while (pPeriod !== -1 && isAbbreviation(trimmed, pPeriod)) {
      pPeriod = trimmed.lastIndexOf('. ', pPeriod - 1)
    }
    const pNewline = trimmed.lastIndexOf('\n', matchIdx)
    sentenceBreak = Math.max(pPeriod !== -1 ? pPeriod + 2 : -1, pNewline !== -1 ? pNewline + 1 : -1)

    if (sentenceBreak !== -1 && sentenceBreak >= start - 40) {
      start = sentenceBreak
    } else {
      const spaceIdx = trimmed.indexOf(' ', start)
      if (spaceIdx !== -1 && spaceIdx <= matchIdx) {
        start = spaceIdx + 1
      }
      prefix = '...'
    }
  }

  // Snap end boundary
  let suffix = ''
  if (end < trimmed.length) {
    const afterMatch = matchIdx + queryLen
    let sentenceBreak = -1
    let nPeriod = trimmed.indexOf('. ', afterMatch)
    while (nPeriod !== -1 && isAbbreviation(trimmed, nPeriod)) {
      nPeriod = trimmed.indexOf('. ', nPeriod + 2)
    }
    const nNewline = trimmed.indexOf('\n', afterMatch)
    const candidates = [nPeriod !== -1 ? nPeriod + 1 : -1, nNewline !== -1 ? nNewline : -1].filter(c => c !== -1)
    if (candidates.length > 0) {
      sentenceBreak = Math.min(...candidates)
    }

    if (sentenceBreak !== -1 && sentenceBreak <= end + 40) {
      end = sentenceBreak
    } else {
      const spaceIdx = trimmed.lastIndexOf(' ', end)
      if (spaceIdx !== -1 && spaceIdx >= afterMatch) {
        end = spaceIdx
      }
      suffix = '...'
    }
  }

  const excerpt = trimmed.slice(start, end).replace(/\s+/g, ' ').trim()
  if (!excerpt) return trimmed.slice(0, maxLen) + (trimmed.length > maxLen ? '...' : '')
  return `${prefix ? prefix + ' ' : ''}${excerpt}${suffix ? ' ' + suffix : ''}`
}

/**
 * Detects the document page number for a given character offset in textContent.
 * Supports explicit "--- Page X ---" headers, "[Page X]" markers, and "X/Y" page footers (e.g. 1/4, 2/4).
 */
export function getPageForOffset(textContent, matchIndex) {
  if (!textContent || matchIndex < 0) return 1

  const textBefore = textContent.slice(0, matchIndex)

  // Explicit start-of-page markers before this offset: take the last one
  const pageStartRegex = /(?:---\s*Page\s+(\d+)\s*---|\[\s*Page\s+(\d+)\s*\]|\bPage\s+(\d+)\s+of\s+\d+\b)/gi
  let lastPageStart = null
  let m
  while ((m = pageStartRegex.exec(textBefore)) !== null) {
    const p = parseInt(m[1] || m[2] || m[3], 10)
    if (!isNaN(p)) lastPageStart = p
  }
  if (lastPageStart !== null) return lastPageStart

  // Check for end-of-page footers like "1/4", "2/4", "3/4" across the document
  const footerRegex = /\b([1-9]\d*)\s*\/\s*([1-9]\d*)\b(?!\s*\/\s*\d{2,4})/g
  const footers = []
  while ((m = footerRegex.exec(textContent)) !== null) {
    const cur = parseInt(m[1], 10)
    const tot = parseInt(m[2], 10)
    if (cur <= tot && tot <= 50) {
      footers.push({ page: cur, offset: m.index + m[0].length })
    }
  }

  if (footers.length > 0) {
    footers.sort((a, b) => a.offset - b.offset)
    for (const f of footers) {
      if (matchIndex <= f.offset) {
        return f.page
      }
    }
    return footers[footers.length - 1].page
  }

  return 1
}

/**
 * Locates the exact page and line number of a text snippet within a document's OCR/text stream.
 * Supports fallback terms to search for entity names/values when Bedrock omitted verbatim snippets.
 */
export function locateSnippetInDoc(snippet, doc, fallbackTerms = [], options = {}) {
  if (!doc) return null

  const isValidSnippet = (str = '') => {
    const trimmed = str.trim()
    return trimmed.length >= 4 && !/^[a-z0-9]$/i.test(trimmed)
  }

  const hasTokenOverlap = (strA, strB, minOverlapRatio = 0.65) => {
    if (!strA || !strB) return false
    const wordsA = strA
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3)
    const wordsB = strB
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length >= 3)
    if (wordsA.length < 3 || wordsB.length < 3) return false
    const setB = new Set(wordsB)
    const matching = wordsA.filter(w => setB.has(w)).length
    return matching / Math.min(wordsA.length, wordsB.length) >= minOverlapRatio
  }

  const excludeList = (options.excludeTerms || []).map(t => t.toLowerCase())
  const isExcluded = str => {
    if (!str || excludeList.length === 0) return false
    const lower = str.toLowerCase()
    return excludeList.some(ex => lower.includes(ex))
  }
  const checkExcluded = (targetText, matchIdxInTarget, termLen = 0) => {
    if (excludeList.length === 0 || !targetText) return false
    if (targetText.length <= 300) {
      return isExcluded(targetText)
    }
    const start = Math.max(0, matchIdxInTarget - 80)
    const end = Math.min(targetText.length, matchIdxInTarget + termLen + 80)
    return isExcluded(targetText.slice(start, end))
  }

  // 1. Try with primary snippet if non-empty
  if (snippet && typeof snippet === 'string' && snippet.trim().length > 0) {
    const cleanSnippet = snippet.trim().toLowerCase()

    if (doc.lineBlocks && Array.isArray(doc.lineBlocks)) {
      const match = doc.lineBlocks.find(b => {
        const bText = (b.text || '').trim().toLowerCase()
        if (isExcluded(bText)) return false
        return (
          bText.includes(cleanSnippet) ||
          (cleanSnippet.length > 15 && bText.length >= 12 && cleanSnippet.includes(bText)) ||
          hasTokenOverlap(cleanSnippet, bText)
        )
      })
      if (match && isValidSnippet(match.text)) {
        const matchedSnippet =
          match.text.length > 250 ? extractConciseSnippet(match.text, cleanSnippet, 220) : match.text.trim()
        return { page: match.page, lineNumber: match.lineNumber, matchedSnippet }
      }
    }

    if (doc.textContent) {
      const lines = doc.textContent.split('\n')
      let currentPage = 1
      let charOffset = 0
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const pageMatch =
          line.match(/^---\s*Page\s+(\d+)\s*---$/i) ||
          line.match(/^\s*Page\s+(\d+)\s+of\s+\d+\s*$/i) ||
          line.match(/^\s*([1-9]\d*)\s*\/\s*([1-9]\d*)\s*$/)
        if (pageMatch) {
          const p = parseInt(pageMatch[1], 10)
          const tot = pageMatch[2] ? parseInt(pageMatch[2], 10) : null
          if (!tot || (p <= tot && tot <= 50)) {
            currentPage = p
          }
        }
        const cleanLine = line.trim().toLowerCase()
        if (cleanLine && !pageMatch) {
          let matchIdx = cleanLine.indexOf(cleanSnippet)
          if (matchIdx !== -1) {
            while (matchIdx !== -1) {
              if (!checkExcluded(cleanLine, matchIdx, cleanSnippet.length)) {
                break
              }
              matchIdx = cleanLine.indexOf(cleanSnippet, matchIdx + 1)
            }
          }
          const matchesPattern =
            matchIdx !== -1 ||
            (!checkExcluded(cleanLine, 0, cleanLine.length) &&
              ((cleanSnippet.length > 15 && cleanLine.length >= 12 && cleanSnippet.includes(cleanLine)) ||
                hasTokenOverlap(cleanSnippet, cleanLine)))

          if (matchesPattern) {
            if (isValidSnippet(line)) {
              const matchedSnippet = line.length > 250 ? extractConciseSnippet(line, cleanSnippet, 220) : line.trim()
              const matchOffsetInLine = matchIdx >= 0 ? matchIdx : 0
              const matchOffsetInDoc = charOffset + matchOffsetInLine
              const lineOffset = line.length > 250 ? Math.max(0, Math.floor(matchOffsetInLine / 80)) : 0
              const detectedPage = getPageForOffset(doc.textContent, matchOffsetInDoc) || currentPage
              return { page: detectedPage, lineNumber: i + 1 + lineOffset, matchedSnippet }
            }
          }
        }
        charOffset += line.length + 1
      }
    }
  }

  // 2. Fallback to searching candidate terms if primary snippet was empty or not located
  const candidateTerms = (Array.isArray(fallbackTerms) ? fallbackTerms : [fallbackTerms])
    .filter(t => t && typeof t === 'string' && t.trim().length >= 3)
    .map(t => t.trim().toLowerCase())

  for (const term of candidateTerms) {
    if (doc.lineBlocks && Array.isArray(doc.lineBlocks)) {
      const match = doc.lineBlocks.find(b => {
        const bText = (b.text || '').trim().toLowerCase()
        if (isExcluded(bText)) return false
        return bText.includes(term) || (term.length > 15 && bText.length >= 12 && term.includes(bText))
      })
      if (match && isValidSnippet(match.text)) {
        const matchedSnippet =
          match.text.length > 250 ? extractConciseSnippet(match.text, term, 220) : match.text.trim()
        return { page: match.page, lineNumber: match.lineNumber, matchedSnippet }
      }
    }

    if (doc.textContent) {
      const lines = doc.textContent.split('\n')
      let currentPage = 1
      let charOffset = 0
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const pageMatch =
          line.match(/^---\s*Page\s+(\d+)\s*---$/i) ||
          line.match(/^\s*Page\s+(\d+)\s+of\s+\d+\s*$/i) ||
          line.match(/^\s*([1-9]\d*)\s*\/\s*([1-9]\d*)\s*$/)
        if (pageMatch) {
          const p = parseInt(pageMatch[1], 10)
          const tot = pageMatch[2] ? parseInt(pageMatch[2], 10) : null
          if (!tot || (p <= tot && tot <= 50)) {
            currentPage = p
          }
        }
        const cleanLine = line.trim().toLowerCase()
        if (cleanLine && !pageMatch) {
          let matchIdx = cleanLine.indexOf(term)
          if (matchIdx !== -1) {
            while (matchIdx !== -1) {
              if (!checkExcluded(cleanLine, matchIdx, term.length)) {
                break
              }
              matchIdx = cleanLine.indexOf(term, matchIdx + 1)
            }
          }
          const matchesPattern =
            matchIdx !== -1 ||
            (!checkExcluded(cleanLine, 0, cleanLine.length) &&
              term.length > 15 &&
              cleanLine.length >= 12 &&
              term.includes(cleanLine))

          if (matchesPattern) {
            if (isValidSnippet(line)) {
              const matchedSnippet = line.length > 250 ? extractConciseSnippet(line, term, 220) : line.trim()
              const matchOffsetInLine = matchIdx >= 0 ? matchIdx : 0
              const matchOffsetInDoc = charOffset + matchOffsetInLine
              const lineOffset = line.length > 250 ? Math.max(0, Math.floor(matchOffsetInLine / 80)) : 0
              const detectedPage = getPageForOffset(doc.textContent, matchOffsetInDoc) || currentPage
              return { page: detectedPage, lineNumber: i + 1 + lineOffset, matchedSnippet }
            }
          }
        }
        charOffset += line.length + 1
      }
    }
  }

  return null
}

/**
 * Resolves which document from a multi-document list an extracted item belongs to,
 * matching on documentId, sourceDocument / documentName, or docIndex.
 */
export function findMatchingDocForItem(item, docs = []) {
  if (!item || !Array.isArray(docs) || docs.length === 0) return null

  // 1. By documentId
  const docId = typeof item === 'object' ? item.documentId || item.DocumentId : null
  if (docId) {
    const byId = docs.find(d => d.id === docId)
    if (byId) return byId
  }

  // 2. By sourceDocument / documentName
  const rawName =
    typeof item === 'object'
      ? item.sourceDocument || item.SourceDocument || item.documentName || item.DocumentName
      : null
  if (rawName && typeof rawName === 'string') {
    const cleanName = rawName.trim().toLowerCase()
    let unescapedName = cleanName
    try {
      unescapedName = decodeURIComponent(cleanName)
    } catch {
      // ignore
    }
    const byName = docs.find(d => {
      const dName = (d.fileName || d.name || '').trim().toLowerCase()
      return dName === cleanName || dName === unescapedName || dName.endsWith(cleanName) || cleanName.endsWith(dName)
    })
    if (byName) return byName
  }

  // 3. By docIndex
  const docIdx =
    typeof item === 'object'
      ? item.docIndex !== undefined
        ? item.docIndex
        : item.DocIndex !== undefined
        ? item.DocIndex
        : null
      : null
  if (docIdx !== null && docs[docIdx]) {
    return docs[docIdx]
  }

  return null
}

/**
 * Detects whether a specific document has diagnostic study characteristics (imaging / EMG/NCS).
 */
export function isDocDiagnosticStudy(doc, idx = 0) {
  if (!doc) return false
  const sub = (doc.documentSubtype || '').toLowerCase()
  if (sub === 'diagnostic study' || sub === 'electrodiagnostic study') return true
  const txt = (doc.textContent || '').slice(0, 4000)
  const isImagingType =
    /\b(mri|magnetic\s+resonance|ct\s+scan|cat\s+scan|computed\s+tomography|\bx-?ray\b|radiograph(?:y|s)?|ultrasound|sonogram|emg|ncs)\b/i.test(
      txt,
    )
  const hasImagingSections = /\b(impression|impressions|findings?|technique|examination)\b/i.test(txt)
  return isImagingType && hasImagingSections
}

/**
 * Searches across an array of documents to locate where a snippet or fallback terms appear.
 * Supports preferredDoc ordering so document attribution prioritizes the originating document.
 * Returns the matching location object and the matched document.
 */
export function locateSnippetInAllDocs(snippet, docs = [], fallbackTerms = [], options = {}) {
  const docList = Array.isArray(docs) ? docs.filter(Boolean) : []
  if (docList.length === 0) return { loc: null, doc: null }

  let preferredDoc = options.preferredDoc || null
  if (!preferredDoc) {
    if (options.preferredDocId) {
      preferredDoc = docList.find(d => d.id === options.preferredDocId) || null
    } else if (options.preferredDocName) {
      const pName = String(options.preferredDocName).trim().toLowerCase()
      let unescaped = pName
      try {
        unescaped = decodeURIComponent(pName)
      } catch {
        // ignore
      }
      preferredDoc =
        docList.find(d => {
          const dName = (d.fileName || d.name || '').trim().toLowerCase()
          return dName === pName || dName === unescaped || dName.endsWith(pName) || pName.endsWith(dName)
        }) || null
    } else if (
      options.preferredDocIndex !== undefined &&
      options.preferredDocIndex !== null &&
      docList[options.preferredDocIndex]
    ) {
      preferredDoc = docList[options.preferredDocIndex]
    }
  }

  const orderedDocs = preferredDoc ? [preferredDoc, ...docList.filter(d => d !== preferredDoc)] : docList

  // 1. Try finding by primary snippet in each document
  if (snippet && typeof snippet === 'string' && snippet.trim().length > 0) {
    for (const d of orderedDocs) {
      const loc = locateSnippetInDoc(snippet, d, [], options)
      if (loc && (loc.lineNumber || loc.matchedSnippet)) {
        return { loc, doc: d }
      }
    }
  }

  // 2. Try finding by candidate/fallback terms in each document
  const validTerms = Array.isArray(fallbackTerms) ? fallbackTerms.filter(t => t && String(t).trim().length > 0) : []
  if (validTerms.length > 0) {
    for (const d of orderedDocs) {
      const loc = locateSnippetInDoc('', d, validTerms, options)
      if (loc && (loc.lineNumber || loc.matchedSnippet)) {
        return { loc, doc: d }
      }
    }
  }

  return { loc: null, doc: preferredDoc || docList[0] || null }
}

/**
 * Infers or standardizes a specific diagnostic test type (e.g. 'MRI', 'CT Scan', 'X-Ray')
 * from raw extraction fields, snippet text, and clinical findings when generic placeholders like
 * 'Diagnostic Test' are encountered.
 */
export function inferDiagnosticTestType(rawModality = '', snippet = '', results = '', bodyPart = '', docContext = '') {
  const genericTerms = new Set([
    'diagnostic test',
    'diagnostic tests',
    'diagnostic study',
    'diagnostic imaging',
    'test',
    'tests',
    'imaging',
    'study',
    'unknown',
  ])

  const cleanRaw = (rawModality || '').trim()
  if (cleanRaw && !genericTerms.has(cleanRaw.toLowerCase())) {
    return cleanRaw
  }

  const contextStr = docContext ? String(docContext).slice(0, 3000) : ''
  const combinedText = `${snippet || ''} ${results || ''} ${bodyPart || ''} ${contextStr}`.replace(/[_]/g, ' ')

  // Check if document or excerpt indicates an MRI
  const isMri = /\b(mri|magnetic\s+resonance)\b/i.test(combinedText)
  const isCt = /\b(ct\s+scan|cat\s+scan|computed\s+tomography|\bct\b)\b/i.test(combinedText)
  const isXray = /\b(x-ray|x\s*ray|radiograph(?:y|s)?|roentgenogram)\b/i.test(combinedText)

  // 1. MRI / Magnetic Resonance Imaging
  if (isMri) {
    if (/\b(lumbar(?:\s+spine)?|l[1-5]-s1|l[1-5]-l[2-5]|thecal\s+sac|cauda\s+equina|conus)\b/i.test(combinedText)) {
      return 'MRI Lumbar Spine'
    }
    if (/\b(cervical(?:\s+spine)?|c[1-7]-c[2-7]|cord\s+compression)\b/i.test(combinedText)) {
      return 'MRI Cervical Spine'
    }
    if (/\b(thoracic(?:\s+spine)?|t(?:[1-9]|1[0-2])-t(?:[1-9]|1[0-2]))\b/i.test(combinedText)) {
      return 'MRI Thoracic Spine'
    }
    if (/\b(brain|head|cerebral|intracranial)\b/i.test(combinedText)) {
      return 'MRI Brain'
    }
    if (
      /\b(right\s+shoulder|left\s+shoulder|shoulder|rotator\s+cuff|supraspinatus|glenohumeral)\b/i.test(combinedText)
    ) {
      const m = combinedText.match(/\b(mri\s+(?:right|left)\s+shoulder)\b/i)
      return m ? m[1].toUpperCase() : 'MRI Shoulder'
    }
    if (/\b(right\s+knee|left\s+knee|knee|meniscus|meniscal|patella|cruciate|acl|pcl)\b/i.test(combinedText)) {
      const m = combinedText.match(/\b(mri\s+(?:right|left)\s+knee)\b/i)
      return m ? m[1].toUpperCase() : 'MRI Knee'
    }
    return 'MRI'
  }

  // 2. CT / Computed Tomography / CAT Scan
  if (isCt) {
    if (/\b(lumbar(?:\s+spine)?|l[1-5]-s1|l[1-5]-l[2-5])\b/i.test(combinedText)) return 'CT Lumbar Spine'
    if (/\b(cervical(?:\s+spine)?|c[1-7]-c[2-7])\b/i.test(combinedText)) return 'CT Cervical Spine'
    if (/\b(head|brain|cerebral)\b/i.test(combinedText)) return 'CT Head'
    if (/\b(abdomen|pelvis|abdomino-?pelvic)\b/i.test(combinedText)) return 'CT Abdomen / Pelvis'
    if (/\b(chest|thorax|pulmonary)\b/i.test(combinedText)) return 'CT Chest'
    return 'CT Scan'
  }

  // 3. X-Ray / Radiograph
  if (isXray) {
    if (/\b(lumbar(?:\s+spine)?|l-?spine|l[1-5]-s1|l[1-5]-l[2-5])\b/i.test(combinedText)) {
      return 'X-Ray Lumbar Spine'
    }
    if (/\b(cervical(?:\s+spine)?|c-?spine|c[1-7]-c[2-7])\b/i.test(combinedText)) return 'X-Ray Cervical Spine'
    if (/\b(chest|thorax)\b/i.test(combinedText)) return 'X-Ray Chest'
    if (/\b(knee)\b/i.test(combinedText)) return 'X-Ray Knee'
    if (/\b(shoulder)\b/i.test(combinedText)) return 'X-Ray Shoulder'
    if (/\b(ankle)\b/i.test(combinedText)) return 'X-Ray Ankle'
    if (/\b(wrist|hand)\b/i.test(combinedText)) return 'X-Ray Wrist'
    if (/\b(pelvis|hip)\b/i.test(combinedText)) return 'X-Ray Pelvis'
    return 'X-Ray'
  }

  // 4. EMG / NCS
  if (/\b(emg\s*\/\s*ncs|ncs\s*\/\s*emg|electromyograph(?:y)?|nerve\s+conduction)\b/i.test(combinedText)) {
    return 'EMG / NCS'
  }
  if (/\b(emg)\b/i.test(combinedText)) return 'EMG'
  if (/\b(ncs)\b/i.test(combinedText)) return 'NCS'

  // 5. Ultrasound / Sonogram
  if (/\b(ultrasound|sonogram|ultrasonograph(?:y)?)\b/i.test(combinedText)) return 'Ultrasound'

  // 6. Other specific modalities
  if (/\b(myelogram|myelography)\b/i.test(combinedText)) return 'Myelogram'
  if (/\b(discogram|discography)\b/i.test(combinedText)) return 'Discogram'
  if (/\b(arthrogram|arthrography)\b/i.test(combinedText)) return 'Arthrogram'
  if (/\b(bone\s+scan|scintigraphy)\b/i.test(combinedText)) return 'Bone Scan'
  if (/\b(pet\s+scan|positron\s+emission)\b/i.test(combinedText)) return 'PET Scan'
  if (/\b(dexa(?:\s+scan)?|bone\s+density|bone\s+densitometry)\b/i.test(combinedText)) return 'DEXA Scan'
  if (/\b(fluoroscop(?:y)?)\b/i.test(combinedText)) return 'Fluoroscopy'
  if (/\b(ekg|ecg|electrocardiogram)\b/i.test(combinedText)) return 'EKG'
  if (/\b(eeg|electroencephalogram)\b/i.test(combinedText)) return 'EEG'
  if (/\b(blood\s+work|complete\s+blood\s+count|cbc|urinalysis|lab\s+test)\b/i.test(combinedText)) return 'Lab Test'

  return cleanRaw || 'Diagnostic Test'
}

/**
 * Detects whether a body part or anatomical location is a sub-segment or level of a broader parent region
 * (e.g. "L4-L5" or "L5-S1" is a sub-segment of "Lumbar Spine"; "C5-C6" is a sub-segment of "Cervical Spine").
 */
export function isAnatomicalSubSegment(segment = '', parent = '') {
  if (!segment || !parent) return false
  const s = segment
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const p = parent
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (s === p) return true

  // Spine sub-segments vs regional spine names
  const isLumbarLevel = /\b(?:l[1-5]\s*s1|l[1-5]\s*l[1-5])\b/.test(s)
  const isLumbarRegion = /\b(?:lumbar(?:\s+spine)?|l\s*spine|back)\b/.test(p)
  if (isLumbarLevel && isLumbarRegion) return true

  const isCervicalLevel = /\b(?:c[1-7]\s*c[1-7]|c[1-7]\s*t1)\b/.test(s)
  const isCervicalRegion = /\b(?:cervical(?:\s+spine)?|c\s*spine|neck)\b/.test(p)
  if (isCervicalLevel && isCervicalRegion) return true

  const isThoracicLevel = /\b(?:t[1-9]|t1[0-2])\s*(?:t[1-9]|t1[0-2]|l1)\b/.test(s)
  const isThoracicRegion = /\b(?:thoracic(?:\s+spine)?|t\s*spine|mid\s*back)\b/.test(p)
  if (isThoracicLevel && isThoracicRegion) return true

  // Lateralized vs general joint/extremity
  if ((s.includes('right') || s.includes('left') || s.includes('bilateral')) && s.includes(p)) return true

  return false
}

/**
 * Determines whether two diagnostic test entities represent the same clinical imaging study or test procedure.
 */
export function areDiagnosticTestsMatching(itemA, itemB) {
  if (!itemA || !itemB) return false
  const modA = (itemA.attributes?.modality || itemA.field || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const modB = (itemB.attributes?.modality || itemB.field || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!modA || !modB) return false

  const modalitiesMatch =
    modA === modB || (modA.length >= 3 && modB.length >= 3 && (modA.includes(modB) || modB.includes(modA)))
  if (!modalitiesMatch) return false

  const rA = (itemA.attributes?.results || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const rB = (itemB.attributes?.results || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (rA && rB) {
    const resultsMatch = rA === rB || (rA.length >= 8 && rB.length >= 8 && (rA.includes(rB) || rB.includes(rA)))
    if (!resultsMatch) return false
  }

  const bpA = itemA.attributes?.bodyPart || ''
  const bpB = itemB.attributes?.bodyPart || ''
  if (bpA && bpB) {
    const compatible =
      isAnatomicalSubSegment(bpA, bpB) ||
      isAnatomicalSubSegment(bpB, bpA) ||
      bpA.toLowerCase().trim() === bpB.toLowerCase().trim()
    if (!compatible) return false
  }

  const dateA = itemA.attributes?.date
  const dateB = itemB.attributes?.date
  if (dateA && dateB && dateA !== dateB) return false

  return true
}

/**
 * Selects the most specific and clinically descriptive canonical test from a cluster.
 */
export function pickBestCanonicalDiagnosticTest(items = []) {
  if (!items || items.length === 0) return null
  if (items.length === 1) return items[0]

  let best = items[0]
  for (let i = 1; i < items.length; i++) {
    const cur = items[i]
    const bestBp = best.attributes?.bodyPart || ''
    const curBp = cur.attributes?.bodyPart || ''

    // Prefer more specific body part (e.g. L4-L5 over Lumbar Spine)
    if (!bestBp && curBp) {
      best = cur
    } else if (
      bestBp &&
      curBp &&
      isAnatomicalSubSegment(curBp, bestBp) &&
      curBp.toLowerCase() !== bestBp.toLowerCase()
    ) {
      best = cur
    } else if ((cur.attributes?.modality || '').length > (best.attributes?.modality || '').length) {
      best = cur
    } else if ((cur.attributes?.results || '').length > (best.attributes?.results || '').length) {
      best = cur
    }
  }
  return best
}

/**
 * Scans the FINDINGS section of a diagnostic imaging report (MRI, CT, X-Ray)
 * and extracts clinically significant abnormal findings (e.g. disc herniations,
 * bulges, spinal stenosis, tears, fractures) while excluding purely normal/unremarkable levels.
 */
export function extractFindingsFromDocument(textContent = '') {
  if (!textContent || typeof textContent !== 'string') return []

  // Extract text between FINDINGS: and IMPRESSION: (or end of report)
  const findingsRegex =
    /(?:^|\n)\s*(?:FINDINGS|EXAMINATION FINDINGS|STUDY FINDINGS|BODY FINDINGS|OBSERVATIONS)\s*[:\n]([\s\S]*?)(?=(?:\n\s*(?:IMPRESSION|IMPRESSIONS|CONCLUSION|SUMMARY|ELECTRONICALLY SIGNED|INTERPRETING PHYSICIAN|REPORT SIGNED|SIGNATURE)\b|$))/i
  const match = textContent.match(findingsRegex)
  if (!match || !match[1]) return []

  const sectionText = match[1].trim()
  const rawLines = sectionText
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)

  const candidateBlocks = []
  let currentBlock = ''

  for (const line of rawLines) {
    // Check if line starts with a level/anatomy header (e.g. L1-L2:, L5-S1:, C3-C4:, Menisci:) or bullet/number
    const isHeader =
      /^(?:(?:[CLTS]\d+(?:-[CLTS]\d+)?|[A-Z][a-z0-9/]+(?:\s+[A-Z][a-z0-9/]+){0,3})\s*:|\d+\.|\*|-|•)\s*/i.test(line)

    if (isHeader) {
      if (currentBlock) {
        candidateBlocks.push(currentBlock)
      }
      currentBlock = line
    } else if (currentBlock) {
      currentBlock = `${currentBlock} ${line}`
    } else if (line.length >= 15) {
      currentBlock = line
    }
  }
  if (currentBlock) {
    candidateBlocks.push(currentBlock)
  }

  // Filter out purely normal/negative lines if they don't describe pathology
  const pathologyTerms =
    /\b(herniation|bulge|stenosis|narrowing|tear|impingement|mass\s+effect|edema|fracture|spondylolisthesis|protrusion|extrusion|effusion|arthropathy|sprain|degeneration|hypertrophy|lesion|abnormal|compression|flattening|contacting|displacement|signal\s+abnormality|spurring|osteophyte|chondromalacia)\b/i

  return candidateBlocks.filter(b => {
    const clean = b
      .replace(/^(?:[CLTS]\d+(?:-[CLTS]\d+)?|[A-Z][a-z0-9/]+(?:\s+[A-Z][a-z0-9/]+){0,3})\s*:\s*/i, '')
      .trim()
    const withoutNegatives = clean
      .replace(
        /\b(?:no|without|negative\s+for)\s+(?:evidence\s+of\s+)?(?:significant\s+|focal\s+|acute\s+)?(?:disc\s+bulge|herniation|stenosis|canal\s+stenosis|foraminal\s+stenosis|foraminal\s+narrowing|fracture|mass\s+effect|impingement|abnormality|thecal\s+sac\s+compression)[^.;]*/gi,
        '',
      )
      .replace(/^(?:unremarkable|normal(?:\s+alignment|\s+signal|\s+heights)?|intact)[.]?\s*/gi, '')
      .trim()
    return pathologyTerms.test(withoutNegatives)
  })
}

export { ARCHETYPE_CATEGORY_PRIORITIES }

/**
 * Determines whether a document is a dedicated Work Activity Status Report /
 * Report of Work Status / Return to Work slip (e.g., "Gordon Geo RTW.txt",
 * Concentra Work Activity Status Report, CO WC 164 work status slip, TX DWC-73,
 * MN Report of Work Ability).
 */
export function isWorkStatusReportDocument(docName = '', docText = '') {
  const sampleText = String(docText || '')
    .slice(0, 4000)
    .trim()
  const cleanName = String(docName || '').trim()
  const combined = `${cleanName} ${sampleText}`
  if (!combined.trim()) return false

  // Must not be a First Report of Injury (FROI), comprehensive clinical encounter note, operative report, or radiology study
  if (
    /(?:^|[^a-zA-Z0-9])(froi|first[\s_-]*report[\s_-]*of[\s_-]*(?:injury|illness|occupational|incident)|employer'?s?[\s_-]*first[\s_-]*report|wc[\s_-]*100|form[\s_-]*100)(?:$|[^a-zA-Z0-9])/i.test(
      combined,
    )
  ) {
    return false
  }
  if (
    /\b(chief\s+complaint|history\s+of\s+present\s+illness|review\s+of\s+systems|operative\s+report|procedure\s+performed|anesthesia|radiology\s+report|examination\s+findings)\b/i.test(
      sampleText,
    )
  ) {
    return false
  }

  const hasExplicitWorkStatusHeader =
    /\b(report\s+of\s+work\s+status|work\s+activity\s+status\s+report|work\s+status\s+report|report\s+of\s+work\s+ability|work\s+ability\s+report|return\s+to\s+work\s+(?:status\s+report|report|slip|certificate|evaluation|form)|work\s+capacity\s+(?:report|evaluation|form)|activity\s+prescription\s+form|dwc\s*form[\s-]*0?73|wc[\s_-]*164|co[\s_-]*164)\b/i.test(
      combined,
    )
  if (hasExplicitWorkStatusHeader) return true

  const hasRtwFileName =
    /(?:^|[^a-zA-Z0-9])(rtw|return[\s_-]*to[\s_-]*work|work[\s_-]*status|work[\s_-]*ability|dwc[\s_-]*0?73|wc[\s_-]*164|co[\s_-]*164)(?:$|[^a-zA-Z0-9])/i.test(
      cleanName,
    )
  const hasWorkStatusFields =
    /\b(work\s+status|return\s+to\s+work(?:\s+status)?|work\s+restrictions|activity\s+restrictions|modified\s+duty|full\s+duty)\b/i.test(
      sampleText,
    )

  return hasRtwFileName && hasWorkStatusFields
}

/**
 * Determines whether a document is a Business Card, Contact Card, or standalone
 * Letterhead / Contact Directory snippet (Issue #798).
 */
export function isBusinessCardDocument(docName = '', docText = '') {
  const sampleText = String(docText || '')
    .slice(0, 2500)
    .trim()
  const cleanName = String(docName || '').trim()
  const combined = `${cleanName} ${sampleText}`
  if (!combined.trim()) return false

  // Must not be a Work Status Report, FROI, or clinical encounter / diagnostic report
  if (isWorkStatusReportDocument(cleanName, sampleText)) return false

  if (
    /(?:^|[^a-zA-Z0-9])(froi|first[\s_-]*report[\s_-]*of[\s_-]*(?:injury|illness|occupational|incident)|employer'?s?[\s_-]*first[\s_-]*report|wc[\s_-]*100|form[\s_-]*100)(?:$|[^a-zA-Z0-9])/i.test(
      combined,
    )
  ) {
    return false
  }

  if (
    /\b(chief\s+complaint|history\s+of\s+present\s+illness|review\s+of\s+systems|operative\s+report|procedure\s+performed|anesthesia|radiology\s+report|examination\s+findings|discharge\s+summary|physical\s+examination|work\s+status\s*:|return\s+to\s+work\s+status\s*:|date\s+of\s+injury\s*:|claim\s*(?:number|no\.?|#)\s*:|patient\s*(?:name)?\s*:|claimant\s*(?:name)?\s*:|dob\s*:|date\s+of\s+birth\s*:|diagnosis\s*:|diagnoses\s*:|impression\s*:|assessment\s*\/\s*plan)\b/i.test(
      sampleText,
    )
  ) {
    return false
  }

  const hasExplicitCardFileName =
    /(?:^|[^a-zA-Z0-9])(business[\s_-]*card|contact[\s_-]*card|calling[\s_-]*card|letterhead|vcard|provider[\s_-]*card)(?:$|[^a-zA-Z0-9])/i.test(
      cleanName,
    )
  if (hasExplicitCardFileName && sampleText.length <= 1500) {
    return true
  }

  const nonEmptyLines = sampleText
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean)
  if (nonEmptyLines.length === 0 || nonEmptyLines.length > 18 || sampleText.length > 900) {
    return false
  }

  const hasPhone = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/.test(sampleText)
  const hasEmail = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/.test(sampleText)
  const hasAddress = /\b[A-Za-z .'-]+,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?\b/.test(sampleText)
  const hasNpi = /\bNPI\s*[:#]?\s*\d{10}\b/i.test(sampleText)
  const hasFax = /\b(?:fax|f)\s*[:.]?\s*(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/i.test(sampleText)
  const hasPersonOrOrgSignal =
    /\b(MD|M\.D\.|DO|D\.O\.|DC|D\.C\.|PT|DPT|OT|OTR|NP|CRNP|FNP|PA-?C|PhD|PsyD|RN|LPN|BSN|CCM|CRC|CMA|RMA|LVN|Adjuster|Coordinator|Manager|Director|Specialist|Attorney|Esq\.?|Paralegal|Case\s+Manager|Medical\s+Assistant|Scheduler|Clinic|Center|Associates|Medical|Orthopa?edics?|Rehabilitation|Therapy|Hospital|Health|Practice|Group|Institute|Services|Insurance|Claims|Law\s+Firm|Offices)\b/i.test(
      sampleText,
    )

  const contactSignalCount =
    (hasPhone ? 1 : 0) + (hasEmail ? 1 : 0) + (hasAddress ? 1 : 0) + (hasNpi ? 1 : 0) + (hasFax ? 1 : 0)

  return hasPersonOrOrgSignal && contactSignalCount >= 2
}

/**
 * Parses a business card or letterhead contact block into structured fields.
 */
export function parseBusinessCardText(docText = '') {
  const result = {
    personName: null,
    credentials: null,
    roleOrTitle: null,
    specialty: null,
    organizationName: null,
    phone: null,
    extension: null,
    fax: null,
    email: null,
    directAddress: null,
    website: null,
    npi: null,
    line1: null,
    line2: null,
    city: null,
    state: null,
    zip: null,
    notes: null,
    isClinicianProvider: false,
    isOperationalContact: false,
  }
  if (!docText || typeof docText !== 'string') return result

  const lines = docText
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean)
  if (lines.length === 0) return result

  // 1. NPI
  const npiMatch = docText.match(/\bNPI\s*[:#-]?\s*(\d{10})\b/i)
  if (npiMatch) result.npi = npiMatch[1]

  // 2. Direct Address & Email
  const emailMatches = [...docText.matchAll(/\b([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})\b/g)]
  for (const m of emailMatches) {
    const addr = m[1]
    const matchIndex = m.index || 0
    const lineStart = Math.max(0, docText.lastIndexOf('\n', matchIndex))
    const lineEnd = docText.indexOf('\n', matchIndex)
    const contextLine = docText.slice(lineStart, lineEnd >= 0 ? lineEnd : docText.length)
    if (
      /@direct\./i.test(addr) ||
      /\.direct\./i.test(addr) ||
      /\bdirect\s*(?:address|msg|messaging)?\s*[:]/i.test(contextLine)
    ) {
      if (!result.directAddress) result.directAddress = addr
    } else if (!result.email) {
      result.email = addr
    }
  }

  // 3. Website
  const webMatch = docText.match(/\b((?:https?:\/\/|www\.)[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\/[^\s]*)?)\b/i)
  if (webMatch) result.website = webMatch[1]

  // 4. Phone, Extension & Fax
  for (const line of lines) {
    const faxLineMatch = line.match(
      /\b(?:fax|f|facsimile)\s*[:.#-]?\s*((?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4})\b/i,
    )
    if (faxLineMatch && !result.fax) {
      result.fax = faxLineMatch[1].trim()
    }

    const withoutFax = line.replace(
      /\b(?:fax|f|facsimile)\s*[:.#-]?\s*(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/gi,
      '',
    )
    const phoneMatch = withoutFax.match(
      /(?:\b(?:tel|telephone|phone|ph|p|office|o|main|direct|cell|mobile|m|c)\s*[:.#-]?\s*)?((?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4})(?:\s*(?:ext\.?|extension|x)\s*(\d{1,6}))?/i,
    )
    if (phoneMatch && !result.phone) {
      result.phone = phoneMatch[1].trim()
      if (phoneMatch[2] && !result.extension) {
        result.extension = phoneMatch[2].trim()
      }
    }

    if (!result.extension) {
      const extMatch = line.match(/\b(?:ext\.?|extension)\s*[:#]?\s*(\d{1,6})\b/i)
      if (extMatch) result.extension = extMatch[1].trim()
    }
  }

  // 5. Address (City, State ZIP + preceding street lines)
  for (let i = 0; i < lines.length; i++) {
    const cszMatch = lines[i].match(/^([A-Za-z .'-]+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/)
    if (cszMatch) {
      result.city = cszMatch[1].trim()
      result.state = cszMatch[2].trim()
      result.zip = cszMatch[3].trim()
      if (i >= 1) {
        const prev1 = lines[i - 1]
        if (
          /^(?:suite|ste\.?|apt\.?|unit|bldg\.?|building|floor|fl\.?|room|rm\.?|#)\s*[A-Za-z0-9-]+$/i.test(prev1) &&
          i >= 2
        ) {
          result.line2 = prev1
          result.line1 = lines[i - 2]
        } else if (/\d+\s+[A-Za-z0-9]/.test(prev1) || /\b(?:p\.?o\.?\s*box)\b/i.test(prev1)) {
          const suiteSplit = prev1.match(
            /^(.+?),\s*((?:suite|ste\.?|apt\.?|unit|bldg\.?|building|floor|fl\.?|room|rm\.?|#)\s*[A-Za-z0-9-]+)$/i,
          )
          if (suiteSplit) {
            result.line1 = suiteSplit[1].trim()
            result.line2 = suiteSplit[2].trim()
          } else {
            result.line1 = prev1
          }
        }
      }
      break
    }

    const inlineAddrMatch = lines[i].match(
      /^(\d+\s+[^,\r\n]+?)(?:,\s*((?:suite|ste\.?|apt\.?|unit|bldg\.?|building|floor|fl\.?|room|rm\.?|#)\s*[A-Za-z0-9-]+))?,\s*([A-Za-z .'-]+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i,
    )
    if (inlineAddrMatch) {
      result.line1 = inlineAddrMatch[1].trim()
      if (inlineAddrMatch[2]) result.line2 = inlineAddrMatch[2].trim()
      result.city = inlineAddrMatch[3].trim()
      result.state = inlineAddrMatch[4].trim()
      result.zip = inlineAddrMatch[5].trim()
      break
    }
  }

  // 6. Classify remaining lines (Person Name, Credentials, Role/Specialty, Organization)
  const clinicianCredRegex =
    /(?:,\s*|\s+)(MD|M\.D\.|DO|D\.O\.|DC|D\.C\.|DPM|DDS|DMD|OD|PT|DPT|MPT|OT|OTR\/L|OTR|SLP|NP|CRNP|FNP(?:-C|-BC)?|APRN|PA-C|PA|PhD|Ph\.D\.|PsyD|Psy\.D\.|LCSW|LMFT|LPC|CRNA)(?:\b|,|$)/i
  const staffCredRegex = /(?:,\s*|\s+)(CMA|RMA|CCMA|LVN|LPN|RN|BSN|MSN|CCM|CRC|CDMS|CWCP|AIC|SCLA|Esq\.?|JD)(?:\b|,|$)/i
  const orgKeywordRegex =
    /\b(Associates|Clinic|Center|Centre|Orthopa?edics?|Medical\s+Group|Health(?:\s+System|\s+Center|\s+Care|\s+Partners)?|Hospital|Rehabilitation|Physical\s+Therapy|Imaging|Radiology|Diagnostics?|Surgical|Surgery\s+Center|Institute|P\.?C\.?|PLLC|LLC|Inc\.?|Corp\.?|Company|Insurance|Mutual|Casualty|Indemnity|Claims|Services|Law\s+(?:Firm|Offices|Group)|Partners|Practice|Pharmacy)\b/i
  const operationalRoleRegex =
    /\b(Medical\s+Assistant|Certified\s+Medical\s+Assistant|Referral\s+Coordinator|Surgery\s+Scheduler|Surgical\s+Coordinator|Scheduling\s+Coordinator|Patient\s+Care\s+Coordinator|Care\s+Coordinator|Intake\s+Coordinator|Office\s+Manager|Practice\s+Manager|Clinic\s+Manager|Billing\s+(?:Specialist|Coordinator|Manager)|Authorization\s+(?:Specialist|Coordinator)|Workers'?\s*Comp(?:ensation)?\s+Coordinator|Claims\s+(?:Adjuster|Representative|Examiner|Specialist|Manager)|Senior\s+Claims\s+Adjuster|Nurse\s+Case\s+Manager|Field\s+Case\s+Manager|Telephonic\s+Case\s+Manager|Case\s+Manager|Vocational\s+(?:Rehabilitation\s+)?Counselor|Attorney(?:\s+at\s+Law)?|Defense\s+Attorney|Applicant\s+Attorney|Claimant\s+Attorney|Paralegal|Legal\s+Assistant|HR\s+(?:Manager|Director|Representative|Specialist|Coordinator)|Human\s+Resources|Safety\s+(?:Manager|Director|Coordinator)|Risk\s+Manager|Return\s+to\s+Work\s+Coordinator|Account\s+Manager|Client\s+Services)\b/i
  const medicalSpecialtyRegex =
    /\b(Orthop(?:a?edic)?\s+Surgery|Orthop(?:a?edic)s|Neurosurgery|Neurological\s+Surgery|Neurology|Physical\s+Medicine(?:\s+(?:&|and)\s+Rehabilitation)?|PM&R|Physiatry|Pain\s+Management|Interventional\s+Pain|Anesthesiology|Internal\s+Medicine|Family\s+Medicine|Occupational\s+Medicine|Emergency\s+Medicine|General\s+Surgery|Hand\s+Surgery|Spine\s+Surgery|Plastic\s+Surgery|Podiatry|Chiropractic|Physical\s+Therapy|Occupational\s+Therapy|Diagnostic\s+Radiology|Radiology|Psychiatry|Psychology|Neuropsychology|Cardiology|Pulmonology|Rheumatology|Dermatology|Ophthalmology|Otolaryngology|ENT|Urology|Sports\s+Medicine)\b/i

  for (const line of lines) {
    if (
      line === result.line1 ||
      line === result.line2 ||
      (result.city && line.startsWith(result.city)) ||
      (result.npi && line.includes(result.npi)) ||
      /^(?:tel|telephone|phone|ph|p|fax|f|email|e-mail|web|website|direct|npi|office|cell|mobile)\s*[:.#-]/i.test(line)
    ) {
      continue
    }
    if (
      /^((?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}|\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b|(?:https?:\/\/|www\.)\S+)$/i.test(
        line,
      )
    ) {
      continue
    }

    const clinMatch = line.match(clinicianCredRegex)
    if (clinMatch && !result.personName && !orgKeywordRegex.test(line)) {
      result.personName = line.trim()
      result.credentials = clinMatch[1].trim()
      result.isClinicianProvider = true
      continue
    }

    if (/^Dr\.?\s+[A-Z][a-z]+/i.test(line) && !result.personName && !orgKeywordRegex.test(line)) {
      result.personName = line.trim()
      result.isClinicianProvider = true
      continue
    }

    const staffMatch = line.match(staffCredRegex)
    if (staffMatch && !result.personName && !orgKeywordRegex.test(line)) {
      result.personName = line.trim()
      result.credentials = staffMatch[1].trim()
      continue
    }

    if (operationalRoleRegex.test(line)) {
      if (!result.roleOrTitle) {
        result.roleOrTitle = line.trim()
      } else if (!result.notes) {
        result.notes = line.trim()
      }
      result.isOperationalContact = true
      continue
    }

    if (
      medicalSpecialtyRegex.test(line) &&
      !/\b(Associates|Clinic|Center|Centre|Hospital|Group|Partners|Institute|Health\s+System|Practice|LLC|Inc\.?|P\.?C\.?|PLLC)\b/i.test(
        line,
      )
    ) {
      if (!result.specialty) {
        result.specialty = line.trim()
      }
      continue
    }

    if (orgKeywordRegex.test(line)) {
      if (!result.organizationName) {
        result.organizationName = line.trim()
      }
      continue
    }

    // Bare person name line (2-4 capitalized words without digits)
    if (
      !result.personName &&
      !/\d/.test(line) &&
      /^[A-Z][a-zA-Z.'-]+(?:\s+[A-Z][a-zA-Z.'-]+){1,3}$/.test(line) &&
      !orgKeywordRegex.test(line) &&
      !medicalSpecialtyRegex.test(line) &&
      !operationalRoleRegex.test(line)
    ) {
      result.personName = line.trim()
    }
  }

  return result
}

/**
 * Consolidates a Business Card / Letterhead extraction payload on the frontend
 * into a single coherent Provider OR Key Contact compound entity.
 */
function consolidateBusinessCardExtractedData(extractedData = {}, docs = [], defaultDocName = '', rawText = '') {
  const effectiveDocs =
    Array.isArray(docs) && docs.length > 0
      ? docs
      : [
          {
            fileName: defaultDocName || 'Business_Card.txt',
            textContent: rawText || extractedData.rawText || '',
          },
        ]

  const allRawProviders = Array.isArray(extractedData.providers || extractedData.Providers)
    ? extractedData.providers || extractedData.Providers
    : []
  const allRawContacts = Array.isArray(extractedData.contacts || extractedData.Contacts)
    ? extractedData.contacts || extractedData.Contacts
    : []
  const isSingleCardBatch = effectiveDocs.length === 1
  const client = isSingleCardBatch ? extractedData.client || extractedData.Client || {} : {}
  const emp = isSingleCardBatch ? extractedData.employer || extractedData.Employer || {} : {}

  const operationalRoleRegex =
    /\b(Medical\s+Assistant|Certified\s+Medical\s+Assistant|Referral\s+Coordinator|Surgery\s+Scheduler|Surgical\s+Coordinator|Scheduling\s+Coordinator|Patient\s+Care\s+Coordinator|Care\s+Coordinator|Intake\s+Coordinator|Office\s+Manager|Practice\s+Manager|Clinic\s+Manager|Billing\s+(?:Specialist|Coordinator|Manager)|Authorization\s+(?:Specialist|Coordinator)|Workers'?\s*Comp(?:ensation)?\s+Coordinator|Claims\s+(?:Adjuster|Representative|Examiner|Specialist|Manager)|Senior\s+Claims\s+Adjuster|Nurse\s+Case\s+Manager|Field\s+Case\s+Manager|Telephonic\s+Case\s+Manager|Case\s+Manager|Vocational\s+(?:Rehabilitation\s+)?Counselor|Attorney(?:\s+at\s+Law)?|Defense\s+Attorney|Applicant\s+Attorney|Claimant\s+Attorney|Paralegal|Legal\s+Assistant|HR\s+(?:Manager|Director|Representative|Specialist|Coordinator)|Human\s+Resources|Safety\s+(?:Manager|Director|Coordinator)|Risk\s+Manager|Return\s+to\s+Work\s+Coordinator|Account\s+Manager|Client\s+Services)\b/i
  const clinicianCredRegex =
    /(?:,\s*|\s+)(MD|M\.D\.|DO|D\.O\.|DC|D\.C\.|DPM|DDS|DMD|OD|PT|DPT|MPT|OT|OTR\/L|OTR|SLP|NP|CRNP|FNP(?:-C|-BC)?|APRN|PA-C|PA|PhD|Ph\.D\.|PsyD|Psy\.D\.|LCSW|LMFT|LPC|CRNA)(?:\b|,|$)/i

  const consolidatedContacts = []
  const consolidatedProviders = []

  effectiveDocs.forEach((doc, idx) => {
    const docText = doc?.textContent || doc?.text || (idx === 0 ? rawText || extractedData.rawText || '' : '')
    const docName = doc?.fileName || doc?.documentName || doc?.name || defaultDocName || `Business_Card_${idx + 1}.txt`
    const parsed = parseBusinessCardText(docText)

    const belongsToDoc = item => {
      if (!item) return false
      const src = item.sourceDocument || item.SourceDocument || item.documentName || item.DocumentName || ''
      if (src) return src.toLowerCase() === docName.toLowerCase()
      if (isSingleCardBatch) return true
      const itemName = item.name || item.Name || item.practiceName || item.PracticeName || ''
      return Boolean(itemName && docText.toLowerCase().includes(itemName.toLowerCase()))
    }

    const rawProviders = allRawProviders.filter(belongsToDoc)
    const rawContacts = allRawContacts.filter(belongsToDoc)

    const firstContact = rawContacts.length > 0 ? rawContacts[0] : null
    const indivProv = rawProviders.find(p => {
      const cls = p.classification || p.Classification || ''
      const n = p.name || p.Name || ''
      return cls.toLowerCase() === 'individual' || (!cls && detectProviderClassification(n) === 'Individual')
    })
    const orgProv = rawProviders.find(p => {
      const cls = p.classification || p.Classification || ''
      const n = p.name || p.Name || ''
      return cls.toLowerCase() === 'organization' || detectProviderClassification(n) === 'Organization'
    })
    const primaryProv = indivProv || (rawProviders.length > 0 ? rawProviders[0] : null)

    const provSpec =
      primaryProv?.specialty || primaryProv?.Specialty || primaryProv?.providerType || primaryProv?.ProviderType || ''
    const provName = primaryProv?.name || primaryProv?.Name || ''
    const provIsActuallyContact =
      operationalRoleRegex.test(provSpec) && !clinicianCredRegex.test(provName) && !parsed.isClinicianProvider

    const isKeyContactCard =
      (parsed.isOperationalContact && !parsed.isClinicianProvider) ||
      (firstContact != null && !parsed.isClinicianProvider && (!primaryProv || provIsActuallyContact)) ||
      provIsActuallyContact

    const snippet = docText.trim()
    const clientFullName = [client.firstName || client.FirstName, client.lastName || client.LastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    const clientAddr = client.address || client.Address || {}
    const provAddr = primaryProv?.address || primaryProv?.Address || orgProv?.address || orgProv?.Address || {}
    const contactAddr = firstContact?.address || firstContact?.Address || {}

    const mergedAddress = {
      line1:
        contactAddr.line1 ||
        contactAddr.Line1 ||
        provAddr.line1 ||
        provAddr.Line1 ||
        parsed.line1 ||
        clientAddr.line1 ||
        clientAddr.Line1 ||
        null,
      line2:
        contactAddr.line2 ||
        contactAddr.Line2 ||
        provAddr.line2 ||
        provAddr.Line2 ||
        parsed.line2 ||
        clientAddr.line2 ||
        clientAddr.Line2 ||
        null,
      city:
        contactAddr.city ||
        contactAddr.City ||
        provAddr.city ||
        provAddr.City ||
        parsed.city ||
        clientAddr.city ||
        clientAddr.City ||
        null,
      state:
        contactAddr.state ||
        contactAddr.State ||
        provAddr.state ||
        provAddr.State ||
        parsed.state ||
        clientAddr.state ||
        clientAddr.State ||
        null,
      zip:
        contactAddr.zip ||
        contactAddr.Zip ||
        provAddr.zip ||
        provAddr.Zip ||
        parsed.zip ||
        clientAddr.zip ||
        clientAddr.Zip ||
        null,
    }

    if (isKeyContactCard) {
      const contactName =
        parsed.personName ||
        firstContact?.name ||
        firstContact?.Name ||
        (provIsActuallyContact ? provName : '') ||
        clientFullName ||
        'Key Contact'
      const role =
        parsed.roleOrTitle ||
        firstContact?.role ||
        firstContact?.Role ||
        (provIsActuallyContact ? provSpec : '') ||
        client.occupation ||
        client.Occupation ||
        'Staff Contact'
      const orgOrProv =
        firstContact?.organizationOrProviderName ||
        firstContact?.OrganizationOrProviderName ||
        parsed.organizationName ||
        orgProv?.name ||
        orgProv?.Name ||
        primaryProv?.practiceName ||
        primaryProv?.PracticeName ||
        emp.name ||
        emp.Name ||
        ''
      const phone =
        firstContact?.phone ||
        firstContact?.Phone ||
        parsed.phone ||
        primaryProv?.phone ||
        primaryProv?.Phone ||
        client.phone ||
        client.Phone ||
        ''
      const ext = firstContact?.extension || firstContact?.Extension || parsed.extension || ''
      const fax = firstContact?.fax || firstContact?.Fax || parsed.fax || primaryProv?.fax || primaryProv?.Fax || ''
      const email =
        firstContact?.email ||
        firstContact?.Email ||
        parsed.email ||
        primaryProv?.email ||
        primaryProv?.Email ||
        client.email ||
        client.Email ||
        ''
      const directAddr = firstContact?.directAddress || firstContact?.DirectAddress || parsed.directAddress || ''
      const notes = firstContact?.notes || firstContact?.Notes || parsed.notes || ''

      consolidatedContacts.push({
        ...(firstContact || {}),
        name: contactName,
        role,
        organizationOrProviderName: orgOrProv,
        phone,
        extension: ext,
        fax,
        email,
        directAddress: directAddr,
        address: mergedAddress,
        notes,
        documentId: doc?.id || firstContact?.documentId,
        sourceDocument: firstContact?.sourceDocument || docName,
        sourceTextSnippet: firstContact?.sourceTextSnippet || snippet,
      })
      return
    }

    // Provider Business Card / Letterhead
    const providerName =
      parsed.personName ||
      indivProv?.name ||
      indivProv?.Name ||
      primaryProv?.name ||
      primaryProv?.Name ||
      parsed.organizationName ||
      clientFullName ||
      'Treating Provider'
    const isOrgOnly =
      !parsed.personName &&
      !indivProv &&
      (Boolean(orgProv) || Boolean(parsed.organizationName && providerName === parsed.organizationName))
    const classification = isOrgOnly ? 'Organization' : 'Individual'
    const practiceName = isOrgOnly
      ? providerName
      : indivProv?.practiceName ||
        indivProv?.PracticeName ||
        parsed.organizationName ||
        orgProv?.name ||
        orgProv?.Name ||
        emp.name ||
        emp.Name ||
        ''
    const specialty =
      indivProv?.specialty ||
      indivProv?.Specialty ||
      parsed.specialty ||
      primaryProv?.specialty ||
      primaryProv?.Specialty ||
      (isOrgOnly ? 'Treating Facility' : 'Treatment Provider')
    const phone =
      indivProv?.phone ||
      indivProv?.Phone ||
      parsed.phone ||
      primaryProv?.phone ||
      primaryProv?.Phone ||
      orgProv?.phone ||
      orgProv?.Phone ||
      client.phone ||
      client.Phone ||
      ''
    const fax =
      indivProv?.fax ||
      indivProv?.Fax ||
      parsed.fax ||
      primaryProv?.fax ||
      primaryProv?.Fax ||
      orgProv?.fax ||
      orgProv?.Fax ||
      ''
    const email =
      indivProv?.email ||
      indivProv?.Email ||
      parsed.email ||
      primaryProv?.email ||
      primaryProv?.Email ||
      orgProv?.email ||
      orgProv?.Email ||
      ''
    const website =
      indivProv?.website ||
      indivProv?.Website ||
      parsed.website ||
      primaryProv?.website ||
      primaryProv?.Website ||
      orgProv?.website ||
      orgProv?.Website ||
      ''
    const npi =
      indivProv?.npi ||
      indivProv?.Npi ||
      parsed.npi ||
      primaryProv?.npi ||
      primaryProv?.Npi ||
      orgProv?.npi ||
      orgProv?.Npi ||
      ''

    consolidatedProviders.push({
      ...(primaryProv || {}),
      name: providerName,
      classification,
      providerType: isOrgOnly ? 'Treating Facility' : 'Treating Provider',
      specialty,
      practiceName,
      phone,
      fax,
      email,
      website,
      npi,
      address: mergedAddress,
      documentId: doc?.id || primaryProv?.documentId,
      sourceDocument: primaryProv?.sourceDocument || docName,
      sourceTextSnippet: primaryProv?.sourceTextSnippet || snippet,
    })
  })

  return {
    ...extractedData,
    client: {},
    referral: {},
    injury: {},
    employer: {},
    contacts: consolidatedContacts,
    providers: consolidatedProviders,
    diagnosis: [],
    diagnoses: [],
    tests: [],
    appointments: [],
    status: {},
    tasks: [],
    activities: [],
  }
}

/**
 * Normalizes Bedrock / IntakeService extraction data into a standardized
 * entity list with multi-document and multi-occurrence clustering.
 */
export function normalizeExtractedEntities(extractedInput = {}, existingCase = {}, fallbackDocs = [], options = {}) {
  // Determine if this is an initial Smart Intake or draft case review
  const isSmartIntake = Boolean(
    options?.isSmartIntake ||
      extractedInput?.source === 'Smart Intake' ||
      extractedInput?.extensions?.pendingExtractions?.source === 'Smart Intake' ||
      existingCase?.extensions?.pendingExtractions?.source === 'Smart Intake' ||
      existingCase?.pendingExtractions?.source === 'Smart Intake' ||
      existingCase?.caseStatus?.toLowerCase() === 'draft' ||
      existingCase?.status === 'Draft' ||
      existingCase?.isDraft,
  )

  // Unwrap pendingExtractions container if present
  let extractedData = extractedInput
  let docs = fallbackDocs || []
  const rawText =
    extractedInput?.rawText ||
    extractedInput?.pendingExtractions?.rawText ||
    extractedInput?.extensions?.pendingExtractions?.rawText ||
    extractedInput?.pendingExtractions?.documents?.[0]?.textContent ||
    extractedInput?.extensions?.pendingExtractions?.documents?.[0]?.textContent ||
    extractedData?.rawText ||
    existingCase?.pendingExtractions?.rawText ||
    existingCase?.extensions?.pendingExtractions?.rawText ||
    existingCase?.pendingExtractions?.documents?.[0]?.textContent ||
    existingCase?.extensions?.pendingExtractions?.documents?.[0]?.textContent ||
    existingCase?.extractedRawText ||
    existingCase?.extensions?.extractedRawText ||
    fallbackDocs?.[0]?.textContent ||
    ''

  if (typeof extractedData === 'string') {
    try {
      extractedData = JSON.parse(extractedData)
    } catch {}
  }

  if (extractedData?.extensions?.pendingExtractions) {
    const pending = extractedData.extensions.pendingExtractions
    extractedData = pending.extractedData || pending
    if (pending.documents && pending.documents.length > 0) docs = pending.documents
  } else if (extractedData?.pendingExtractions) {
    const pending = extractedData.pendingExtractions
    extractedData = pending.extractedData || pending
    if (pending.documents && pending.documents.length > 0) docs = pending.documents
  } else if (extractedData?.extractedData) {
    if (extractedData.documents && extractedData.documents.length > 0) docs = extractedData.documents
    extractedData = extractedData.extractedData
  }

  if (typeof extractedData === 'string') {
    try {
      extractedData = JSON.parse(extractedData)
    } catch {}
  }

  // Detect Document Subtype / Archetype
  let docSubtype = docs[0]?.documentSubtype || extractedInput?.documentSubtype || extractedData?.documentSubtype || null

  if (!docSubtype && docs[0]?.properties) {
    try {
      const parsedProps = typeof docs[0].properties === 'string' ? JSON.parse(docs[0].properties) : docs[0].properties
      docSubtype = parsedProps?.['Document Subtype'] || parsedProps?.documentSubtype || null
    } catch {}
  }

  if (!docSubtype) {
    const docProps =
      extractedData?.extensions?.documentProperties ||
      extractedData?.documentProperties ||
      extractedInput?.extensions?.documentProperties
    if (docProps && typeof docProps === 'object') {
      const firstKey = Object.keys(docProps)[0]
      if (firstKey && docProps[firstKey]?.documentSubtype) {
        docSubtype = docProps[firstKey].documentSubtype
      }
    }
  }

  // Content-based heuristic fallback if metadata does not yet have Document Subtype
  if (!docSubtype) {
    const docText = (rawText || docs[0]?.textContent || docs[0]?.text || '').slice(0, 5000).toLowerCase()
    const docName = (docs[0]?.fileName || '').toLowerCase()
    const combinedSample = `${docName} ${docText}`

    if (
      /\b(emg\s*\/\s*ncs|electromyograph|nerve\s+conduction)\b/i.test(combinedSample) &&
      /\b(study|needle|conduction|latency|amplitude|impression|findings)\b/i.test(combinedSample)
    ) {
      docSubtype = 'Electrodiagnostic Study'
    } else if (
      /\b(mri|magnetic\s+resonance|ct\s+scan|computed\s+tomography|cat\s+scan|x-?ray|radiograph|ultrasound|sonogram|fluoroscop)\b/i.test(
        combinedSample,
      ) &&
      (/\b(impression|findings?|technique|radiolog|axial|sagittal|coronal|sequence|t1|t2)\b/i.test(docText) ||
        (/\b(mri|ct|x-?ray|imaging|scan)\b/i.test(docName) && /\b(report|study)\b/i.test(docName)))
    ) {
      docSubtype = 'Diagnostic Study'
    } else if (
      /\b(operative\s+report|post-?operative|pre-?operative|procedure\s+performed|surgeon)\b/i.test(combinedSample)
    ) {
      docSubtype = 'Operative Report'
    } else if (
      /\b(physical\s+therapy|pt\s+eval|daily\s+notes?|discharge\s+summary|range\s+of\s+motion)\b/i.test(combinedSample)
    ) {
      docSubtype = 'Physical Therapy'
    } else if (/\b(independent\s+medical\s+exam|qme|ame|ime\s+report)\b/i.test(combinedSample)) {
      docSubtype = 'Independent Medical Exam'
    }
  }

  const isDiagnosticStudyArchetype = docSubtype === 'Diagnostic Study' || docSubtype === 'Electrodiagnostic Study'

  const entities = []
  let nextId = 1
  const defaultDocId = docs[0]?.id || 'doc-1'
  const defaultDocName = docs[0]?.fileName || 'Intake_Document.txt'

  const isDocWorkStatusReport = (doc, idx = 0) => {
    const dName = doc?.fileName || doc?.documentName || doc?.name || (idx === 0 ? defaultDocName : '')
    const dText = doc?.textContent || doc?.text || (idx === 0 ? rawText : '')
    return isWorkStatusReportDocument(dName, dText)
  }
  const isSingleWorkStatusReportUpload =
    docs.length > 0
      ? docs.every((d, idx) => isDocWorkStatusReport(d, idx))
      : isWorkStatusReportDocument(defaultDocName, rawText)

  const isDocBusinessCard = (doc, idx = 0) => {
    const dName = doc?.fileName || doc?.documentName || doc?.name || (idx === 0 ? defaultDocName : '')
    const dText = doc?.textContent || doc?.text || (idx === 0 ? rawText : '')
    return isBusinessCardDocument(dName, dText)
  }
  const isSingleBusinessCardUpload =
    !isSingleWorkStatusReportUpload &&
    (docs.length > 0
      ? docs.every((d, idx) => isDocBusinessCard(d, idx))
      : isBusinessCardDocument(defaultDocName, rawText))

  if (isSingleBusinessCardUpload) {
    extractedData = consolidateBusinessCardExtractedData(extractedData, docs, defaultDocName, rawText)
  }

  // 1. Referral & Insurance Guardrails (Only propose when non-empty extracted value exists)
  const ref =
    extractedData.referral ||
    extractedData.Referral ||
    extractedData.extensions?.referral ||
    extractedData.extensions?.Referral ||
    {}
  const existingRef = existingCase.referral || existingCase.Referral || {}

  const cleanCombinedFieldSegment = val => {
    if (!val || typeof val !== 'string') return val
    return val.replace(/\s+(?:\||I)\s+[A-Za-z][A-Za-z0-9\s#/()]{1,28}\s*:.*$/i, '').trim()
  }

  const rawClaimNumber = ref.claimNumber || ref.ClaimNumber
  const claimNumber = cleanCombinedFieldSegment(rawClaimNumber)
  let embeddedPolicyFromClaim = null
  if (rawClaimNumber && typeof rawClaimNumber === 'string') {
    const pMatch = rawClaimNumber.match(
      /(?:\||I)\s*Policy\s*(?:#|Number|No\.?)\s*:\s*([^\r\n|]+?)(?=\s+(?:\||I)\s+[A-Za-z]|\s*$)/i,
    )
    if (pMatch && pMatch[1]) embeddedPolicyFromClaim = pMatch[1].trim()
  }

  const existingClaimVal =
    existingRef.claimNumber ||
    existingRef.ClaimNumber ||
    existingCase.claimNumber ||
    existingCase.ClaimNumber ||
    existingCase.referral?.claimNumber ||
    existingCase.referral?.ClaimNumber ||
    ''
  const isDuplicateClaim =
    !isSmartIntake && existingClaimVal && isEquivalentClaimOrPolicy(existingClaimVal, claimNumber)

  if (claimNumber && String(claimNumber).trim().length > 0 && !isDuplicateClaim) {
    const snippet =
      ref.claimSourceSnippet ||
      ref.ClaimSourceSnippet ||
      ref.sourceSnippet ||
      ref.SourceSnippet ||
      extractedData.sourceSnippet ||
      ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [claimNumber, 'claim'])
    entities.push({
      id: `ref-claim-${nextId++}`,
      category: 'referral',
      field: 'Claim Number',
      currentValue: isSmartIntake ? '' : existingClaimVal,
      extractedValue: String(claimNumber).trim(),
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: ref.documentId || matchedDoc?.id || defaultDocId,
      documentName: ref.documentName || matchedDoc?.fileName || defaultDocName,
      page: ref.page || loc?.page,
      lineNumber: ref.lineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  }

  const policyNumber = cleanCombinedFieldSegment(ref.policyNumber || ref.PolicyNumber || embeddedPolicyFromClaim)
  const existingPolicyVal =
    existingRef.policyNumber ||
    existingRef.PolicyNumber ||
    existingCase.policyNumber ||
    existingCase.PolicyNumber ||
    existingCase.referral?.policyNumber ||
    existingCase.referral?.PolicyNumber ||
    ''
  const isDuplicatePolicy =
    !isSmartIntake && existingPolicyVal && isEquivalentClaimOrPolicy(existingPolicyVal, policyNumber)

  if (policyNumber && String(policyNumber).trim().length > 0 && !isDuplicatePolicy) {
    const snippet = ref.policySourceSnippet || ref.PolicySourceSnippet || ref.sourceSnippet || ref.SourceSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [policyNumber, 'policy'])
    entities.push({
      id: `ref-policy-${nextId++}`,
      category: 'referral',
      field: 'Policy Number',
      currentValue: isSmartIntake ? '' : existingPolicyVal,
      extractedValue: String(policyNumber).trim(),
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: ref.documentId || matchedDoc?.id || defaultDocId,
      documentName: ref.documentName || matchedDoc?.fileName || defaultDocName,
      page: ref.page || loc?.page,
      lineNumber: ref.lineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  }

  const refDate = ref.date || ref.Date
  const existingRefDate = existingRef.date || existingRef.Date || existingCase.date || ''
  const isDuplicateRefDate = !isSmartIntake && existingRefDate && isEquivalentDate(existingRefDate, refDate)

  if (refDate && String(refDate).trim().length > 0 && !isDuplicateRefDate) {
    const snippet = ref.sourceSnippet || ref.SourceSnippet || ref.dateSourceSnippet || ''
    const formattedRefDate = formatProposedDate(refDate)
    const rawRefDateStr = String(refDate).trim()
    const allDocsText = docs
      .map(d => d?.textContent || (d?.lineBlocks || []).map(b => b.text).join(' ') || '')
      .join(' ')
    const appearsInDoc =
      (snippet && snippet.trim().length > 0) ||
      (formattedRefDate && allDocsText.includes(formattedRefDate)) ||
      (rawRefDateStr && allDocsText.includes(rawRefDateStr))

    if (appearsInDoc) {
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [
        refDate,
        formattedRefDate,
        'referral date',
      ])
      entities.push({
        id: `ref-date-${nextId++}`,
        category: 'referral',
        field: 'Date of Referral',
        currentValue: isSmartIntake ? '' : formatProposedDate(existingRefDate),
        extractedValue: formattedRefDate,
        sourceSnippet: snippet || loc?.matchedSnippet || formattedRefDate,
        documentId: ref.documentId || matchedDoc?.id || defaultDocId,
        documentName: ref.documentName || matchedDoc?.fileName || defaultDocName,
        page: ref.page || loc?.page,
        lineNumber: ref.lineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // 2. Demographics (Only propose when non-empty extracted values exist)
  const client =
    extractedData.client ||
    extractedData.Client ||
    extractedData.extensions?.client ||
    extractedData.extensions?.Client ||
    {}
  const existingClient = existingCase.client || existingCase.Client || existingCase || {}

  let firstName = cleanCombinedFieldSegment((client.firstName || client.FirstName || '').trim())
  let lastName = cleanCombinedFieldSegment((client.lastName || client.LastName || '').trim())
  const rawName = cleanCombinedFieldSegment((client.name || client.Name || '').trim())
  if (/^(?:RN|LPN|MD|DO|PT|DPT|DC|NP|PA-?C|CCM|CRC)$/i.test(firstName) && lastName && /\s+/.test(lastName)) {
    const nameTokens = lastName.split(/\s+/).filter(Boolean)
    firstName = nameTokens.slice(0, -1).join(' ')
    lastName = nameTokens[nameTokens.length - 1]
  }
  firstName = firstName.replace(/^(?:RN|LPN|MD|DO|PT|DPT|DC|NP|PA-?C|CCM|CRC)\b[\s,.-]*/i, '').trim()
  lastName = lastName.replace(/(?:,\s*(?:RN|LPN|MD|DO|PT|DPT|DC|NP|PA-?C|CCM|CRC)\b)+.*$/i, '').trim()
  const snippet =
    client.sourceTextSnippet || client.SourceTextSnippet || client.sourceSnippet || client.SourceSnippet || ''

  if (rawName && (!firstName || !lastName)) {
    if (rawName.includes(',')) {
      const parts = rawName
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
      if (parts.length >= 2) {
        lastName = parts[0]
        firstName = parts[1]
      } else if (parts.length === 1) {
        lastName = parts[0]
      }
    } else {
      const parts = rawName.split(/\s+/)
      firstName = parts[0] || ''
      lastName = parts.slice(1).join(' ') || ''
    }
  } else if (firstName.includes(',')) {
    const parts = firstName
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    if (parts.length >= 2) {
      lastName = parts[0]
      firstName = parts[1]
    } else if (parts.length === 1) {
      firstName = parts[0]
    }
  }

  // Detect reversed name: if snippet is "Aspen, , Howard" or "Aspen, Howard", it means Aspen is Last and Howard is First
  if (firstName && lastName && snippet) {
    const escapedFirst = (firstName || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const escapedLast = (lastName || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const reversedInSnippet = new RegExp(`\\b${escapedFirst}\\s*,\\s*,?\\s*${escapedLast}\\b`, 'i')
    if (reversedInSnippet.test(snippet)) {
      const temp = firstName
      firstName = lastName
      lastName = temp
    }
  }
  if (!isSingleWorkStatusReportUpload && (firstName || lastName)) {
    const extractedName = `${firstName} ${lastName}`.trim()
    if (extractedName.length > 0) {
      const currentName = `${existingClient.firstName || existingClient.FirstName || ''} ${
        existingClient.lastName || existingClient.LastName || ''
      }`.trim()
      const isDuplicateName = !isSmartIntake && currentName && isEquivalentName(currentName, extractedName)
      if (!isDuplicateName) {
        const snippet =
          client.sourceTextSnippet || client.SourceTextSnippet || client.sourceSnippet || client.SourceSnippet || ''
        const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [firstName, lastName, extractedName])
        if (!isDocWorkStatusReport(matchedDoc)) {
          entities.push({
            id: `demo-name-${nextId++}`,
            category: 'demographics',
            field: 'Claimant Name',
            currentValue: isSmartIntake ? '' : currentName,
            extractedValue: extractedName,
            sourceSnippet: snippet || loc?.matchedSnippet || '',
            documentId: client.documentId || matchedDoc?.id || defaultDocId,
            documentName: client.documentName || matchedDoc?.fileName || defaultDocName,
            page: client.page || loc?.page,
            lineNumber: client.lineNumber || loc?.lineNumber,
            occurrenceIndex: 1,
            totalOccurrences: 1,
            status: 'pending',
          })
        }
      }
    }
  }

  const dob = client.birthdate || client.Birthdate || client.dateOfBirth || client.DateOfBirth
  const currentDob =
    existingClient.birthdate || existingClient.Birthdate || existingClient.dateOfBirth || existingCase.birthdate || ''
  const isDuplicateDob = !isSmartIntake && currentDob && isEquivalentDate(currentDob, dob)

  if (!isSingleWorkStatusReportUpload && dob && String(dob).trim().length > 0 && !isDuplicateDob) {
    const rawSnippet =
      client.dobSourceSnippet || client.DobSourceSnippet || client.sourceSnippet || client.SourceSnippet || ''
    // Discard snippet if it is merely the form label without the actual date digits
    const cleanRawSnippet = normalizeTextKey(rawSnippet)
    const isJustLabel =
      cleanRawSnippet === 'date of birth' ||
      cleanRawSnippet === 'birthdate' ||
      cleanRawSnippet === 'dob' ||
      cleanRawSnippet === 'birth date'
    const snippet = isJustLabel ? '' : rawSnippet

    const formattedDob = formatProposedDate(dob)
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [
      formattedDob,
      dob,
      Moment(dob).format('M/D/YYYY'),
      'birth',
      'dob',
    ])
    let matchedLocSnippet = loc?.matchedSnippet || ''
    if (matchedLocSnippet && normalizeTextKey(matchedLocSnippet) === 'date of birth') {
      matchedLocSnippet = ''
    }

    if (!isDocWorkStatusReport(matchedDoc)) {
      entities.push({
        id: `demo-dob-${nextId++}`,
        category: 'demographics',
        field: 'Date of Birth',
        currentValue: isSmartIntake ? '' : formatProposedDate(currentDob),
        extractedValue: formattedDob,
        sourceSnippet: snippet || matchedLocSnippet || formattedDob,
        documentId: client.documentId || matchedDoc?.id || defaultDocId,
        documentName: client.documentName || matchedDoc?.fileName || defaultDocName,
        page: client.page || loc?.page,
        lineNumber: client.lineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // Claimant Phone
  const rawClientPhone = client.phone || client.Phone || client.telephone || client.Telephone
  const clientPhone = cleanCombinedFieldSegment(
    typeof rawClientPhone === 'string'
      ? rawClientPhone.replace(/\s+(?:\||I)\s*(?:Client\s+|Patient\s+|Claimant\s+)?Email\s*:.*$/i, '').trim()
      : rawClientPhone,
  )
  const currentPhone = existingClient.phone || existingClient.Phone || existingCase.phone || ''
  const isDuplicatePhone = !isSmartIntake && currentPhone && isEquivalentPhone(currentPhone, clientPhone)

  if (!isSingleWorkStatusReportUpload && clientPhone && String(clientPhone).trim().length > 0 && !isDuplicatePhone) {
    const cleanPhone = String(clientPhone).trim()
    const snippet =
      client.phoneSourceSnippet || client.sourceSnippet || client.SourceSnippet || client.sourceTextSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [cleanPhone, 'phone', 'tel'])
    if (!isDocWorkStatusReport(matchedDoc)) {
      entities.push({
        id: `demo-phone-${nextId++}`,
        category: 'demographics',
        field: 'Claimant Phone',
        currentValue: isSmartIntake ? '' : currentPhone,
        extractedValue: cleanPhone,
        sourceSnippet: snippet || loc?.matchedSnippet || cleanPhone,
        documentId: client.documentId || matchedDoc?.id || defaultDocId,
        documentName: client.documentName || matchedDoc?.fileName || defaultDocName,
        page: client.page || loc?.page,
        lineNumber: client.lineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // Claimant Address
  const clientAddr = client.address || client.Address || {}
  const clientAddrStr = [
    clientAddr.line1 || clientAddr.Line1 || clientAddr.address1 || clientAddr.Address1,
    clientAddr.line2 || clientAddr.Line2 || clientAddr.address2 || clientAddr.Address2,
    clientAddr.city || clientAddr.City,
    clientAddr.state || clientAddr.State,
    clientAddr.zip || clientAddr.Zip || clientAddr.postalCode || clientAddr.PostalCode,
  ]
    .filter(Boolean)
    .join(', ')
  if (!isSingleWorkStatusReportUpload && clientAddrStr && clientAddrStr.trim().length > 0) {
    const existingAddr = existingClient.address || existingClient.Address || existingCase.address || {}
    const existingAddrStr = [
      existingAddr.line1 || existingAddr.Line1,
      existingAddr.city || existingAddr.City,
      existingAddr.state || existingAddr.State,
      existingAddr.zip || existingAddr.Zip,
    ]
      .filter(Boolean)
      .join(', ')
    const isDuplicateAddr = !isSmartIntake && existingAddrStr && isEquivalentAddress(existingAddrStr, clientAddrStr)

    if (!isDuplicateAddr) {
      const snippet =
        client.addressSourceSnippet || client.sourceSnippet || client.SourceSnippet || client.sourceTextSnippet || ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [
        clientAddr.line1 || clientAddr.Line1,
        clientAddr.city || clientAddr.City,
        clientAddrStr,
        'address',
      ])
      if (!isDocWorkStatusReport(matchedDoc)) {
        // Preserve the complete address string instead of truncating to just line 1
        const finalAddressSnippet = snippet && snippet.length >= clientAddrStr.length / 2 ? snippet : clientAddrStr
        entities.push({
          id: `demo-addr-${nextId++}`,
          category: 'demographics',
          field: 'Claimant Address',
          currentValue: isSmartIntake ? '' : existingAddrStr,
          extractedValue: clientAddrStr,
          attributes: {
            line1: clientAddr.line1 || clientAddr.Line1 || clientAddr.address1 || clientAddr.Address1,
            line2: clientAddr.line2 || clientAddr.Line2 || clientAddr.address2 || clientAddr.Address2,
            city: clientAddr.city || clientAddr.City,
            state: clientAddr.state || clientAddr.State,
            zip: clientAddr.zip || clientAddr.Zip || clientAddr.postalCode || clientAddr.PostalCode,
          },
          sourceSnippet: finalAddressSnippet,
          documentId: client.documentId || matchedDoc?.id || defaultDocId,
          documentName: client.documentName || matchedDoc?.fileName || defaultDocName,
          page: client.page || loc?.page,
          lineNumber: client.lineNumber || loc?.lineNumber,
          occurrenceIndex: 1,
          totalOccurrences: 1,
          status: 'pending',
        })
      }
    }
  }

  // Claimant Occupation
  const uncleanedOccupation =
    client.occupation ||
    client.Occupation ||
    client.jobTitle ||
    client.JobTitle ||
    extractedData.occupation ||
    extractedData.Occupation ||
    (extractedData.employer || extractedData.Employer || {}).occupation ||
    (extractedData.employer || extractedData.Employer || {}).Occupation
  let embeddedHireDateFromOcc = null
  if (uncleanedOccupation && typeof uncleanedOccupation === 'string') {
    const hMatch = uncleanedOccupation.match(
      /(?:\||I)\s*(?:Date\s+of\s+Hire|Hire\s+Date)\s*:\s*([^\r\n|]+?)(?=\s+(?:\||I)\s+[A-Za-z]|\s*$)/i,
    )
    if (hMatch && hMatch[1]) embeddedHireDateFromOcc = hMatch[1].trim()
  }
  const rawOccupation = cleanCombinedFieldSegment(
    typeof uncleanedOccupation === 'string'
      ? uncleanedOccupation.replace(/\s+(?:\||I)\s*(?:Date\s+of\s+Hire|Hire\s+Date)\s*:.*$/i, '').trim()
      : uncleanedOccupation,
  )
  if (!isSingleWorkStatusReportUpload && rawOccupation && String(rawOccupation).trim().length > 0) {
    const cleanOcc = String(rawOccupation).trim()
    const currentOcc = existingClient.occupation || existingClient.Occupation || existingCase.occupation || ''
    const isDuplicateOcc = !isSmartIntake && currentOcc && isEquivalentText(currentOcc, cleanOcc)

    if (!isDuplicateOcc) {
      const snippet =
        client.occupationSourceSnippet || client.sourceSnippet || client.SourceSnippet || client.sourceTextSnippet || ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [cleanOcc, 'occupation', 'job title'])
      if (!isDocWorkStatusReport(matchedDoc)) {
        entities.push({
          id: `demo-occ-${nextId++}`,
          category: 'demographics',
          field: 'Occupation',
          currentValue: isSmartIntake ? '' : currentOcc,
          extractedValue: cleanOcc,
          sourceSnippet: snippet || loc?.matchedSnippet || cleanOcc,
          documentId: client.documentId || matchedDoc?.id || defaultDocId,
          documentName: client.documentName || matchedDoc?.fileName || defaultDocName,
          page: client.page || loc?.page,
          lineNumber: client.lineNumber || loc?.lineNumber,
          occurrenceIndex: 1,
          totalOccurrences: 1,
          status: 'pending',
        })
      }
    }
  }

  const injury =
    extractedData.injury ||
    extractedData.Injury ||
    extractedData.extensions?.injury ||
    extractedData.extensions?.Injury ||
    {}
  const existingInjury = existingCase.injury || existingCase.Injury || {}
  const docsToSearch = [
    ...(Array.isArray(docs) ? docs : []),
    ...(Array.isArray(fallbackDocs) ? fallbackDocs : []),
  ].filter(Boolean)
  let explicitDoi = extractExplicitDateOfInjury(docsToSearch)
  if (!explicitDoi && (rawText || extractedData.rawText || existingCase?.extractedRawText)) {
    explicitDoi = extractExplicitDateOfInjury({
      textContent: rawText || extractedData.rawText || existingCase?.extractedRawText,
    })
  }
  const rawInjuryDate =
    injury.date ||
    injury.Date ||
    injury.dateOfInjury ||
    injury.DateOfInjury ||
    extractedData.referral?.dateOfInjury ||
    extractedData.referral?.DateOfInjury ||
    extractedData.dateOfInjury ||
    extractedData.DateOfInjury ||
    extractedData.injuryDate ||
    extractedData.InjuryDate ||
    extractedData.doi ||
    extractedData.DOI
  const injuryDate = explicitDoi?.date || rawInjuryDate
  const currentDoi =
    existingInjury.date ||
    existingInjury.Date ||
    existingInjury.incidentDate ||
    existingInjury.IncidentDate ||
    existingInjury.dateOfInjury ||
    existingInjury.DateOfInjury ||
    existingCase.dateOfInjury ||
    existingCase.incidentDate ||
    ''
  const isDuplicateDoi = !isSmartIntake && currentDoi && isEquivalentDate(currentDoi, injuryDate)

  if (injuryDate && String(injuryDate).trim().length > 0 && !isDuplicateDoi) {
    const rawSnippet = explicitDoi?.snippet || injury.dateSourceSnippet || injury.DateSourceSnippet || ''
    const formattedDoi = formatProposedDate(injuryDate)

    // Discard snippet if it is merely the form label without the actual date digits or if it belongs to a competing notification label
    const cleanRawSnippet = normalizeTextKey(rawSnippet)
    const isCompetingSnippet =
      /\b(?:notified|employer\s+notified|date\s+employer\s+notified)\b/i.test(rawSnippet) &&
      !/\b(?:injury|accident|illness)\b/i.test(rawSnippet)
    const isJustLabel =
      isCompetingSnippet ||
      !/\d/.test(rawSnippet) ||
      cleanRawSnippet === 'date of injury' ||
      cleanRawSnippet === 'injury date' ||
      cleanRawSnippet === 'date of accident' ||
      cleanRawSnippet === 'accident date' ||
      cleanRawSnippet === 'date of injury illness' ||
      cleanRawSnippet === 'injury illness date'
    const snippet = isJustLabel ? '' : rawSnippet

    const mDoi = Moment(injuryDate, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'YYYY/MM/DD'], true)
    const unpaddedDoi = mDoi.isValid() ? mDoi.format('M/D/YYYY') : formattedDoi

    const preferredDoiDoc =
      docsToSearch.find(d => d?.id === (explicitDoi?.documentId || injury.documentId)) ||
      findMatchingDocForItem(injury, docsToSearch)
    const { loc, doc: matchedDoiDoc } = locateSnippetInAllDocs(
      snippet,
      docsToSearch,
      [
        `injury illness date ${formattedDoi}`,
        `injury illness date: ${formattedDoi}`,
        `injury illness date ${unpaddedDoi}`,
        `date of injury illness ${formattedDoi}`,
        `date of injury: ${formattedDoi}`,
        `date of injury ${formattedDoi}`,
        `injury date: ${formattedDoi}`,
        `injury date ${formattedDoi}`,
        `date of accident ${formattedDoi}`,
        `accident date ${formattedDoi}`,
        formattedDoi,
        unpaddedDoi,
        injuryDate,
      ],
      {
        preferredDoc: preferredDoiDoc,
        excludeTerms: ['treatment', 'treated', 'notified', 'employer notified', 'date employer'],
      },
    )
    const targetDoc = matchedDoiDoc || preferredDoiDoc || docsToSearch[0] || docs[0] || {}
    // Ensure matched snippet is not the accident narrative, treatment line, or bare label
    let matchedLocSnippet = loc?.matchedSnippet || ''
    if (
      matchedLocSnippet &&
      (!/\d/.test(matchedLocSnippet) ||
        isAccidentNarrativeText(matchedLocSnippet) ||
        /\b(?:treatment|treated)\b/i.test(matchedLocSnippet) ||
        (/\b(?:notified|employer\s+notified)\b/i.test(matchedLocSnippet) &&
          !/\b(?:injury|accident|illness)\b/i.test(matchedLocSnippet)) ||
        normalizeTextKey(matchedLocSnippet) === 'date of injury' ||
        normalizeTextKey(matchedLocSnippet) === 'injury date' ||
        normalizeTextKey(matchedLocSnippet) === 'injury illness date' ||
        (extractedData.injury?.description &&
          matchedLocSnippet.includes(extractedData.injury.description.slice(0, 20))))
    ) {
      matchedLocSnippet = ''
    }

    let validExplicitSnippet = explicitDoi?.snippet || ''
    if (
      /\b(?:notified|employer\s+notified)\b/i.test(validExplicitSnippet) &&
      !/\b(?:injury|accident|illness)\b/i.test(validExplicitSnippet)
    ) {
      validExplicitSnippet = ''
    }

    const finalSourceSnippet = validExplicitSnippet || snippet || matchedLocSnippet || formattedDoi

    let finalLineNumber = explicitDoi?.lineNumber || loc?.lineNumber || injury.lineNumber
    if (!explicitDoi?.lineNumber && injury.lineNumber && targetDoc?.lineBlocks && Array.isArray(targetDoc.lineBlocks)) {
      const block = targetDoc.lineBlocks.find(b => b.lineNumber === injury.lineNumber)
      if (
        block &&
        /\b(?:notified|employer\s+notified|date\s+employer)\b/i.test(block.text) &&
        !/\b(?:injury|accident|illness)\b/i.test(block.text)
      ) {
        finalLineNumber = loc?.lineNumber || null
      }
    }

    entities.push({
      id: `demo-doi-${nextId++}`,
      category: 'demographics',
      field: 'Date of Injury',
      currentValue: isSmartIntake ? '' : formatProposedDate(currentDoi),
      extractedValue: formattedDoi,
      sourceSnippet: finalSourceSnippet,
      documentId: explicitDoi?.documentId || injury.documentId || targetDoc.id || defaultDocId,
      documentName: explicitDoi?.documentName || injury.documentName || targetDoc.fileName || defaultDocName,
      page: explicitDoi?.page || injury.page || loc?.page || 1,
      lineNumber: finalLineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  }

  // Injury Description Narrative
  const rawDescriptions =
    injury.descriptions ||
    injury.Descriptions ||
    (injury.description ? [injury.description] : injury.Description ? [injury.Description] : [])
  const extractedInjuryDescs = rawDescriptions
    .map(descObj =>
      typeof descObj === 'string'
        ? descObj
        : descObj.description || descObj.Description || descObj.text || descObj.Text || '',
    )
    .filter(Boolean)
  rawDescriptions.forEach((descObj, idx) => {
    const descText =
      typeof descObj === 'string'
        ? descObj
        : descObj.description || descObj.Description || descObj.text || descObj.Text || ''
    if (!descText || descText.trim().length === 0) return
    const snippet =
      (typeof descObj === 'object'
        ? descObj.sourceTextSnippet || descObj.SourceTextSnippet || descObj.sourceSnippet || descObj.SourceSnippet
        : '') ||
      injury.sourceTextSnippet ||
      injury.SourceTextSnippet ||
      injury.sourceSnippet ||
      injury.SourceSnippet ||
      ''
    const fullSnippet = snippet && snippet.length >= descText.length * 0.7 ? snippet : snippet || descText.trim()
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(fullSnippet, docs, [
      snippet,
      descText.slice(0, 30),
      'detailed accident description',
      'accident description',
      'injury description',
      'injury',
      'accident',
    ])
    const sentenceCasedDesc = toSentenceCase(descText.trim())
    const existingDescVal =
      existingInjury.descriptions?.[0]?.description ||
      existingInjury.descriptions?.[0]?.text ||
      (typeof existingInjury.descriptions?.[0] === 'string' ? existingInjury.descriptions[0] : '') ||
      existingInjury.description ||
      existingInjury.Description ||
      ''
    entities.push({
      id: `injury-desc-${idx + 1}-${nextId++}`,
      category: 'referral',
      field: 'Injury Description',
      currentValue: existingDescVal,
      extractedValue: sentenceCasedDesc,
      attributes: {
        description: sentenceCasedDesc,
        provider: typeof descObj === 'object' ? descObj.provider || descObj.Provider : 'Report',
      },
      sourceSnippet: fullSnippet,
      documentId:
        (typeof descObj === 'object' && (descObj.documentId || descObj.DocumentId)) ||
        injury.documentId ||
        matchedDoc?.id ||
        defaultDocId,
      documentName:
        (typeof descObj === 'object' &&
          (descObj.documentName || descObj.DocumentName || descObj.sourceDocument || descObj.SourceDocument)) ||
        injury.documentName ||
        matchedDoc?.fileName ||
        defaultDocName,
      page:
        (typeof descObj === 'object' && (descObj.page || descObj.Page)) || injury.page || injury.Page || loc?.page || 1,
      lineNumber: loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  })

  // Mechanism of Injury
  const moi =
    injury.moi ||
    injury.Moi ||
    injury.mechanism ||
    injury.Mechanism ||
    injury.mechanismOfInjury ||
    injury.MechanismOfInjury
  if (moi && String(moi).trim().length > 0) {
    const cleanMoi = String(moi).trim()
    const currentMoi = existingInjury.moi || existingInjury.mechanism || ''
    const snippet =
      injury.sourceSnippet || injury.SourceSnippet || injury.sourceTextSnippet || injury.SourceTextSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [cleanMoi, 'mechanism', 'cause of injury'])
    entities.push({
      id: `injury-moi-${nextId++}`,
      category: 'referral',
      field: 'Mechanism of Injury',
      currentValue: currentMoi,
      extractedValue: cleanMoi,
      sourceSnippet: snippet || loc?.matchedSnippet || cleanMoi,
      documentId: injury.documentId || matchedDoc?.id || defaultDocId,
      documentName: injury.documentName || matchedDoc?.fileName || defaultDocName,
      page: injury.page || loc?.page || 1,
      lineNumber: injury.lineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  }

  // Nature of Injury
  const noi =
    injury.noi || injury.Noi || injury.nature || injury.Nature || injury.natureOfInjury || injury.NatureOfInjury
  if (noi && String(noi).trim().length > 0) {
    const cleanNoi = String(noi).trim()
    const currentNoi = existingInjury.noi || existingInjury.nature || ''
    const snippet =
      injury.sourceSnippet || injury.SourceSnippet || injury.sourceTextSnippet || injury.SourceTextSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [
      cleanNoi,
      'nature of injury',
      'physical injuries',
    ])
    entities.push({
      id: `injury-noi-${nextId++}`,
      category: 'referral',
      field: 'Nature of Injury',
      currentValue: currentNoi,
      extractedValue: cleanNoi,
      sourceSnippet: snippet || loc?.matchedSnippet || cleanNoi,
      documentId: injury.documentId || matchedDoc?.id || defaultDocId,
      documentName: injury.documentName || matchedDoc?.fileName || defaultDocName,
      page: injury.page || loc?.page || 1,
      lineNumber: injury.lineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  }

  // Employer Information
  const emp =
    extractedData.employer ||
    extractedData.Employer ||
    extractedData.extensions?.employer ||
    extractedData.extensions?.Employer ||
    {}
  const existingEmp = existingCase.employer || existingCase.Employer || {}
  const empName = emp.name || emp.Name || emp.employerName || emp.EmployerName
  if (empName && String(empName).trim().length > 0) {
    const cleanEmpName = String(empName).trim()
    const currentEmpName = existingEmp.name || existingEmp.Name || ''
    const isDuplicateEmp = !isSmartIntake && currentEmpName && isEquivalentText(currentEmpName, cleanEmpName)

    if (!isDuplicateEmp) {
      const empAddr = emp.address || emp.Address || {}
      const addrStr = [
        empAddr.line1 || empAddr.Line1,
        empAddr.city || empAddr.City,
        empAddr.state || empAddr.State,
        empAddr.zip || empAddr.Zip,
      ]
        .filter(Boolean)
        .join(', ')
      const snippet = emp.sourceTextSnippet || emp.SourceTextSnippet || emp.sourceSnippet || emp.SourceSnippet || ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [
        cleanEmpName,
        empAddr.line1 || empAddr.Line1,
        'employer',
      ])
      entities.push({
        id: `emp-name-${nextId++}`,
        category: 'referral',
        field: 'Employer',
        currentValue: isSmartIntake ? '' : currentEmpName,
        extractedValue: `${cleanEmpName}${addrStr ? ` (${addrStr})` : ''}`,
        attributes: {
          name: cleanEmpName,
          address: empAddr,
          phone: cleanCombinedFieldSegment(emp.contact?.phone || emp.Contact?.Phone || emp.phone || emp.Phone),
          occupation: cleanCombinedFieldSegment(emp.occupation || emp.Occupation),
        },
        sourceSnippet: snippet || loc?.matchedSnippet || cleanEmpName,
        documentId: emp.documentId || matchedDoc?.id || defaultDocId,
        documentName: emp.documentName || matchedDoc?.fileName || defaultDocName,
        page: emp.page || emp.Page || loc?.page || 1,
        lineNumber: emp.lineNumber || emp.LineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // Date of Hire
  const dateOfHire =
    emp.dateOfHire ||
    emp.DateOfHire ||
    emp.hireDate ||
    emp.HireDate ||
    extractedData.claimant?.dateOfHire ||
    extractedData.Claimant?.dateOfHire ||
    extractedData.claimant?.DateOfHire ||
    extractedData.Claimant?.DateOfHire ||
    embeddedHireDateFromOcc
  if (dateOfHire && String(dateOfHire).trim().length > 0) {
    const formattedHireDate = formatProposedDate(dateOfHire)
    const currentHireDate = formatProposedDate(
      existingEmp.dateOfHire ||
        existingEmp.DateOfHire ||
        existingEmp.hireDate ||
        existingEmp.HireDate ||
        existingCase.claimant?.dateOfHire ||
        existingCase.Claimant?.dateOfHire ||
        existingCase.claimant?.DateOfHire ||
        existingCase.Claimant?.DateOfHire ||
        '',
    )
    const isDuplicateHireDate = !isSmartIntake && currentHireDate && isEquivalentDate(currentHireDate, dateOfHire)

    if (!isDuplicateHireDate) {
      const snippet =
        emp.dateOfHireSourceSnippet ||
        emp.DateOfHireSourceSnippet ||
        emp.hireDateSourceSnippet ||
        emp.HireDateSourceSnippet ||
        ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [
        formattedHireDate,
        String(dateOfHire),
        'date of hire',
        'hire date',
      ])
      entities.push({
        id: `emp-hire-${nextId++}`,
        category: 'referral',
        field: 'Date of Hire',
        currentValue: isSmartIntake ? '' : currentHireDate,
        extractedValue: formattedHireDate,
        attributes: { dateOfHire: formattedHireDate },
        sourceSnippet: snippet || loc?.matchedSnippet || formattedHireDate,
        documentId: emp.documentId || matchedDoc?.id || defaultDocId,
        documentName: emp.documentName || matchedDoc?.fileName || defaultDocName,
        page: emp.dateOfHirePage || emp.DateOfHirePage || emp.page || emp.Page || loc?.page || 1,
        lineNumber: emp.dateOfHireLineNumber || emp.DateOfHireLineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // 4. Providers (declare early so insurer fallback can check rawProviders)
  const rawProviders =
    extractedData.providers ||
    extractedData.Providers ||
    extractedData.extensions?.providers ||
    extractedData.extensions?.Providers ||
    []

  // 2. Insurance Carrier
  let insurer =
    ref.insuranceCompany ||
    ref.InsuranceCompany ||
    extractedData.insuranceCompany ||
    extractedData.InsuranceCompany ||
    extractedData.carrier ||
    extractedData.Carrier ||
    extractedData.extensions?.insuranceCompany ||
    extractedData.extensions?.InsuranceCompany ||
    {}
  const existingInsurer =
    existingCase.insurer ||
    existingCase.Insurer ||
    existingCase.referral?.insuranceCompany ||
    existingCase.referral?.InsuranceCompany ||
    {}
  let insurerName = insurer.name || insurer.Name || (typeof insurer === 'string' ? insurer : '')
  if (!insurerName && Array.isArray(rawProviders)) {
    const carrierProv = rawProviders.find(p => {
      const n = (p.name || p.Name || p.practiceName || p.PracticeName || '').toLowerCase()
      const t = (p.providerType || p.ProviderType || '').toLowerCase()
      return /\b(insurance|carrier|assurance|indemnity)\b/i.test(n) || /\b(insurance|carrier)\b/i.test(t)
    })
    if (carrierProv) {
      insurer = carrierProv
      insurerName = carrierProv.name || carrierProv.Name || carrierProv.practiceName || carrierProv.PracticeName || ''
    }
  }
  if (insurerName && String(insurerName).trim().length > 0) {
    const cleanInsurer = String(insurerName).trim()
    const currentInsurerName =
      existingInsurer.name || existingInsurer.Name || (typeof existingInsurer === 'string' ? existingInsurer : '')
    const isDuplicateInsurer = currentInsurerName && isEquivalentText(currentInsurerName, cleanInsurer)

    if (!isDuplicateInsurer) {
      const snippet =
        insurer.sourceTextSnippet || insurer.SourceTextSnippet || insurer.sourceSnippet || insurer.SourceSnippet || ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [
        cleanInsurer,
        cleanInsurer.replace(/\b(?:insurance\s+)?company\b/i, '').trim(),
        'insurance carrier',
        'claims administrator',
        'insurance',
        'carrier',
      ])
      entities.push({
        id: `ins-name-${nextId++}`,
        category: 'referral',
        field: 'Insurance Carrier',
        currentValue: currentInsurerName,
        extractedValue: cleanInsurer,
        attributes: { name: cleanInsurer },
        sourceSnippet: snippet || loc?.matchedSnippet || cleanInsurer,
        documentId: insurer.documentId || matchedDoc?.id || defaultDocId,
        documentName: insurer.documentName || matchedDoc?.fileName || defaultDocName,
        page: insurer.page || insurer.Page || loc?.page || 1,
        lineNumber: insurer.lineNumber || insurer.LineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // Pre-extract rawTests for cross-referencing in diagnoses suppression
  const rawTests =
    extractedData.tests ||
    extractedData.Tests ||
    extractedData.diagnosticTests ||
    extractedData.DiagnosticTests ||
    extractedData.imaging ||
    extractedData.Imaging ||
    extractedData.extensions?.tests ||
    []

  // 3. Diagnoses (Cluster by ICD-10 code or normalized description)
  const rawDiagnoses =
    extractedData.diagnosis ||
    extractedData.Diagnosis ||
    extractedData.diagnoses ||
    extractedData.Diagnoses ||
    extractedData.extensions?.diagnosis ||
    extractedData.extensions?.Diagnosis ||
    []
  const diagClusters = new Map()
  const reroutedDiagnosticTests = []
  let survivingDiagCount = 0

  rawDiagnoses.forEach((diag, idx) => {
    const diagText =
      typeof diag === 'string'
        ? diag
        : diag.name ||
          diag.Name ||
          diag.description ||
          diag.Description ||
          diag.code ||
          diag.Code ||
          diag.icd10 ||
          diag.Icd10 ||
          ''
    const diagCode = typeof diag === 'object' ? diag.code || diag.Code || diag.icd10 || diag.Icd10 : ''
    const anatomical =
      typeof diag === 'object' ? diag.anatomicalLevel || diag.AnatomicalLevel || diag.bodyPart || diag.BodyPart : null
    const displayValue =
      diagCode && diagText && !diagText.includes(diagCode) ? `${diagCode} - ${diagText}` : diagText || diagCode || ''

    const rawSnippet =
      (typeof diag === 'object'
        ? diag.sourceTextSnippet || diag.SourceTextSnippet || diag.sourceSnippet || diag.SourceSnippet
        : '') || ''

    const isNeg = isNegativeFindingText(diagText)
    const isPre = isPreExistingConditionText(diagText, diagCode)
    const isRad = isRadiologyImpressionText(diagText)

    // Discard accident narratives from clinical diagnoses (they belong in Injury Description)
    // Check both the diagnosis label AND the source snippet cited by Bedrock
    const allInjuryDescs = [
      ...extractedInjuryDescs,
      injury.sourceTextSnippet,
      injury.SourceTextSnippet,
      injury.sourceSnippet,
      injury.SourceSnippet,
    ].filter(Boolean)

    const preferredDiagDoc = findMatchingDocForItem(diag, docs)
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(rawSnippet, docs, [diagText, diagCode, anatomical], {
      preferredDoc: preferredDiagDoc,
    })
    const matchedDocName = matchedDoc?.fileName || matchedDoc?.documentName || matchedDoc?.name || ''
    const docIndex =
      docs.indexOf(matchedDoc) >= 0
        ? docs.indexOf(matchedDoc)
        : typeof diag === 'object' &&
          (diag.docIndex !== undefined ? diag.docIndex : diag.DocIndex !== undefined ? diag.DocIndex : 0)

    // First Report of Injury (FROI / WC 100) lay form field guard:
    // Employer/claimant statutory forms ask lay questions ("Tell us the part of the body that was affected",
    // "Tell us the nature of the injury/illness", "How the injury occurred"). These represent administrative
    // injury reporting, not physician clinical diagnoses. Suppress from diagnosis and enrich Injury Description instead.
    const isDocFroi =
      /(?:^|[^a-zA-Z0-9])(froi|first[\s_-]*report[\s_-]*of[\s_-]*(?:injury|illness|occupational|incident)|employer'?s?[\s_-]*first[\s_-]*report|wc[\s_-]*100|form[\s_-]*100)(?:$|[^a-zA-Z0-9])/i.test(
        matchedDocName || docs[0]?.fileName || '',
      ) ||
      /(?:^|[^a-zA-Z0-9])(froi|first[\s_-]*report[\s_-]*of[\s_-]*(?:injury|illness|occupational|incident)|employer'?s?[\s_-]*first[\s_-]*report|wc[\s_-]*100|form[\s_-]*100)(?:$|[^a-zA-Z0-9])/i.test(
        (matchedDoc?.textContent || rawText || '').slice(0, 1500),
      )
    const isFroiLayFieldSnippet =
      /\b(?:tell\s+us\s+)?(?:the\s+)?(?:part\s+of\s+(?:the\s+)?body|nature\s+of\s+(?:the\s+)?injury|nature\s+of\s+(?:the\s+)?illness|body\s+(?:part\s+)?affected|how\s+(?:the\s+)?injury\s+occurred|what\s+happened)\b/i.test(
        rawSnippet,
      )
    const isFroiLayFieldText =
      /\b(?:tell\s+us\s+)?(?:the\s+)?(?:part\s+of\s+(?:the\s+)?body|nature\s+of\s+(?:the\s+)?injury|nature\s+of\s+(?:the\s+)?illness|body\s+(?:part\s+)?affected|how\s+(?:the\s+)?injury\s+occurred)\b/i.test(
        diagText,
      )
    const hasPhysician = typeof diag === 'object' && Boolean(diag.physician || diag.Physician)
    const isFroiLayInjury = (isFroiLayFieldSnippet || isFroiLayFieldText || isDocFroi) && !diagCode && !hasPhysician

    if (isFroiLayInjury) {
      if (diagText) {
        const descEntity = entities.find(e => e.field === 'Injury Description')
        const cleanedDiag = toSentenceCase(diagText.trim()).replace(/\.+$/, '')
        if (descEntity) {
          const curVal = (descEntity.extractedValue || '').trim()
          if (!curVal.toLowerCase().includes(cleanedDiag.toLowerCase())) {
            const trimmedCur = curVal.replace(/\.+$/, '')
            const prefix = cleanedDiag.toLowerCase().startsWith('injury:') ? '' : 'Injury: '
            const enriched = trimmedCur ? `${trimmedCur}. ${prefix}${cleanedDiag}.` : `${prefix}${cleanedDiag}.`
            descEntity.extractedValue = enriched
            descEntity.attributes = { ...(descEntity.attributes || {}), description: enriched }
            if (rawSnippet && !descEntity.sourceSnippet?.includes(rawSnippet)) {
              descEntity.sourceSnippet = descEntity.sourceSnippet
                ? `${descEntity.sourceSnippet} | ${rawSnippet}`
                : rawSnippet
            }
          }
        } else {
          entities.push({
            id: `injury-desc-froi-${nextId++}`,
            category: 'referral',
            field: 'Injury Description',
            currentValue: existingInjury.descriptions?.[0]?.description || '',
            extractedValue: cleanedDiag,
            attributes: { description: cleanedDiag, provider: 'Report' },
            sourceSnippet: rawSnippet || diagText,
            documentId: matchedDoc?.id || defaultDocId,
            documentName: matchedDocName || defaultDocName,
            page: typeof diag === 'object' ? diag.page || diag.Page || loc?.page || 1 : loc?.page || 1,
            occurrenceIndex: 1,
            totalOccurrences: 1,
            status: 'pending',
          })
        }
        allInjuryDescs.push(diagText)
        extractedInjuryDescs.push(diagText)
      }
      return
    }

    const isGenericInjuryName =
      !diagCode &&
      /\b(?:left|right|bilateral)?\s*(?:knee|elbow|shoulder|back|ankle|wrist|hip|leg|arm|hand|foot|neck|head)\s+injury\b/i.test(
        diagText,
      )

    const isAccidentNarrative =
      isGenericInjuryName ||
      isAccidentNarrativeText(diagText, allInjuryDescs) ||
      isAccidentNarrativeText(rawSnippet, allInjuryDescs) ||
      (rawSnippet &&
        allInjuryDescs.some(d => {
          const cleanD = d.toLowerCase()
          const cleanRaw = rawSnippet.toLowerCase()
          if (cleanD.length >= 15 && (cleanD === cleanRaw || (cleanRaw.length >= 25 && cleanD.includes(cleanRaw)))) {
            return true
          }
          const ANATOMICAL_AND_COMMON = new Set([
            'left',
            'right',
            'bilateral',
            'knee',
            'elbow',
            'shoulder',
            'back',
            'ankle',
            'wrist',
            'hip',
            'leg',
            'arm',
            'hand',
            'foot',
            'neck',
            'head',
            'and',
            'the',
            'for',
            'with',
            'injury',
            'pain',
          ])
          const wordsD = cleanD
            .replace(/[^a-z0-9\s]/g, ' ')
            .split(/\s+/)
            .filter(w => w.length >= 3 && !ANATOMICAL_AND_COMMON.has(w))
          const wordsRaw = cleanRaw
            .replace(/[^a-z0-9\s]/g, ' ')
            .split(/\s+/)
            .filter(w => w.length >= 3 && !ANATOMICAL_AND_COMMON.has(w))
          if (wordsD.length >= 2 && wordsRaw.length >= 2) {
            const setD = new Set(wordsD)
            const overlap = wordsRaw.filter(w => setD.has(w)).length
            if (overlap / wordsRaw.length >= 0.5) return true
          }
          return false
        }))

    if (isAccidentNarrative) {
      return
    }

    const isMatchedDocDiagStudy =
      (docs.length === 1 && isDiagnosticStudyArchetype) ||
      matchedDoc?.documentSubtype === 'Diagnostic Study' ||
      matchedDoc?.documentSubtype === 'Electrodiagnostic Study'

    const hasExplicitImagingModality =
      /\b(?:x-?rays?|radiograph(?:y|s)?|plain\s+films?|views?\s+of|mri|ct\s+scan|cat\s+scan|ultrasound|sonogram|arthrogram|fluoroscop\w*|emg|ncs|imaging\s+study|roentgenogram)\b/i.test(
        diagText,
      )

    const isExplicitDiagnosisSection =
      /\b(?:diagnosis|diagnoses|assessment|impression\s*\/\s*plan|treatment\s+plan|clinical\s+impression|assessment\s*\/\s*plan)\b/i.test(
        rawSnippet,
      )

    const isClinicalPathologyCondition =
      /\b(?:rotator\s+cuff\s+tear|meniscal\s+tear|annular\s+tear|sprain|strain|fracture|contusion|radiculopath|bursitis|tendinit|tendinopath|impingement|syndrome)\b/i.test(
        diagText,
      )

    const isImpressionOrRadiologyFinding =
      hasExplicitImagingModality ||
      (!diagCode &&
        !isExplicitDiagnosisSection &&
        (isMatchedDocDiagStudy ||
          (isRad && !isClinicalPathologyCondition) ||
          (isRadiologyImpressionText(diagText) && !isClinicalPathologyCondition)))

    if (isImpressionOrRadiologyFinding) {
      reroutedDiagnosticTests.push({
        rawText: diagText,
        diagCode,
        anatomical,
        snippet: rawSnippet || diagText,
        docIndex,
        documentId: matchedDoc?.id || defaultDocId,
        documentName: matchedDocName || defaultDocName,
        sourceDocument: matchedDocName || defaultDocName,
        diagDate: typeof diag === 'object' && (diag.date || diag.Date) ? diag.date || diag.Date : null,
        diagPhysician:
          typeof diag === 'object' && (diag.physician || diag.Physician) ? diag.physician || diag.Physician : null,
        page: typeof diag === 'object' ? diag.page || diag.Page || loc?.page || 1 : loc?.page || 1,
        lineNumber: typeof diag === 'object' ? diag.lineNumber || diag.LineNumber || loc?.lineNumber : loc?.lineNumber,
      })
      return
    }

    const pathKey = getDiagnosisPathologyKey(diagText, diagCode)
    const clusterKey = isNeg ? `neg-${normalizeTextKey(diagText)}` : pathKey || `diag-${idx}`

    const docId =
      typeof diag === 'object' && (diag.documentId || diag.DocumentId)
        ? diag.documentId || diag.DocumentId
        : matchedDoc?.id || defaultDocId
    const docName =
      typeof diag === 'object' && (diag.documentName || diag.DocumentName || diag.sourceDocument || diag.SourceDocument)
        ? diag.documentName || diag.DocumentName || diag.sourceDocument || diag.SourceDocument
        : matchedDocName || defaultDocName

    const snippet = rawSnippet

    const existingDiags = existingCase.diagnosis || existingCase.Diagnosis || []
    const duplicate = existingDiags.find(d => {
      const existingName = (d.name || d.Name || d.description || d.Description || d || '').toLowerCase()
      const existingCode = (d.code || d.Code || d.icd10 || d.Icd10 || '').toLowerCase()
      return (
        (pathKey && existingName.includes(pathKey)) ||
        (diagCode && existingCode.includes(diagCode.toLowerCase())) ||
        (existingName && normalizeTextKey(existingName) === normalizeTextKey(diagText))
      )
    })

    let diagDate = null
    if (typeof diag === 'object' && (diag.date || diag.Date)) {
      const rawD = diag.date || diag.Date
      const m = Moment(rawD, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
      diagDate = m.isValid() ? m.format('YYYY-MM-DD') : null
    }

    const isExplicitPrimary =
      typeof diag === 'object' && (diag.category || diag.Category || '').toLowerCase() === 'primary'
    const isPrimary = isExplicitPrimary || survivingDiagCount === 0
    survivingDiagCount++

    const rawItem = {
      category: 'diagnosis',
      field: isPrimary ? 'Primary Diagnosis' : 'Secondary Diagnosis',
      currentValue: duplicate
        ? duplicate.name || duplicate.Name || duplicate.description || duplicate.Description || duplicate
        : '',
      extractedValue: displayValue,
      isNegativeFinding: isNeg,
      isPreExisting: isPre,
      isRadiologyImpression: isRad,
      attributes: {
        code: diagCode,
        anatomicalLevel: anatomical,
        category: isPre
          ? 'Pre-Existing / Incidental'
          : isPrimary
          ? 'Primary'
          : (typeof diag === 'object' && (diag.category || diag.Category)) || 'Secondary',
        date: diagDate,
        physician: typeof diag === 'object' ? diag.physician || diag.Physician : null,
      },
      duplicateMatch: duplicate
        ? {
            existingName:
              duplicate.name || duplicate.Name || duplicate.description || duplicate.Description || duplicate,
          }
        : null,
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: docId,
      documentName: docName,
      page: (typeof diag === 'object' && (diag.page || diag.Page)) || loc?.page,
      lineNumber: (typeof diag === 'object' && (diag.lineNumber || diag.LineNumber)) || loc?.lineNumber,
      status: 'pending',
    }

    if (!diagClusters.has(clusterKey)) {
      diagClusters.set(clusterKey, [])
    }
    diagClusters.get(clusterKey).push(rawItem)
  })

  diagClusters.forEach((clusterItems, clusterKey) => {
    const isClusterConsolidated = clusterItems.length > 1
    const canonical = pickBestCanonicalDiagnosis(clusterItems)
    const clusterLeaderId = `cluster-diag-${clusterKey}`

    clusterItems.forEach((item, occIdx) => {
      if (occIdx === 0) {
        entities.push({
          ...item,
          extractedValue: canonical ? canonical.extractedValue : item.extractedValue,
          id: `diag-${clusterKey}-0-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: 1,
          totalOccurrences: clusterItems.length,
          isConsolidated: isClusterConsolidated,
          clusterCount: clusterItems.length,
          occurrences: isClusterConsolidated ? clusterItems : undefined,
        })
      } else {
        entities.push({
          ...item,
          id: `diag-${clusterKey}-${occIdx}-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: occIdx + 1,
          totalOccurrences: clusterItems.length,
        })
      }
    })
  })

  // 4. Providers (Individual doctors, clinics, facilities, NPI)
  const provClusters = new Map()

  // Detect any organizations mentioned in document headers/text (e.g. "VALLEY REGIONAL IMAGING")
  const detectedDocOrgs = detectOrganizationsFromDocument(docs)
  const expandedRawProviders = [...rawProviders]

  if (!isSingleBusinessCardUpload) {
    detectedDocOrgs.forEach(org => {
      if (isCertificationOrCredential(org)) return
      const cleanTokens = s =>
        s
          .toLowerCase()
          .replace(/\b(?:county|memorial|regional|community|general|center|centre|hospital|clinic|facility)\b/g, '')
          .replace(/[^a-z0-9]/g, ' ')
          .trim()
          .split(/\s+/)
          .filter(Boolean)

      const orgTokens = cleanTokens(org)

      const alreadyPresent = expandedRawProviders.some(p => {
        const pName = p.name || p.Name || p.practiceName || p.PracticeName || ''
        if (!pName) return false
        if (pName.toLowerCase().includes(org.toLowerCase()) || org.toLowerCase().includes(pName.toLowerCase()))
          return true
        const pTokens = cleanTokens(pName)
        if (orgTokens.length > 0 && pTokens.length > 0) {
          if (orgTokens.every(t => pTokens.includes(t)) || pTokens.every(t => orgTokens.includes(t))) return true
        }
        return false
      })
      if (!alreadyPresent) {
        const nearbyAddrProv = expandedRawProviders.find(p => p.address || p.Address)
        expandedRawProviders.push({
          name: org,
          classification: 'Organization',
          providerType: 'Treating Facility',
          address: nearbyAddrProv?.address || nearbyAddrProv?.Address,
          documentId: defaultDocId,
          documentName: defaultDocName,
          sourceTextSnippet: org,
        })
      }
    })
  }

  expandedRawProviders.forEach((prov, idx) => {
    const provName =
      prov.name ||
      prov.Name ||
      [prov.firstName || prov.FirstName, prov.lastName || prov.LastName, prov.suffix || prov.Suffix]
        .filter(Boolean)
        .join(' ') ||
      prov.practiceName ||
      prov.PracticeName ||
      prov.organizationName ||
      prov.OrganizationName ||
      'Physician'

    const rawProvType = prov.providerType || prov.ProviderType || ''

    // Strict Guardrail: Insurance Carriers, Claims Administrators, TPAs, Billing Agents, and Employers are NOT healthcare treatment providers!
    const nonTreatmentPatterns =
      /\b(insurance|carrier|assurance|indemnity|casualty|underwriters?|claims?\s+admin|claims?\s+administrator|third\s+party\s+admin|tpa|mail\s+medical\s+bills|billing\s+agent|billing\s+service)\b/i
    if (
      nonTreatmentPatterns.test(rawProvType) ||
      nonTreatmentPatterns.test(provName) ||
      nonTreatmentPatterns.test(prov.practiceName || prov.PracticeName || '')
    ) {
      return
    }

    // Strict Guardrail: Professional certifications, board credentials, degrees, staff titles, and accreditation bodies are NOT providers or facilities!
    if (
      isCertificationOrCredential(provName) ||
      isCertificationOrCredential(prov.practiceName || prov.PracticeName || '') ||
      isCertificationOrCredential(prov.organizationName || prov.OrganizationName || '')
    ) {
      return
    }

    if (
      empName &&
      (provName.toLowerCase().includes(String(empName).toLowerCase().trim()) ||
        String(empName).toLowerCase().trim().includes(provName.toLowerCase()))
    ) {
      return
    }

    const addr = prov.address || prov.Address || {}
    const npiVal = prov.npi || prov.Npi
    const npi = npiVal ? String(npiVal).trim() : ''
    const normalizedName = normalizeTextKey(provName)
    const clusterKey = getProviderClusterKey(provName, npi) || normalizedName || `prov-${idx}`

    const preferredProvDoc = findMatchingDocForItem(prov, docs)
    const snippet = prov.sourceTextSnippet || prov.SourceTextSnippet || prov.sourceSnippet || prov.SourceSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(
      snippet,
      docs,
      [
        provName,
        provName.replace(/\b(?:county\s+)?(?:memorial\s+)?hospital\b/i, '').trim(),
        prov.practiceName || prov.PracticeName,
        prov.lastName || prov.LastName,
        'treating physician',
        'treating hospital',
        'medical provider',
        npi,
      ],
      { preferredDoc: preferredProvDoc },
    )
    const docId = prov.documentId || prov.DocumentId || matchedDoc?.id || defaultDocId
    const docName =
      prov.documentName ||
      prov.DocumentName ||
      prov.sourceDocument ||
      prov.SourceDocument ||
      matchedDoc?.fileName ||
      defaultDocName

    const existingProviders = [
      ...(existingCase.treatmentProviders || existingCase.TreatmentProviders || []),
      ...(existingCase.providers || existingCase.Providers || []),
      ...(existingCase.caseProviders || []),
    ]

    const duplicate = existingProviders.find(p => {
      const existingName = (
        p.name ||
        p.Name ||
        [p.firstName || p.FirstName, p.lastName || p.LastName].filter(Boolean).join(' ') ||
        p.companyName ||
        p.CompanyName ||
        p.organizationName ||
        p.OrganizationName ||
        ''
      ).toLowerCase()
      const existingNpi = String(p.npi || p.Npi || '').trim()
      return (
        (npi && existingNpi === npi) ||
        (normalizedName && existingName.includes(normalizedName)) ||
        (normalizedName && normalizedName.includes(normalizeTextKey(existingName))) ||
        (clusterKey && getProviderClusterKey(existingName, existingNpi) === clusterKey)
      )
    })

    const backendMatchType = prov.matchType || prov.MatchType
    const backendMatchedName = prov.matchedProviderName || prov.MatchedProviderName
    const backendMatchedId = prov.matchedProviderId || prov.MatchedProviderId
    const backendMatchReason = prov.matchReason || prov.MatchReason
    const backendMatchConfidence = prov.matchConfidence || prov.MatchConfidence

    let matchType = 'new'
    let matchedName = null
    let matchedId = null
    let matchReason = null
    let matchConfidence = 0

    if (duplicate) {
      matchType = 'case'
      matchedName =
        duplicate.name ||
        duplicate.Name ||
        [duplicate.firstName || duplicate.FirstName, duplicate.lastName || duplicate.LastName]
          .filter(Boolean)
          .join(' ') ||
        duplicate.companyName ||
        duplicate.practiceName ||
        duplicate.organizationName
      matchedId = duplicate.id || duplicate.Id || duplicate.providerId || duplicate.ProviderId
      matchReason = 'Matches provider already on this case'
      matchConfidence = 1.0
    } else if (backendMatchType === 'case') {
      matchType = 'case'
      matchedName = backendMatchedName
      matchedId = backendMatchedId
      matchReason = backendMatchReason || 'Matches provider already on this case'
      matchConfidence = backendMatchConfidence || 0.95
    } else if (backendMatchType === 'library') {
      matchType = 'library'
      matchedName = backendMatchedName
      matchedId = backendMatchedId
      matchReason = backendMatchReason || 'Matches provider in library'
      matchConfidence = backendMatchConfidence || 0.9
    } else {
      const libraryProviders = [
        ...(options?.libraryProviders || []),
        ...(existingCase.libraryProviders || existingCase.LibraryProviders || []),
      ]
      const libMatch = libraryProviders.find(p => {
        const existingName = (
          p.name ||
          p.Name ||
          [p.firstName || p.FirstName, p.lastName || p.LastName].filter(Boolean).join(' ') ||
          p.companyName ||
          p.CompanyName ||
          p.organizationName ||
          p.OrganizationName ||
          ''
        ).toLowerCase()
        const existingNpi = String(p.npi || p.Npi || '').trim()
        return (
          (npi && existingNpi === npi) ||
          (normalizedName && existingName.includes(normalizedName)) ||
          (normalizedName && normalizedName.includes(normalizeTextKey(existingName))) ||
          (clusterKey && getProviderClusterKey(existingName, existingNpi) === clusterKey)
        )
      })
      if (libMatch) {
        matchType = 'library'
        matchedName =
          libMatch.name ||
          libMatch.Name ||
          [libMatch.firstName || libMatch.FirstName, libMatch.lastName || libMatch.LastName]
            .filter(Boolean)
            .join(' ') ||
          libMatch.companyName ||
          libMatch.practiceName ||
          libMatch.organizationName
        matchedId = libMatch.id || libMatch.Id || libMatch.providerId || libMatch.ProviderId
        matchReason = 'Matches provider in library'
        matchConfidence = 0.95
      }
    }

    const classification = prov.classification || prov.Classification || detectProviderClassification(provName)
    const provType =
      (rawProvType && !/^(organization|individual|provider)$/i.test(rawProvType) ? rawProvType : null) ||
      (classification === 'Organization' ? 'Treating Facility' : 'Treating Provider')

    let practiceName =
      prov.practiceName ||
      prov.PracticeName ||
      prov.organizationName ||
      prov.OrganizationName ||
      (classification === 'Organization' ? provName : '')
    if (isCertificationOrCredential(practiceName)) {
      practiceName = ''
    }
    if (!practiceName && classification === 'Individual') {
      const provPage = prov.page || prov.Page
      // 1. Search for an Organization provider on the same page or within 3 pages
      const orgProvOnPage = expandedRawProviders.find(p => {
        const pClass = p.classification || p.Classification || detectProviderClassification(p.name || p.Name || '')
        const pPage = p.page || p.Page
        const candidateName = p.name || p.Name || ''
        return (
          pClass === 'Organization' &&
          candidateName !== provName &&
          !isCertificationOrCredential(candidateName) &&
          (!provPage || !pPage || Math.abs(pPage - provPage) <= 3)
        )
      })
      if (orgProvOnPage) {
        practiceName = orgProvOnPage.name || orgProvOnPage.Name || orgProvOnPage.practiceName
      }
      // 2. Check if a letterhead organization was detected on this page in lineBlocks
      const targetBlocks = matchedDoc?.lineBlocks || docs[0]?.lineBlocks
      if (!practiceName && provPage && Array.isArray(targetBlocks)) {
        const pageHeaderBlocks = targetBlocks.filter(
          b => (b.page === provPage || !b.page) && (b.top === undefined || b.top < 0.25),
        )
        if (pageHeaderBlocks.length > 0) {
          const detectedOnPage = detectOrganizationsFromDocument([{ lineBlocks: pageHeaderBlocks }])
          if (detectedOnPage.length > 0 && !isCertificationOrCredential(detectedOnPage[0])) {
            practiceName = detectedOnPage[0]
          }
        }
      }
      // 3. Fallback to first detected document org
      if (!practiceName && detectedDocOrgs.length > 0) {
        const validDocOrg = detectedDocOrgs.find(org => !isCertificationOrCredential(org))
        if (validDocOrg) practiceName = validDocOrg
      }
      if (isCertificationOrCredential(practiceName)) {
        practiceName = ''
      }
    }

    let initialExtractedName = provName
    if (matchType === 'case' && matchedName && isMoreSpecificProviderName(matchedName, provName)) {
      initialExtractedName = matchedName
    }

    const rawItem = {
      category: 'provider',
      field: provType,
      currentValue: matchType === 'case' && matchedName ? matchedName : '',
      extractedValue: initialExtractedName,
      isNpiVerified: !!(prov.isNpiVerified || prov.IsNpiVerified),
      providerMatch: {
        type: matchType,
        matchedName,
        matchedId,
        reason: matchReason,
        confidence: matchConfidence,
      },
      duplicateMatch:
        matchType === 'case' || duplicate
          ? {
              existingName: matchedName || duplicate?.name || duplicate?.Name || duplicate?.practiceName,
              existingId: matchedId,
              confidence: matchConfidence,
            }
          : null,
      attributes: {
        npi,
        classification,
        isSoleProprietor: false,
        specialty:
          prov.specialty ||
          prov.Specialty ||
          (classification === 'Organization' ? 'Treating Facility' : 'Treatment Provider'),
        practiceName,
        phone: prov.phone || prov.Phone,
        fax: prov.fax || prov.Fax,
        email: prov.email || prov.Email,
        website: prov.website || prov.Website,
        line1: addr.line1 || addr.Line1 || addr.address1 || addr.Address1,
        line2: addr.line2 || addr.Line2 || addr.address2 || addr.Address2,
        city: addr.city || addr.City,
        state: addr.state || addr.State,
        zip: addr.zip || addr.Zip || addr.postalCode || addr.PostalCode,
      },
      sourceSnippet: snippet || loc?.matchedSnippet || provName,
      documentId: docId,
      documentName: docName,
      page: loc?.page && loc.page > 1 ? loc.page : prov.page || prov.Page || loc?.page || 1,
      lineNumber: prov.lineNumber || prov.LineNumber || loc?.lineNumber,
      status: 'pending',
    }

    if (!provClusters.has(clusterKey)) {
      provClusters.set(clusterKey, [])
    }
    provClusters.get(clusterKey).push(rawItem)
  })

  const workStatusReportProviders = []

  provClusters.forEach((clusterItems, clusterKey) => {
    const isClusterConsolidated = clusterItems.length > 1
    const canonical = pickBestCanonicalProvider(clusterItems)

    const bestCaseMatch = clusterItems.find(i => i.providerMatch?.type === 'case')?.providerMatch
    const bestLibraryMatch = clusterItems.find(i => i.providerMatch?.type === 'library')?.providerMatch
    const bestMatch = bestCaseMatch || bestLibraryMatch
    const leader = clusterItems[0]

    let effectiveExtractedValue = canonical ? canonical.extractedValue : leader.extractedValue
    const mergedMatch = bestMatch || leader.providerMatch
    if (mergedMatch?.type === 'case' && mergedMatch.matchedName) {
      if (isMoreSpecificProviderName(mergedMatch.matchedName, effectiveExtractedValue)) {
        effectiveExtractedValue = mergedMatch.matchedName
      }
    }

    const leaderDoc = docs.find(d => d.id === leader.documentId || d.fileName === leader.documentName)
    if (isSingleWorkStatusReportUpload || isDocWorkStatusReport(leaderDoc)) {
      workStatusReportProviders.push({
        ...leader,
        extractedValue: effectiveExtractedValue,
        providerMatch: mergedMatch,
      })
      return
    }

    const clusterLeaderId = `cluster-prov-${clusterKey}`

    clusterItems.forEach((item, occIdx) => {
      if (occIdx === 0) {
        entities.push({
          ...leader,
          extractedValue: effectiveExtractedValue,
          providerMatch: mergedMatch,
          duplicateMatch:
            mergedMatch?.type === 'case'
              ? { existingName: mergedMatch.matchedName, existingId: mergedMatch.matchedId }
              : leader.duplicateMatch,
          currentValue:
            mergedMatch?.type === 'case' && mergedMatch.matchedName ? mergedMatch.matchedName : leader.currentValue,
          id: `prov-${clusterKey}-0-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: 1,
          totalOccurrences: clusterItems.length,
          isConsolidated: isClusterConsolidated,
          clusterCount: clusterItems.length,
          occurrences: isClusterConsolidated ? clusterItems : undefined,
        })
      } else {
        entities.push({
          ...item,
          id: `prov-${clusterKey}-${occIdx}-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: occIdx + 1,
          totalOccurrences: clusterItems.length,
        })
      }
    })
  })

  // 5. Diagnostic Tests & Imaging (MRI, X-Ray, CT, EMG, etc.)

  const formatStudyResultsBlock = (impressions = [], findings = [], otherResults = []) => {
    const sections = []

    if (impressions.length > 0) {
      const impLines = impressions.map((imp, idx) => {
        const clean = imp.trim()
        if (/^(?:\d+\.|\*|-|•)\s*/.test(clean)) return clean
        return impressions.length > 1 ? `${idx + 1}. ${clean}` : clean
      })
      sections.push(`IMPRESSION:\n${impLines.join('\n')}`)
    }

    if (findings.length > 0) {
      const fLines = findings.map(f => {
        const clean = f.trim()
        if (/^(?:\d+\.|\*|-|•)\s*/.test(clean)) return clean
        return `- ${clean}`
      })
      sections.push(`FINDINGS:\n${fLines.join('\n')}`)
    }

    if (otherResults.length > 0) {
      const remaining = otherResults.filter(r => {
        const rLower = (r || '').toLowerCase().trim()
        if (!rLower) return false
        return (
          !impressions.some(imp => imp.toLowerCase().includes(rLower) || rLower.includes(imp.toLowerCase())) &&
          !findings.some(f => f.toLowerCase().includes(rLower) || rLower.includes(f.toLowerCase()))
        )
      })
      if (remaining.length > 0) {
        sections.push(remaining.join('\n'))
      }
    }

    return sections.join('\n\n')
  }

  const handledRawTestIndices = new Set()
  const handledReroutedIndices = new Set()

  // Consolidate diagnostic study documents into a single test entry per study
  docs.forEach((doc, dIdx) => {
    if (!isDocDiagnosticStudy(doc, dIdx)) return

    const docContent = doc.textContent || (dIdx === 0 ? rawText : '') || ''
    const docFindings = extractFindingsFromDocument(docContent)

    const docRerouted = []
    reroutedDiagnosticTests.forEach((re, rIdx) => {
      const itemDoc = findMatchingDocForItem(re, docs)
      const targetDocIdx = itemDoc ? docs.indexOf(itemDoc) : re.docIndex !== undefined ? re.docIndex : 0
      if (targetDocIdx === dIdx || (docs.length === 1 && targetDocIdx === 0)) {
        docRerouted.push(re)
        handledReroutedIndices.add(rIdx)
      }
    })

    const docRaw = []
    rawTests.forEach((t, tIdx) => {
      const itemDoc = findMatchingDocForItem(t, docs)
      const targetDocIdx = itemDoc
        ? docs.indexOf(itemDoc)
        : t.docIndex !== undefined
        ? t.docIndex
        : docs.length === 1
        ? 0
        : -1
      if (targetDocIdx === dIdx || (docs.length === 1 && targetDocIdx === 0)) {
        docRaw.push(t)
        handledRawTestIndices.add(tIdx)
      }
    })

    if (docRerouted.length === 0 && docFindings.length === 0 && docRaw.length === 0) return

    const impressions = []
    docRerouted.forEach(r => {
      if (r.rawText && !impressions.includes(r.rawText.trim())) impressions.push(r.rawText.trim())
    })
    docRaw.forEach(t => {
      const imp = t.impression || t.Impression
      if (imp && !impressions.includes(imp.trim())) impressions.push(imp.trim())
    })

    const findings = []
    docFindings.forEach(f => {
      if (!findings.includes(f.trim())) findings.push(f.trim())
    })
    docRaw.forEach(t => {
      const f = t.findings || t.Findings
      if (f && !findings.includes(f.trim())) findings.push(f.trim())
    })

    const otherResults = []
    docRaw.forEach(t => {
      const res = t.results || t.Results
      if (res && !impressions.includes(res.trim()) && !findings.includes(res.trim())) {
        otherResults.push(res.trim())
      }
    })

    const combinedTextForModality = [
      doc.fileName,
      ...impressions,
      ...findings,
      ...otherResults,
      docRaw.map(t => t.type || t.modality || '').join(' '),
    ].join(' ')

    const rawModalityCandidate =
      docRaw.find(t => t.type || t.modality)?.type || docRaw.find(t => t.type || t.modality)?.modality || ''
    const modality = inferDiagnosticTestType(rawModalityCandidate, combinedTextForModality, '', '', docContent)

    const examDateMatch = docContent.match(
      /\b(?:date\s+of\s+(?:exam|examination|study)|exam\s+date|study\s+date)\s*[:-]?\s*(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{2}-\d{2})\b/i,
    )
    const rawDate =
      docRerouted.find(r => r.diagDate)?.diagDate ||
      docRaw.find(t => t.date)?.date ||
      (examDateMatch ? examDateMatch[1] : '')
    const date = formatProposedDate(rawDate)

    const consolidatedResults = formatStudyResultsBlock(impressions, findings, otherResults)
    const bodyPart =
      docRerouted.find(r => r.anatomical)?.anatomical ||
      docRaw.find(t => t.bodyPart || t.BodyPart)?.bodyPart ||
      docRaw.find(t => t.bodyPart || t.BodyPart)?.BodyPart ||
      ''

    const primarySnippet =
      docRerouted[0]?.snippet ||
      docFindings[0] ||
      docRaw[0]?.sourceTextSnippet ||
      docRaw[0]?.sourceSnippet ||
      consolidatedResults.slice(0, 150)
    const loc = locateSnippetInDoc(primarySnippet, doc, [modality, bodyPart, impressions[0] || findings[0] || ''])

    entities.push({
      id: `test-study-${doc.id || dIdx + 1}-${nextId++}`,
      category: 'test',
      field: modality,
      currentValue: '',
      extractedValue: consolidatedResults,
      originalExtractedValue: consolidatedResults,
      attributes: {
        modality,
        type: modality,
        testType: modality,
        bodyPart,
        results: consolidatedResults,
        date,
      },
      sourceSnippet: primarySnippet || loc?.matchedSnippet || '',
      documentId: doc.id || defaultDocId,
      documentName: doc.fileName || defaultDocName,
      page: docRerouted[0]?.page || docRaw[0]?.page || loc?.page || 1,
      lineNumber: docRerouted[0]?.lineNumber || docRaw[0]?.lineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  })

  // Process any remaining individual raw tests not part of a consolidated study
  const candidateTests = []

  rawTests.forEach((test, idx) => {
    if (handledRawTestIndices.has(idx)) return

    const rawModality =
      test.type ||
      test.Type ||
      test.modality ||
      test.Modality ||
      test.testType ||
      test.TestType ||
      test.name ||
      test.Name ||
      ''
    const bodyPart = test.bodyPart || test.BodyPart || test.anatomy || test.Anatomy || ''
    const results =
      test.results || test.Results || test.impression || test.Impression || test.findings || test.Findings || ''
    const date = formatProposedDate(test.date || test.Date || '')
    const facility = test.facility || test.Facility || ''
    const snippet = test.sourceTextSnippet || test.SourceTextSnippet || test.sourceSnippet || test.SourceSnippet || ''
    const preferredDoc = findMatchingDocForItem(test, docs)
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [rawModality, bodyPart, results], {
      preferredDoc,
    })

    const modality = inferDiagnosticTestType(
      rawModality,
      snippet,
      results,
      bodyPart,
      matchedDoc?.textContent || rawText,
    )
    const shouldIncludeBodyPart =
      bodyPart &&
      !modality.toLowerCase().includes(bodyPart.toLowerCase()) &&
      !String(results).toLowerCase().includes(bodyPart.toLowerCase())
    const headerPart = [modality, shouldIncludeBodyPart ? `(${bodyPart})` : ''].filter(Boolean).join(' ')
    const displayVal = results ? (headerPart ? `${headerPart}: ${results}` : results) : headerPart

    candidateTests.push({
      id: `test-${idx + 1}-${nextId++}`,
      category: 'test',
      field: modality,
      currentValue: '',
      extractedValue: displayVal,
      originalExtractedValue: displayVal,
      attributes: {
        modality,
        type: modality,
        testType: modality,
        bodyPart,
        results,
        date,
        facility,
      },
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: test.documentId || test.DocumentId || matchedDoc?.id || defaultDocId,
      documentName:
        test.documentName ||
        test.DocumentName ||
        test.sourceDocument ||
        test.SourceDocument ||
        matchedDoc?.fileName ||
        defaultDocName,
      page: test.page || test.Page || loc?.page,
      lineNumber: test.lineNumber || test.LineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  })

  // Process any remaining individual rerouted diagnostic tests not part of a consolidated study
  reroutedDiagnosticTests.forEach((reDiag, rIdx) => {
    if (handledReroutedIndices.has(rIdx)) return

    const preferredDoc = findMatchingDocForItem(reDiag, docs)
    const cleanResults = reDiag.rawText
    const snippet = reDiag.snippet || cleanResults
    const preferredDocContent = preferredDoc?.textContent || rawText || ''
    const modality = inferDiagnosticTestType('', snippet, cleanResults, reDiag.anatomical, preferredDocContent)
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(
      snippet,
      docs,
      [cleanResults.slice(0, 40), cleanResults, reDiag.anatomical, modality],
      { preferredDoc },
    )
    const shouldIncludeBodyPart =
      reDiag.anatomical &&
      !modality.toLowerCase().includes(reDiag.anatomical.toLowerCase()) &&
      !cleanResults.toLowerCase().includes(reDiag.anatomical.toLowerCase())
    const headerPart = [modality, shouldIncludeBodyPart ? `(${reDiag.anatomical})` : ''].filter(Boolean).join(' ')
    const displayVal = headerPart ? `${headerPart}: ${cleanResults}` : cleanResults
    const date = formatProposedDate(reDiag.diagDate || '')

    candidateTests.push({
      id: `test-rerouted-${rIdx + 1}-${nextId++}`,
      category: 'test',
      field: modality,
      currentValue: '',
      extractedValue: displayVal,
      originalExtractedValue: displayVal,
      attributes: {
        modality,
        type: modality,
        testType: modality,
        bodyPart: reDiag.anatomical || '',
        results: cleanResults,
        date,
        physician: reDiag.diagPhysician || null,
        code: reDiag.diagCode || null,
      },
      sourceSnippet: snippet || loc?.matchedSnippet || cleanResults,
      documentId: reDiag.documentId || matchedDoc?.id || defaultDocId,
      documentName: reDiag.documentName || reDiag.sourceDocument || matchedDoc?.fileName || defaultDocName,
      page: reDiag.page || loc?.page || 1,
      lineNumber: reDiag.lineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  })

  const testClusters = new Map()
  candidateTests.forEach((testItem, cIdx) => {
    let matchedKey = null
    for (const [key, clusterItems] of testClusters.entries()) {
      if (areDiagnosticTestsMatching(clusterItems[0], testItem)) {
        matchedKey = key
        break
      }
    }
    const clusterKey = matchedKey || `test-cluster-${cIdx}`
    if (!testClusters.has(clusterKey)) {
      testClusters.set(clusterKey, [])
    }
    testClusters.get(clusterKey).push(testItem)
  })

  testClusters.forEach((clusterItems, clusterKey) => {
    const isClusterConsolidated = clusterItems.length > 1
    const canonical = pickBestCanonicalDiagnosticTest(clusterItems)
    const clusterLeaderId = `cluster-${clusterKey}`

    const canonModality = canonical.attributes?.modality || canonical.field || ''
    const canonBodyPart = canonical.attributes?.bodyPart || ''
    const canonResults = canonical.attributes?.results || ''
    const canonIncludeBp =
      canonBodyPart &&
      !canonModality.toLowerCase().includes(canonBodyPart.toLowerCase()) &&
      !String(canonResults).toLowerCase().includes(canonBodyPart.toLowerCase())
    const canonHeader = [canonModality, canonIncludeBp ? `(${canonBodyPart})` : ''].filter(Boolean).join(' ')
    const canonDisplayVal = canonResults
      ? canonHeader
        ? `${canonHeader}: ${canonResults}`
        : canonResults
      : canonHeader

    clusterItems.forEach((item, occIdx) => {
      if (occIdx === 0) {
        entities.push({
          ...item,
          field: canonModality,
          extractedValue: canonDisplayVal,
          originalExtractedValue: canonDisplayVal,
          attributes: {
            ...item.attributes,
            ...canonical.attributes,
            modality: canonModality,
            type: canonModality,
            testType: canonModality,
            bodyPart: canonBodyPart,
            results: canonResults,
          },
          entityClusterId: clusterLeaderId,
          occurrenceIndex: 1,
          totalOccurrences: clusterItems.length,
          isConsolidated: isClusterConsolidated,
          clusterCount: clusterItems.length,
          occurrences: isClusterConsolidated ? clusterItems : undefined,
        })
      } else {
        entities.push({
          ...item,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: occIdx + 1,
          totalOccurrences: clusterItems.length,
        })
      }
    })
  })

  // 6. Medications (Cluster by normalized drug name)
  const rawMeds = extractedData.medications || extractedData.Medications || extractedData.extensions?.medications || []
  const medClusters = new Map()

  rawMeds.forEach((med, idx) => {
    const medName = typeof med === 'string' ? med : med.name || med.Name || ''
    if (!medName) return
    const dosage = typeof med === 'object' ? med.dosage || med.Dosage : null
    const freq = typeof med === 'object' ? med.frequency || med.Frequency : null
    const medStatus = typeof med === 'object' ? med.status || med.Status : null
    const isUnknownStatus = medStatus && /unknown|unconfirmed/i.test(String(medStatus))
    const statusLabel = isUnknownStatus ? '(Status: Unknown)' : ''
    const displayVal = [medName, dosage, freq, statusLabel].filter(Boolean).join(' ')
    const clusterKey = normalizeTextKey(medName) || `med-${idx}`

    const preferredMedDoc = findMatchingDocForItem(med, docs)
    const snippet =
      (typeof med === 'object'
        ? med.sourceTextSnippet || med.SourceTextSnippet || med.sourceSnippet || med.SourceSnippet
        : '') || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [medName, dosage], {
      preferredDoc: preferredMedDoc,
    })
    const docId =
      typeof med === 'object' && (med.documentId || med.DocumentId)
        ? med.documentId || med.DocumentId
        : matchedDoc?.id || defaultDocId
    const docName =
      typeof med === 'object' && (med.documentName || med.DocumentName || med.sourceDocument || med.SourceDocument)
        ? med.documentName || med.DocumentName || med.sourceDocument || med.SourceDocument
        : matchedDoc?.fileName || defaultDocName

    const existingMeds = existingCase.medications || existingCase.Medications || []
    const duplicate = existingMeds.find(m => (m.name || m.Name || m || '').toLowerCase() === medName.toLowerCase())

    const rawItem = {
      category: 'medication',
      field: 'Medication',
      currentValue: duplicate ? duplicate.name || duplicate.Name || duplicate : '',
      extractedValue: displayVal,
      attributes: {
        name: medName,
        dosage,
        frequency: freq,
        status: isUnknownStatus ? 'Unknown' : medStatus || 'Taking',
        relatedToInjury: Boolean(typeof med === 'object' && med.relatedToInjury),
        preInjury: Boolean(typeof med === 'object' && (med.preInjury || med.isPreInjury)),
        isPreInjury: Boolean(typeof med === 'object' && (med.preInjury || med.isPreInjury)),
        discontinued: Boolean(
          typeof med === 'object' && (med.discontinued || med.isDiscontinued || med.dateDiscontinued),
        ),
        isDiscontinued: Boolean(
          typeof med === 'object' && (med.discontinued || med.isDiscontinued || med.dateDiscontinued),
        ),
      },
      duplicateMatch: duplicate ? { existingName: duplicate.name || duplicate.Name || duplicate } : null,
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: docId,
      documentName: docName,
      page: (typeof med === 'object' && (med.page || med.Page)) || loc?.page,
      lineNumber: (typeof med === 'object' && (med.lineNumber || med.LineNumber)) || loc?.lineNumber,
      status: 'pending',
    }

    if (!medClusters.has(clusterKey)) {
      medClusters.set(clusterKey, [])
    }
    medClusters.get(clusterKey).push(rawItem)
  })

  medClusters.forEach((clusterItems, clusterKey) => {
    const isClusterConsolidated = clusterItems.length > 1
    const clusterLeaderId = `cluster-med-${clusterKey}`

    clusterItems.forEach((item, occIdx) => {
      if (occIdx === 0) {
        entities.push({
          ...item,
          id: `med-${clusterKey}-0-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: 1,
          totalOccurrences: clusterItems.length,
          isConsolidated: isClusterConsolidated,
          clusterCount: clusterItems.length,
          occurrences: isClusterConsolidated ? clusterItems : undefined,
        })
      } else {
        entities.push({
          ...item,
          id: `med-${clusterKey}-${occIdx}-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: occIdx + 1,
          totalOccurrences: clusterItems.length,
        })
      }
    })
  })

  // 7. Allergies (Cluster by allergen name)
  const rawAllergies = extractedData.allergies || extractedData.Allergies || extractedData.extensions?.allergies || []
  const algClusters = new Map()

  const isNkda = name => {
    const n = (name || '').toLowerCase().trim()
    return (
      n === 'nkda' ||
      n === 'nka' ||
      n === 'no known drug allergies' ||
      n === 'no known drug allergy' ||
      n === 'no known allergies' ||
      n === 'no allergies' ||
      n === 'none known' ||
      n === 'none'
    )
  }

  const existingAllergies = existingCase.allergies || existingCase.Allergies || []
  const hasSpecificExistingAllergy = existingAllergies.some(a => !isNkda(a.name || a.Name || a))
  const hasSpecificExtractedAllergy = rawAllergies.some(a => {
    const name = typeof a === 'string' ? a : a.name || a.Name || ''
    return name && !isNkda(name)
  })

  rawAllergies.forEach((alg, idx) => {
    const algName = typeof alg === 'string' ? alg : alg.name || alg.Name || ''
    if (!algName) return

    // Suppress "No Known Drug Allergies" / "NKDA" if specific drug allergies exist in either extracted or existing case allergies
    if (isNkda(algName) && (hasSpecificExistingAllergy || hasSpecificExtractedAllergy)) {
      return
    }
    const reaction = typeof alg === 'object' ? alg.reaction || alg.Reaction : null
    const displayVal = reaction ? `${algName} (${reaction})` : algName
    const clusterKey = normalizeTextKey(algName) || `alg-${idx}`

    const preferredAlgDoc = findMatchingDocForItem(alg, docs)
    const snippet =
      (typeof alg === 'object'
        ? alg.sourceTextSnippet || alg.SourceTextSnippet || alg.sourceSnippet || alg.SourceSnippet
        : '') || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [algName, reaction], {
      preferredDoc: preferredAlgDoc,
    })
    const docId =
      typeof alg === 'object' && (alg.documentId || alg.DocumentId)
        ? alg.documentId || alg.DocumentId
        : matchedDoc?.id || defaultDocId
    const docName =
      typeof alg === 'object' && (alg.documentName || alg.DocumentName || alg.sourceDocument || alg.SourceDocument)
        ? alg.documentName || alg.DocumentName || alg.sourceDocument || alg.SourceDocument
        : matchedDoc?.fileName || defaultDocName

    const existingAllergies = existingCase.allergies || existingCase.Allergies || []
    const duplicate = existingAllergies.find(a => (a.name || a.Name || a || '').toLowerCase() === algName.toLowerCase())

    const rawItem = {
      category: 'allergy',
      field: 'Allergy',
      currentValue: duplicate ? duplicate.name || duplicate.Name || duplicate : '',
      extractedValue: displayVal,
      attributes: {
        name: algName,
        reaction,
      },
      duplicateMatch: duplicate ? { existingName: duplicate.name || duplicate.Name || duplicate } : null,
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: docId,
      documentName: docName,
      page: (typeof alg === 'object' && (alg.page || alg.Page)) || loc?.page,
      lineNumber: (typeof alg === 'object' && (alg.lineNumber || alg.LineNumber)) || loc?.lineNumber,
      status: 'pending',
    }

    if (!algClusters.has(clusterKey)) {
      algClusters.set(clusterKey, [])
    }
    algClusters.get(clusterKey).push(rawItem)
  })

  algClusters.forEach((clusterItems, clusterKey) => {
    const isClusterConsolidated = clusterItems.length > 1
    const clusterLeaderId = `cluster-alg-${clusterKey}`

    clusterItems.forEach((item, occIdx) => {
      if (occIdx === 0) {
        entities.push({
          ...item,
          id: `alg-${clusterKey}-0-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: 1,
          totalOccurrences: clusterItems.length,
          isConsolidated: isClusterConsolidated,
          clusterCount: clusterItems.length,
          occurrences: isClusterConsolidated ? clusterItems : undefined,
        })
      } else {
        entities.push({
          ...item,
          id: `alg-${clusterKey}-${occIdx}-${nextId++}`,
          entityClusterId: clusterLeaderId,
          occurrenceIndex: occIdx + 1,
          totalOccurrences: clusterItems.length,
        })
      }
    })
  })

  // 8. Appointments & Referrals
  let workStatusReportApptDate = ''
  let workStatusReportApptProvider = ''
  const rawAppts =
    extractedData.appointments ||
    extractedData.Appointments ||
    extractedData.followUpAppointments ||
    extractedData.FollowUpAppointments ||
    extractedData.extensions?.appointments ||
    extractedData.extensions?.Appointments ||
    []
  rawAppts.forEach((appt, idx) => {
    const prov = appt.provider || appt.Provider || appt.doctor || appt.Doctor || ''
    const date = formatProposedDate(appt.date || appt.Date || '')
    const facility = appt.facility || appt.Facility || appt.location || appt.Location || ''
    const purpose =
      appt.title ||
      appt.Title ||
      appt.description ||
      appt.Description ||
      appt.purpose ||
      appt.Purpose ||
      appt.specialty ||
      appt.Specialty ||
      (appt.complete || appt.Complete ? 'Completed Visit' : 'Follow-up / Referral')

    let snippet = appt.sourceTextSnippet || appt.SourceTextSnippet || appt.sourceSnippet || appt.SourceSnippet || ''
    if (snippet) {
      snippet = snippet
        .replace(/;?\s*(?:date|time)\s+of\s+injury\s*[:-]?\s*[^;]*/gi, '')
        .replace(/;?\s*injury\s+site\s+address\s*[:-]?\s*[^;]*/gi, '')
        .replace(/;?\s*(?:employer|carrier|insurer|tpa)\s+(?:name|address)\s*[:-]?\s*[^;]*/gi, '')
        .trim()
    }

    // Universal Clinical Substance Guard:
    // An appointment candidate must represent an actionable clinical encounter (evaluation, consultation, therapy, visit)
    // with an identifiable provider/doctor or facility, OR an explicit scheduled clinical encounter date.
    // Generic administrative survey responses, questionnaire checkboxes, or historical triage notations
    // (e.g. "Initial treatment (check one)", "Type of medical care: Emergency room / Minor on-site", "First aid given: Yes")
    // without a healthcare provider or facility are incident survey questions, not actionable appointments.
    const isSurveyOrAdministrativeQuestion =
      /\b(?:initial\s+treatment|check\s+one|medical\s+care|treatment\s+given|first\s+aid|minor\s+on-?site|hospitalized\s+overnight)\b/i.test(
        purpose,
      ) ||
      /\b(?:initial\s+treatment|check\s+one|medical\s+care|treatment\s+given|first\s+aid|minor\s+on-?site|hospitalized\s+overnight)\b/i.test(
        snippet,
      )

    if (isSurveyOrAdministrativeQuestion && !prov && !facility) {
      return
    }

    // Universal Date Hallucination Guard:
    // If an appointment candidate has no provider/facility, or is an administrative question,
    // and its date merely duplicates the Date of Injury, it is a copied DOI date, not an appointment date.
    const caseInjuryDate = formatProposedDate(extractedData.injury?.date || existingCase?.injuryDate || '')
    let effectiveApptDate = date
    if (effectiveApptDate && caseInjuryDate && isEquivalentDate(effectiveApptDate, caseInjuryDate)) {
      if (isSurveyOrAdministrativeQuestion || (!prov && !facility)) {
        effectiveApptDate = ''
      }
    }

    const displayVal = [
      purpose,
      prov ? `with ${prov}` : '',
      effectiveApptDate ? `on ${effectiveApptDate}` : '',
      facility ? `at ${facility}` : '',
    ]
      .filter(Boolean)
      .join(' ')
    const isBareDateSnippet =
      /^\s*(?:\d{1,2}\s*[/.-]\s*\d{1,2}(?:\s*[/.-]\s*\d{2,4})?|\d{4}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*\d{1,2})\s*$/.test(
        snippet.trim(),
      )
    if (
      snippet &&
      (isBareDateSnippet ||
        /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date|injury\s+occurred)\b/i.test(
          snippet,
        ))
    ) {
      snippet = ''
    }
    const preferredApptDoc = findMatchingDocForItem(appt, docs)
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(
      snippet,
      docs,
      [
        'treatment type',
        'type of treatment',
        'initial treatment',
        'emergency room',
        'emergency evaluation',
        'emergency',
        'date of treatment',
        'treatment date',
        'date of first treatment',
        effectiveApptDate ? `date of treatment ${effectiveApptDate}` : '',
        effectiveApptDate ? `treatment date ${effectiveApptDate}` : '',
        appt.title || appt.Title,
        purpose,
        prov,
        facility,
      ].filter(Boolean),
      {
        preferredDoc: preferredApptDoc,
        excludeTerms: [
          'date of injury',
          'injury date',
          'date of accident',
          'accident date',
          'date of birth',
          'birth date',
          'dob',
          'date of hire',
          'hire date',
          'date employer notified',
          'date notified',
        ],
      },
    )

    let finalApptSnippet = snippet || loc?.matchedSnippet || ''
    if (finalApptSnippet) {
      finalApptSnippet = finalApptSnippet
        .replace(/;?\s*(?:date|time)\s+of\s+injury\s*[:-]?\s*[^;]*/gi, '')
        .replace(/;?\s*injury\s+site\s+address\s*[:-]?\s*[^;]*/gi, '')
        .replace(/;?\s*(?:employer|carrier|insurer|tpa)\s+(?:name|address)\s*[:-]?\s*[^;]*/gi, '')
        .trim()
    }
    if (
      finalApptSnippet &&
      (/^\s*(?:\d{1,2}\s*[/.-]\s*\d{1,2}(?:\s*[/.-]\s*\d{2,4})?|\d{4}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*\d{1,2})\s*$/.test(
        finalApptSnippet.trim(),
      ) ||
        /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date)\b/i.test(
          finalApptSnippet,
        ))
    ) {
      finalApptSnippet = ''
    }

    const resolvedApptPage = loc?.page && loc.page > 1 ? loc.page : appt.page || appt.Page || loc?.page || 1

    if (isSingleWorkStatusReportUpload || isDocWorkStatusReport(preferredApptDoc || matchedDoc)) {
      if (effectiveApptDate && !workStatusReportApptDate) {
        workStatusReportApptDate = effectiveApptDate
      }
      if (prov && !workStatusReportApptProvider) {
        workStatusReportApptProvider = prov
      }
      return
    }

    entities.push({
      id: `appt-${idx + 1}-${nextId++}`,
      category: 'appointment',
      field: appt.title || appt.Title || 'Appointment / Referral',
      currentValue: '',
      extractedValue: displayVal,
      attributes: {
        title: appt.title || appt.Title,
        purpose,
        provider: prov,
        date: effectiveApptDate,
        facility,
        complete: appt.complete || appt.Complete,
      },
      sourceSnippet: finalApptSnippet,
      documentId: appt.documentId || appt.DocumentId || matchedDoc?.id || defaultDocId,
      documentName:
        appt.documentName ||
        appt.DocumentName ||
        appt.sourceDocument ||
        appt.SourceDocument ||
        matchedDoc?.fileName ||
        defaultDocName,
      page: resolvedApptPage,
      lineNumber: appt.lineNumber || appt.LineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  })

  // 9. Surgeries & Hospitalizations
  const rawSurgeries =
    extractedData.surgeriesAndHospitalizations ||
    extractedData.SurgeriesAndHospitalizations ||
    extractedData.surgeries ||
    extractedData.Surgeries ||
    extractedData.procedures ||
    extractedData.Procedures ||
    extractedData.extensions?.surgeries ||
    []
  rawSurgeries.forEach((surg, idx) => {
    const proc =
      surg.procedure || surg.Procedure || surg.name || surg.Name || surg.surgery || surg.Surgery || 'Surgical Procedure'
    const date = formatProposedDate(surg.date || surg.Date || '')
    const facility = surg.facility || surg.Facility || ''
    const surgeon = surg.surgeon || surg.Surgeon || ''
    const preferredSurgDoc = findMatchingDocForItem(surg, docs)
    const snippet = surg.sourceTextSnippet || surg.SourceTextSnippet || surg.sourceSnippet || surg.SourceSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [proc, surgeon, facility], {
      preferredDoc: preferredSurgDoc,
    })

    entities.push({
      id: `surg-${idx + 1}-${nextId++}`,
      category: 'surgery',
      field: 'Surgery / Procedure',
      currentValue: '',
      extractedValue: `${proc}${date ? ` (${date})` : ''}`,
      attributes: {
        procedure: proc,
        date,
        facility,
        surgeon,
      },
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: surg.documentId || surg.DocumentId || matchedDoc?.id || defaultDocId,
      documentName:
        surg.documentName ||
        surg.DocumentName ||
        surg.sourceDocument ||
        surg.SourceDocument ||
        matchedDoc?.fileName ||
        defaultDocName,
      page: surg.page || surg.Page || loc?.page,
      lineNumber: surg.lineNumber || surg.LineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  })

  // 10. Work Status & MMI
  const statusObj =
    extractedData.status ||
    extractedData.Status ||
    extractedData.extensions?.status ||
    extractedData.extensions?.Status ||
    {}
  const ws =
    statusObj.workStatus || statusObj.WorkStatus
      ? statusObj
      : extractedData.workStatus ||
        extractedData.WorkStatus ||
        (extractedData.workCapacity || extractedData.WorkCapacity ? extractedData : statusObj)
  let capacity =
    ws.workStatus ||
    ws.WorkStatus ||
    ws.status ||
    ws.Status ||
    ws.workCapacity ||
    ws.WorkCapacity ||
    (typeof ws === 'string' ? ws : '')

  if (!capacity && rawText) {
    const wsMatch = rawText.match(/(?:Work\s+Status|Duty\s+Status|Current\s+Work\s+Status)\s*:\s*([^\r\n|]+)/i)
    if (wsMatch && wsMatch[1]) {
      capacity = wsMatch[1].trim()
    } else if (
      /\[\s*[xX✓✔]\s*\]\s*(?:Off\s+Work|Not\s+Working|Unable\s+to\s+Work|Temporarily\s+Totally\s+Disabled)/i.test(
        rawText,
      )
    ) {
      capacity = 'Not working'
    } else if (
      /\[\s*[xX✓✔]\s*\]\s*(?:Modified\s+Duty|Restricted\s+Duty|Light\s+Duty|Return\s+to\s+Work\s+with\s+Restrictions)/i.test(
        rawText,
      )
    ) {
      capacity = 'Modified duty'
    } else if (/\[\s*[xX✓✔]\s*\]\s*(?:Full\s+Duty|Regular\s+Duty|Return\s+to\s+Full\s+Duty)/i.test(rawText)) {
      capacity = 'Full duty'
    }
  }

  let restrictions = ws.restrictions || ws.Restrictions || ws.returnStatus || ws.ReturnStatus || ''
  if (!restrictions && rawText) {
    const rtwMatch = rawText.match(
      /(?:Return\s+to\s+Work\s+Status(?:\s*\/\s*Restrictions)?|Return\s+Status|Work\s+Restrictions?|Restrictions?|Activity\s+Restrictions?|Duty\s+Restrictions?|Modified\s+Duty\s+Restrictions?)\s*:\s*([^\r\n|]+)/i,
    )
    if (rtwMatch && rtwMatch[1]) {
      restrictions = rtwMatch[1].trim()
    }
  }

  if (!capacity && restrictions && isSingleWorkStatusReportUpload) {
    capacity = /\b(off\s+work|not\s+working|unable\s+to\s+work)\b/i.test(restrictions) ? 'Not working' : 'Modified duty'
  }

  if (capacity && String(capacity).trim().length > 0) {
    let rawDateReported =
      ws.date || ws.Date || ws.effectiveDate || ws.EffectiveDate || statusObj.date || statusObj.Date || ''
    if (!rawDateReported && rawText) {
      const hdrDateMatch = rawText.match(
        /(?:Report\s+of\s+Work\s+(?:Status|Ability)|Work\s+(?:Activity\s+)?Status\s+Report|Work\s+Ability\s+Report|Return\s+to\s+Work\s+(?:Status|Report|Note)|Date\s+of\s+(?:Report|Exam|Visit|Evaluation|Service)|Date\s+Reported|Exam\s+Date|Visit\s+Date|Date)\s*:?\s*(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{2}-\d{2})/i,
      )
      if (hdrDateMatch && hdrDateMatch[1]) {
        rawDateReported = hdrDateMatch[1].trim()
      }
    }
    if (!rawDateReported && workStatusReportApptDate) {
      rawDateReported = workStatusReportApptDate
    }
    const effDate = rawDateReported ? formatProposedDate(rawDateReported) || String(rawDateReported).trim() : null

    let rawDateLastWorked =
      ws.dateLastWorked ?? ws.DateLastWorked ?? statusObj.dateLastWorked ?? statusObj.DateLastWorked ?? null
    if (!rawDateLastWorked && rawText) {
      const dlwMatch = rawText.match(
        /(?:Date\s+Last\s+Worked|Last\s+Day\s+Worked|DLW)\s*:\s*(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{2}-\d{2})/i,
      )
      if (dlwMatch && dlwMatch[1]) {
        rawDateLastWorked = dlwMatch[1].trim()
      }
    }
    const dateLastWorked = rawDateLastWorked
      ? formatProposedDate(rawDateLastWorked) || String(rawDateLastWorked).trim()
      : null

    let rawCurrentTreatment =
      ws.currentTreatment ?? ws.CurrentTreatment ?? statusObj.currentTreatment ?? statusObj.CurrentTreatment ?? null
    if (!rawCurrentTreatment && rawText) {
      const ctMatch = rawText.match(/(?:Current\s+Treatment|Treatment\s+Plan)\s*:\s*([^\r\n|]+)/i)
      if (ctMatch && ctMatch[1]) {
        rawCurrentTreatment = ctMatch[1].trim()
      }
    }
    const currentTreatment =
      rawCurrentTreatment && String(rawCurrentTreatment).trim().length > 0 ? String(rawCurrentTreatment).trim() : null

    const preferredWsDoc = findMatchingDocForItem(ws, docs)
    const snippet = ws.sourceTextSnippet || ws.SourceTextSnippet || ws.sourceSnippet || ws.SourceSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(
      snippet,
      docs,
      [capacity, restrictions, 'work status', 'return to work'],
      { preferredDoc: preferredWsDoc },
    )

    const existingStatus = existingCase.status || existingCase.Status || {}
    const existingWs = existingCase.workStatus || existingCase.WorkStatus || {}
    const existingHistory = Array.isArray(existingStatus.history)
      ? existingStatus.history
      : Array.isArray(existingCase.status)
      ? existingCase.status
      : []
    const latestHistory = existingHistory.length > 0 ? existingHistory[existingHistory.length - 1] : null
    const currentVal =
      latestHistory?.workStatus ||
      existingStatus.workStatus ||
      existingStatus.WorkStatus ||
      existingWs.status ||
      existingWs.Status ||
      ''

    let treatmentProvider =
      ws.treatmentProvider ||
      ws.TreatmentProvider ||
      ws.provider ||
      ws.Provider ||
      ws.physician ||
      ws.Physician ||
      statusObj.treatmentProvider ||
      statusObj.TreatmentProvider ||
      workStatusReportProviders[0]?.extractedValue ||
      (Array.isArray(extractedData.providers) && extractedData.providers.length === 1
        ? extractedData.providers[0]?.name || ''
        : '') ||
      workStatusReportApptProvider ||
      ''

    if (!treatmentProvider && rawText) {
      const provMatch = rawText.match(
        /(?:Treatment\s+Provider|Treating\s+(?:Provider|Physician|Doctor)|Provider|Physician|Examining\s+Physician|Completed\s+By|Signed\s+By)\s*:\s*([^\r\n|]+)/i,
      )
      if (provMatch && provMatch[1]) {
        treatmentProvider = provMatch[1].trim()
      } else {
        const credLineMatch = rawText.match(
          /^\s*([A-Z][A-Za-z.'-]+(?:\s+[A-Z][A-Za-z.'-]+){1,3},\s*(?:M\.?D\.?|D\.?O\.?|D\.?C\.?|N\.?P\.?|P\.?A\.?(?:-C)?|D\.?P\.?T\.?|P\.?T\.?|Ph\.?D\.?))\s*$/m,
        )
        if (credLineMatch && credLineMatch[1]) {
          treatmentProvider = credLineMatch[1].trim()
        }
      }
    }
    treatmentProvider = treatmentProvider ? String(treatmentProvider).trim() : null

    // Resolve Provider Match for Treatment Provider (Case -> Library -> New)
    let wsProviderMatch = workStatusReportProviders[0]?.providerMatch || null
    if (treatmentProvider) {
      const existingProvidersForWs = [
        ...(existingCase.treatmentProviders || existingCase.TreatmentProviders || []),
        ...(existingCase.providers || existingCase.Providers || []),
        ...(existingCase.caseProviders || []),
      ]
      const normalizedWsProv = normalizeTextKey(treatmentProvider)
      const wsClusterKey = getProviderClusterKey(treatmentProvider, '') || normalizedWsProv
      const caseDup = existingProvidersForWs.find(p => {
        const existingName = (
          p.name ||
          p.Name ||
          [p.firstName || p.FirstName, p.lastName || p.LastName, p.suffix || p.Suffix].filter(Boolean).join(' ') ||
          p.companyName ||
          p.CompanyName ||
          p.organizationName ||
          p.OrganizationName ||
          ''
        ).trim()
        const normExisting = normalizeTextKey(existingName)
        return (
          (normalizedWsProv &&
            normExisting &&
            (normExisting.includes(normalizedWsProv) || normalizedWsProv.includes(normExisting))) ||
          isEquivalentName(existingName, treatmentProvider) ||
          (wsClusterKey && getProviderClusterKey(existingName, '') === wsClusterKey)
        )
      })

      if (caseDup) {
        const matchedCaseName =
          caseDup.name ||
          caseDup.Name ||
          [
            caseDup.firstName || caseDup.FirstName,
            caseDup.lastName || caseDup.LastName,
            caseDup.suffix || caseDup.Suffix,
          ]
            .filter(Boolean)
            .join(' ') ||
          caseDup.companyName ||
          caseDup.practiceName ||
          caseDup.organizationName ||
          treatmentProvider
        wsProviderMatch = {
          type: 'case',
          matchedName: matchedCaseName,
          matchedId: caseDup.id || caseDup.Id || caseDup.providerId || caseDup.ProviderId || null,
          reason: 'Matches provider already on this case',
          confidence: 1.0,
        }
      } else if (!wsProviderMatch || wsProviderMatch.type === 'new') {
        const backendWsMatchType = ws.matchType || ws.MatchType || statusObj.matchType || statusObj.MatchType
        const backendWsMatchedName =
          ws.matchedProviderName ||
          ws.MatchedProviderName ||
          statusObj.matchedProviderName ||
          statusObj.MatchedProviderName
        const backendWsMatchedId =
          ws.matchedProviderId || ws.MatchedProviderId || statusObj.matchedProviderId || statusObj.MatchedProviderId
        const libraryProvidersList = existingCase.libraryProviders || options?.libraryProviders || []
        const libDup = Array.isArray(libraryProvidersList)
          ? libraryProvidersList.find(p => {
              const libName = (
                p.name ||
                p.Name ||
                [p.firstName || p.FirstName, p.lastName || p.LastName, p.suffix || p.Suffix]
                  .filter(Boolean)
                  .join(' ') ||
                p.organization ||
                p.organizationName ||
                ''
              ).trim()
              return (
                isEquivalentName(libName, treatmentProvider) ||
                (wsClusterKey && getProviderClusterKey(libName, '') === wsClusterKey)
              )
            })
          : null

        if (backendWsMatchType === 'case') {
          wsProviderMatch = {
            type: 'case',
            matchedName: backendWsMatchedName || treatmentProvider,
            matchedId: backendWsMatchedId || null,
            reason: 'Matches provider already on this case',
            confidence: 0.95,
          }
        } else if (backendWsMatchType === 'library' || libDup) {
          wsProviderMatch = {
            type: 'library',
            matchedName: backendWsMatchedName || libDup?.name || libDup?.Name || treatmentProvider,
            matchedId: backendWsMatchedId || libDup?.id || libDup?.Id || null,
            reason: 'Matches provider in library',
            confidence: 0.9,
          }
        } else {
          wsProviderMatch = {
            type: 'new',
            matchedName: null,
            matchedId: null,
            reason: 'New provider to be added to Library and linked to Case',
            confidence: 0,
          }
        }
      }
    }

    const isNonEmptyRestriction =
      Boolean(restrictions) && !/^(none|n\/a|no\s+restrictions?|full\s+duty|unrestricted)$/i.test(restrictions.trim())
    const lightDuty = Boolean(
      ws.lightDuty === true ||
        ws.LightDuty === true ||
        statusObj.lightDuty === true ||
        statusObj.LightDuty === true ||
        (isNonEmptyRestriction &&
          /\b(light\s*duty|desk\s*duty|modified\s*duty|sedentary|restricted|restrictions?|transitional|limited|no\s+lifting|lifting\s+limit|weight\s+limit|accommodat\w*|sit\s*\/\s*stand|seated)\b/i.test(
            `${capacity} ${restrictions}`,
          )) ||
        /\b(light\s*duty|desk\s*duty|modified\s*duty|restricted\s*duty)\b/i.test(capacity),
    )

    entities.push({
      id: `ws-${nextId++}`,
      category: 'status',
      field: 'Work Status',
      currentValue: currentVal,
      extractedValue: `${capacity}${restrictions ? ` (Restrictions: ${restrictions})` : ''}`,
      providerMatch: wsProviderMatch || undefined,
      duplicateMatch:
        wsProviderMatch?.type === 'case'
          ? {
              existingName: wsProviderMatch.matchedName,
              existingId: wsProviderMatch.matchedId,
              confidence: wsProviderMatch.confidence,
            }
          : null,
      attributes: {
        dateLastWorked,
        date: effDate,
        effectiveDate: effDate || '',
        treatmentProvider,
        workStatus: capacity,
        workCapacity: capacity,
        lightDuty,
        returnStatus: (ws.returnStatus || ws.ReturnStatus || restrictions || '').trim() || null,
        restrictions: (restrictions || '').trim() || null,
        currentTreatment,
        providerMatch: wsProviderMatch || undefined,
      },
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: ws.documentId || ws.DocumentId || matchedDoc?.id || defaultDocId,
      documentName:
        ws.documentName ||
        ws.DocumentName ||
        ws.sourceDocument ||
        ws.SourceDocument ||
        matchedDoc?.fileName ||
        defaultDocName,
      page: ws.page || ws.Page || loc?.page,
      lineNumber: ws.lineNumber || ws.LineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  }

  const mmi =
    extractedData.mmi ||
    extractedData.Mmi ||
    (extractedData.impairmentRating || extractedData.ImpairmentRating ? extractedData : null)
  const isMmiReached = mmi
    ? mmi.reached !== undefined && mmi.reached !== null
      ? mmi.reached
      : mmi.Reached !== undefined && mmi.Reached !== null
      ? mmi.Reached
      : mmi.isMmiReached !== undefined && mmi.isMmiReached !== null
      ? mmi.isMmiReached
      : mmi.IsMmiReached !== undefined && mmi.IsMmiReached !== null
      ? mmi.IsMmiReached
      : undefined
    : undefined
  const mmiDate = formatProposedDate(mmi ? mmi.date || mmi.Date || mmi.mmiDate || mmi.MmiDate : null)
  const impairment =
    mmi &&
    (mmi.impairmentRating !== null && mmi.impairmentRating !== undefined ? mmi.impairmentRating : mmi.ImpairmentRating)
  const hasValidMmiData =
    mmi &&
    (isMmiReached === true ||
      isMmiReached === false ||
      (mmiDate && mmiDate.length > 0) ||
      (impairment !== null && impairment !== undefined && String(impairment).trim().length > 0))

  if (hasValidMmiData) {
    const preferredMmiDoc = findMatchingDocForItem(mmi, docs)
    const snippet = mmi.sourceTextSnippet || mmi.SourceTextSnippet || mmi.sourceSnippet || mmi.SourceSnippet || ''
    const { loc, doc: matchedDoc } = locateSnippetInAllDocs(
      snippet,
      docs,
      ['mmi', 'maximum medical improvement', 'impairment'],
      { preferredDoc: preferredMmiDoc },
    )

    const existingMmi = existingCase.mmi || existingCase.Mmi || {}
    const existingHistory = Array.isArray(existingMmi.history) ? existingMmi.history : []
    const latestHistory = existingHistory.length > 0 ? existingHistory[existingHistory.length - 1] : null
    const reachedVal =
      typeof latestHistory?.isActual === 'boolean'
        ? latestHistory.isActual
        : typeof existingMmi.reached === 'boolean'
        ? existingMmi.reached
        : typeof existingMmi.Reached === 'boolean'
        ? existingMmi.Reached
        : typeof existingMmi.isMmiReached === 'boolean'
        ? existingMmi.isMmiReached
        : null

    const currentVal =
      latestHistory?.narrative ||
      (latestHistory?.date ? `MMI Date: ${latestHistory.date}` : '') ||
      (typeof reachedVal === 'boolean' ? `MMI: ${reachedVal ? 'Reached' : 'Not Reached'}` : '')

    const narrative =
      mmi.narrative ||
      mmi.Narrative ||
      `MMI: ${isMmiReached ? 'Reached' : isMmiReached === false ? 'Not Reached' : 'Pending'}${
        mmiDate ? ` on ${mmiDate}` : ''
      }${impairment ? ` • Impairment: ${impairment}%` : ''}`

    entities.push({
      id: `mmi-${nextId++}`,
      category: 'status',
      field: 'MMI / Impairment',
      currentValue: currentVal,
      extractedValue: `MMI: ${isMmiReached ? 'Reached' : isMmiReached === false ? 'Not Reached' : 'Pending'}${
        mmiDate ? ` on ${mmiDate}` : ''
      }${impairment ? ` • Impairment: ${impairment}%` : ''}`,
      attributes: {
        isMmiReached,
        reached: isMmiReached,
        mmiDate,
        date: mmiDate,
        impairmentRating: impairment,
        narrative,
        isActual: isMmiReached === true,
      },
      sourceSnippet: snippet || loc?.matchedSnippet || '',
      documentId: mmi.documentId || mmi.DocumentId || matchedDoc?.id || defaultDocId,
      documentName:
        mmi.documentName ||
        mmi.DocumentName ||
        mmi.sourceDocument ||
        mmi.SourceDocument ||
        matchedDoc?.fileName ||
        defaultDocName,
      page: mmi.page || mmi.Page || loc?.page,
      lineNumber: mmi.lineNumber || mmi.LineNumber || loc?.lineNumber,
      occurrenceIndex: 1,
      totalOccurrences: 1,
      status: 'pending',
    })
  }

  // 17. Proposed Tasks & Follow-ups Inference
  const rawTasks =
    extractedData.tasks ||
    extractedData.Tasks ||
    extractedInput.tasks ||
    extractedInput.Tasks ||
    extractedInput.pendingExtractions?.tasks ||
    extractedInput.extensions?.pendingExtractions?.tasks ||
    existingCase.pendingExtractions?.tasks ||
    existingCase.extensions?.pendingExtractions?.tasks ||
    []

  if (Array.isArray(rawTasks)) {
    for (const task of rawTasks) {
      if (!task) continue
      const title = String(task.title || task.Title || task.action || task.name || '').trim()
      if (!title) continue

      const cat = String(task.category || task.Category || 'Authorization').trim()
      let leadDays = Number(task.suggestedLeadDays || task.SuggestedLeadDays || task.leadDays || 0)

      if (!leadDays || leadDays <= 0) {
        const combined = `${cat} ${title}`.toLowerCase()
        if (combined.includes('work status') || combined.includes('transmittal') || combined.includes('restriction')) {
          leadDays = 1
        } else if (
          combined.includes('dme') ||
          combined.includes('equipment') ||
          combined.includes('imaging') ||
          combined.includes('mri') ||
          combined.includes('ct') ||
          combined.includes('pharmacy') ||
          combined.includes('medication') ||
          combined.includes('rx')
        ) {
          leadDays = 3
        } else if (
          combined.includes('referral') ||
          combined.includes('specialist') ||
          combined.includes('surgical') ||
          combined.includes('surgery') ||
          combined.includes('ur') ||
          combined.includes('record')
        ) {
          leadDays = 5
        } else if (
          combined.includes('follow-up') ||
          combined.includes('follow up') ||
          combined.includes('appointment tracking') ||
          combined.includes('scheduling') ||
          combined.includes('schedule')
        ) {
          leadDays = 14
        } else if (cat.toLowerCase() === 'authorization') {
          leadDays = 3
        } else {
          leadDays = 3
        }
      }

      let dueDate = task.dueDate || task.DueDate || null
      if (!dueDate || !isValidCalendarDate(dueDate)) {
        // Fallback safely to current date + lead time when document date is absent
        const docDate = docs[0]?.date || docs[0]?.properties?.date || null
        const baseDate = docDate && isValidCalendarDate(docDate) ? Moment(docDate) : Moment()
        dueDate = baseDate.clone().add(leadDays, 'days').format('YYYY-MM-DD')
      }

      const snippet =
        task.sourceTextSnippet ||
        task.SourceTextSnippet ||
        task.sourceSnippet ||
        task.SourceSnippet ||
        task.snippet ||
        task.description ||
        ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [title, cat, 'task'])

      entities.push({
        id: `task-${nextId++}`,
        category: 'task',
        field: 'Proposed Task',
        currentValue: '',
        extractedValue: title,
        attributes: {
          title,
          description: task.description || task.Description || '',
          category: cat,
          taskCategory: cat,
          suggestedLeadDays: leadDays,
          leadDays,
          dueDate,
          date: dueDate,
          providerName: task.providerName || task.ProviderName || task.provider || null,
          activityType: cat || 'Coordination of Services',
          relatedSection: 'documents',
        },
        sourceSnippet: snippet || loc?.matchedSnippet || '',
        documentId: task.documentId || task.DocumentId || matchedDoc?.id || defaultDocId,
        documentName:
          task.documentName ||
          task.DocumentName ||
          task.sourceDocument ||
          task.SourceDocument ||
          matchedDoc?.fileName ||
          defaultDocName,
        page: task.page || task.Page || loc?.page,
        lineNumber: task.lineNumber || task.LineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // 18. Key Contacts & Clinic Staff
  const rawContacts =
    extractedData.contacts ||
    extractedData.Contacts ||
    extractedInput.contacts ||
    extractedInput.Contacts ||
    extractedInput.pendingExtractions?.contacts ||
    extractedInput.extensions?.pendingExtractions?.contacts ||
    existingCase.pendingExtractions?.contacts ||
    existingCase.extensions?.pendingExtractions?.contacts ||
    []

  if (Array.isArray(rawContacts)) {
    const existingKeyContacts = existingCase.keyContacts || existingCase.KeyContacts || []
    for (const contact of rawContacts) {
      if (!contact) continue
      const name = String(contact.name || contact.Name || '').trim()
      if (!name) continue

      const role = contact.role || contact.Role || 'Staff Contact'
      const orgOrProv =
        contact.organizationOrProviderName ||
        contact.OrganizationOrProviderName ||
        contact.organization ||
        contact.Organization ||
        contact.facility ||
        contact.provider ||
        ''
      const addrObj = contact.address || contact.Address || {}
      const line1 = addrObj.line1 || addrObj.Line1 || contact.line1 || contact.Line1 || ''
      const line2 = addrObj.line2 || addrObj.Line2 || contact.line2 || contact.Line2 || ''
      const city = addrObj.city || addrObj.City || contact.city || contact.City || ''
      const state = addrObj.state || addrObj.State || contact.state || contact.State || ''
      const zip = addrObj.zip || addrObj.Zip || contact.zip || contact.Zip || ''
      const formattedAddr = [line1, line2, [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(', ')

      const duplicateContact = existingKeyContacts.find(kc => {
        const kcName = String(kc?.name || kc?.Name || '').trim()
        return kcName && (kcName.toLowerCase() === name.toLowerCase() || isEquivalentName(kcName, name))
      })

      const snippet = contact.sourceTextSnippet || contact.SourceTextSnippet || contact.sourceSnippet || ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [name, role, orgOrProv, 'contact'])

      entities.push({
        id: `contact-${nextId++}`,
        category: 'contact',
        field: role ? `Key Contact (${role})` : 'Key Contact',
        currentValue: duplicateContact ? duplicateContact.name || duplicateContact.Name || '' : '',
        extractedValue: name,
        duplicateMatch: duplicateContact
          ? {
              existingName: duplicateContact.name || duplicateContact.Name || name,
              existingId: duplicateContact.id || duplicateContact.Id || null,
            }
          : null,
        attributes: {
          name,
          role,
          organizationOrProviderName: orgOrProv,
          phone: contact.phone || contact.Phone || '',
          extension: contact.extension || contact.Extension || '',
          fax: contact.fax || contact.Fax || '',
          email: contact.email || contact.Email || '',
          directAddress: contact.directAddress || contact.DirectAddress || '',
          address: formattedAddr ? { line1, line2, city, state, zip } : null,
          line1,
          line2,
          city,
          state,
          zip,
          notes: contact.notes || contact.Notes || '',
        },
        sourceSnippet: snippet || loc?.matchedSnippet || '',
        documentId: contact.documentId || contact.DocumentId || matchedDoc?.id || defaultDocId,
        documentName:
          contact.documentName ||
          contact.DocumentName ||
          contact.sourceDocument ||
          contact.SourceDocument ||
          matchedDoc?.fileName ||
          defaultDocName,
        page: contact.page || contact.Page || loc?.page,
        lineNumber: contact.lineNumber || contact.LineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  // 19. Appointment & Case Activities Inference
  const rawActivities =
    extractedData.activities ||
    extractedData.Activities ||
    extractedInput.activities ||
    extractedInput.Activities ||
    extractedInput.pendingExtractions?.activities ||
    extractedInput.extensions?.pendingExtractions?.activities ||
    existingCase.pendingExtractions?.activities ||
    existingCase.extensions?.pendingExtractions?.activities ||
    []

  if (Array.isArray(rawActivities)) {
    for (const act of rawActivities) {
      if (!act) continue
      const desc = String(act.description || act.Description || act.type || act.Type || '').trim()
      if (!desc) continue

      const snippet = act.sourceTextSnippet || act.SourceTextSnippet || act.sourceSnippet || ''
      const { loc, doc: matchedDoc } = locateSnippetInAllDocs(snippet, docs, [desc, act.type || 'activity'])

      entities.push({
        id: `activity-${nextId++}`,
        category: 'activity',
        field: 'Appointment Activity',
        currentValue: '',
        extractedValue: desc,
        attributes: {
          type: act.type || act.Type || 'Coordination of Services',
          description: desc,
          suggestedHours: Number(act.suggestedHours || act.SuggestedHours || act.hours || 0.2),
          hours: Number(act.suggestedHours || act.SuggestedHours || act.hours || 0.2),
        },
        sourceSnippet: snippet || loc?.matchedSnippet || '',
        documentId: act.documentId || act.DocumentId || matchedDoc?.id || defaultDocId,
        documentName:
          act.documentName ||
          act.DocumentName ||
          act.sourceDocument ||
          act.SourceDocument ||
          matchedDoc?.fileName ||
          defaultDocName,
        page: act.page || act.Page || loc?.page,
        lineNumber: act.lineNumber || act.LineNumber || loc?.lineNumber,
        occurrenceIndex: 1,
        totalOccurrences: 1,
        status: 'pending',
      })
    }
  }

  const priorityOrder = ARCHETYPE_CATEGORY_PRIORITIES[docSubtype]
  if (priorityOrder && priorityOrder.length > 0) {
    const getRank = cat => {
      const idx = priorityOrder.indexOf(cat)
      return idx === -1 ? 999 : idx
    }
    // Stable sort preserving relative discovery order within same category
    entities.sort((a, b) => getRank(a.category) - getRank(b.category))
  }

  return entities.map(entity => {
    if (entity.sourceSnippet && typeof entity.sourceSnippet === 'string') {
      const maxLen = entity.field === 'Injury Description' ? 400 : 250
      if (entity.sourceSnippet.length > maxLen) {
        const matchTerm = entity.extractedValue || entity.field || ''
        entity.sourceSnippet = extractConciseSnippet(entity.sourceSnippet, matchTerm, maxLen)
      }
    }
    return entity
  })
}

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

export default function HitlReviewModal({
  show,
  onHide,
  documentData = EMPTY_OBJECT,
  initialEntities = EMPTY_ARRAY,
  rawExtractedData,
  existingCase = EMPTY_OBJECT,
  onApplyChanges,
  onCancelAndDiscard,
  user,
  title = 'Smart Upload',
  isAnalyzing = false,
  isSmartIntake = false,
}) {
  const [entities, setEntities] = useState([])
  const [activeEntityId, setActiveEntityId] = useState(null)
  const [activeDocumentId, setActiveDocumentId] = useState(null)
  const [isApplying, setIsApplying] = useState(false)
  const [docPropertyOverrides, setDocPropertyOverrides] = useState({})
  const targetCaseNumber =
    existingCase?.caseNumber ||
    existingCase?.id ||
    documentData?.caseNumber ||
    rawExtractedData?.caseNumber ||
    rawExtractedData?.caseId

  // Resolve documents array for multi-document viewer tabs
  const resolvedDocuments = useMemo(() => {
    let docs = []
    if (documentData?.documents && documentData.documents.length > 0) {
      docs = documentData.documents
    } else {
      const pending =
        rawExtractedData?.extensions?.pendingExtractions ||
        rawExtractedData?.pendingExtractions ||
        existingCase?.extensions?.pendingExtractions ||
        existingCase?.pendingExtractions
      if (pending?.documents && pending.documents.length > 0) {
        docs = pending.documents
      } else if (rawExtractedData?.documents && rawExtractedData.documents.length > 0) {
        docs = rawExtractedData.documents
      } else if (documentData?.textContent || documentData?.fileName) {
        docs = [
          {
            id: 'doc-1',
            fileName: documentData.fileName || 'Intake_Document.txt',
            fileType: documentData.fileType || 'txt',
            textContent: documentData.textContent || '',
            url: documentData.url || null,
            lineBlocks: documentData.lineBlocks || [],
          },
        ]
      }
    }

    const token = getToken()

    return docs.map((doc, idx) => {
      const docId = doc.id || `doc-${idx + 1}`
      const override = docPropertyOverrides[docId] || docPropertyOverrides[doc.fileName] || null
      const fileName = doc.fileName || doc.displayName || ''
      const isDocPdf = doc.fileType === 'pdf' || fileName.toLowerCase().endsWith('.pdf')
      const isDocImage =
        /^(image|png|jpe?g)$/i.test(doc.fileType || '') || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(fileName)
      const isDocWord = doc.fileType === 'docx' || doc.fileType === 'doc' || /\.(docx|doc)$/i.test(fileName)
      const detectedType = isDocPdf ? 'pdf' : isDocImage ? 'image' : isDocWord ? 'docx' : 'txt'
      let resolvedUrl = doc.url || doc.blobUrl || null
      if (!resolvedUrl && (isDocPdf || isDocImage || isDocWord) && fileName && targetCaseNumber) {
        resolvedUrl = `/api/cases/${targetCaseNumber}/sharefile-files/content/${encodeURIComponent(fileName)}${
          token ? `?access_token=${token}` : ''
        }`
      }
      const docLineBlocks =
        doc.lineBlocks ||
        documentData?.lineBlocks ||
        rawExtractedData?.extensions?.lineBlocks ||
        existingCase?.extensions?.lineBlocks ||
        []

      let parsedProps = {}
      if (doc.properties) {
        try {
          parsedProps = typeof doc.properties === 'string' ? JSON.parse(doc.properties) : { ...doc.properties }
        } catch {}
      }

      const docProps =
        rawExtractedData?.extensions?.pendingExtractions?.extractedData?.extensions?.documentProperties ||
        rawExtractedData?.extensions?.documentProperties ||
        rawExtractedData?.documentProperties ||
        existingCase?.extensions?.pendingExtractions?.extractedData?.extensions?.documentProperties
      let matchedDocProps = null
      if (docProps && typeof docProps === 'object') {
        const matchKey =
          Object.keys(docProps).find(k => k.toLowerCase() === doc.fileName?.toLowerCase()) ||
          (docs.length === 1 ? Object.keys(docProps)[0] : null)
        if (matchKey && docProps[matchKey]) {
          matchedDocProps = docProps[matchKey]
        }
      }

      let docSubtype =
        override?.documentSubtype ??
        (doc.documentSubtype ||
          parsedProps?.['Document Subtype'] ||
          parsedProps?.documentSubtype ||
          matchedDocProps?.documentSubtype ||
          null)
      let docPurpose =
        override?.documentPurpose ??
        (doc.documentPurpose ||
          parsedProps?.['Document Purpose'] ||
          parsedProps?.documentPurpose ||
          matchedDocProps?.documentPurpose ||
          null)

      const sequenceNumber =
        override?.sequenceNumber ??
        (doc.sequenceNumber ||
          parsedProps?.['Sequence Number'] ||
          parsedProps?.sequenceNumber ||
          matchedDocProps?.sequenceNumber ||
          null)
      const startDate =
        override?.startDate ??
        (doc.startDate || parsedProps?.['Start Date'] || parsedProps?.startDate || matchedDocProps?.startDate || null)
      const endDate =
        override?.endDate ??
        (doc.endDate || parsedProps?.['End Date'] || parsedProps?.endDate || matchedDocProps?.endDate || null)

      const isFroi =
        /(?:^|[^a-zA-Z0-9])(froi|first[\s_-]*report[\s_-]*of[\s_-]*(?:injury|illness|occupational|incident)|employer'?s?[\s_-]*first[\s_-]*report|wc[\s_-]*100|form[\s_-]*100)(?:$|[^a-zA-Z0-9])/i.test(
          doc.fileName || '',
        )
      if (!override && isFroi && (docPurpose === 'Initial Case Report' || !docPurpose)) {
        docPurpose = 'Legal Document'
        docSubtype = null
      }

      const mergedProperties = {
        ...parsedProps,
        ...(override?.properties || {}),
      }

      return {
        ...doc,
        id: docId,
        fileType: detectedType,
        url: resolvedUrl,
        lineBlocks: docLineBlocks,
        documentPurpose: docPurpose,
        documentSubtype: docSubtype,
        sequenceNumber,
        startDate,
        endDate,
        parsedProperties: mergedProperties,
      }
    })
  }, [documentData, rawExtractedData, existingCase, targetCaseNumber, docPropertyOverrides])

  // Extract available organizations for provider linking dropdown
  const availableOrganizations = useMemo(() => {
    const rawData =
      rawExtractedData?.extensions?.pendingExtractions?.extractedData ||
      rawExtractedData?.pendingExtractions?.extractedData ||
      rawExtractedData?.extractedData ||
      rawExtractedData ||
      {}
    return getAvailableOrganizations(rawData, existingCase, resolvedDocuments)
  }, [rawExtractedData, existingCase, resolvedDocuments])

  // Initialize or re-populate entities when modal opens or extraction completes (without wiping user edits on parent re-renders)
  const initializedSessionRef = useRef(false)
  useEffect(() => {
    if (show) {
      if (isAnalyzing) {
        initializedSessionRef.current = false
        setEntities([])
        setActiveEntityId(null)
      } else if (!initializedSessionRef.current) {
        if (initialEntities && initialEntities.length > 0) {
          initializedSessionRef.current = true
          setEntities(initialEntities)
          setActiveEntityId(prev => prev || initialEntities[0]?.id || null)
          setActiveDocumentId(prev => prev || initialEntities[0]?.documentId || resolvedDocuments[0]?.id || 'doc-1')
        } else if (rawExtractedData) {
          const isIntakeMode = Boolean(
            isSmartIntake ||
              title === 'Smart Intake' ||
              rawExtractedData?.source === 'Smart Intake' ||
              rawExtractedData?.extensions?.pendingExtractions?.source === 'Smart Intake' ||
              existingCase?.caseStatus?.toLowerCase() === 'draft' ||
              existingCase?.status === 'Draft' ||
              existingCase?.isDraft,
          )
          const normalized = normalizeExtractedEntities(rawExtractedData, existingCase, resolvedDocuments, {
            isSmartIntake: isIntakeMode,
          })
          initializedSessionRef.current = true
          setEntities(normalized)
          setActiveEntityId(prev => prev || normalized[0]?.id || null)
          setActiveDocumentId(prev => prev || normalized[0]?.documentId || resolvedDocuments[0]?.id || 'doc-1')
        }
      }
    } else {
      initializedSessionRef.current = false
      setEntities([])
      setActiveEntityId(null)
      setActiveDocumentId(null)
    }
  }, [show, rawExtractedData, initialEntities, existingCase, resolvedDocuments, isAnalyzing, isSmartIntake, title])

  // Async Provider Library lookup for Work Status Treatment Provider and standalone Provider entities when not already matched to Case
  useEffect(() => {
    if (!show || !entities || entities.length === 0 || typeof ProviderService?.search !== 'function') return
    const uncheckedEntities = entities.filter(
      e =>
        !e._libraryChecked &&
        (!e.providerMatch || e.providerMatch.type === 'new') &&
        ((e.category === 'status' && e.field === 'Work Status' && e.attributes?.treatmentProvider) ||
          (e.category === 'provider' && e.extractedValue)),
    )
    if (uncheckedEntities.length === 0) return

    let isCancelled = false
    ;(async () => {
      for (const targetEnt of uncheckedEntities) {
        const provName =
          targetEnt.category === 'provider' ? targetEnt.extractedValue : targetEnt.attributes.treatmentProvider
        try {
          const searchRes = await ProviderService.search({ searchText: provName, page: 1, perPage: 10 })
          if (isCancelled) return
          const match = searchRes?.results?.find(p => {
            const candidate = p.name || p.sortName || p.organization || p.organizationName || ''
            return candidate.toLowerCase() === provName.toLowerCase() || isEquivalentName(candidate, provName)
          })
          setEntities(prev =>
            prev.map(item => {
              if (item.id !== targetEnt.id) return item
              if (match?.id) {
                const updatedMatch = {
                  type: 'library',
                  matchedName: match.name || provName,
                  matchedId: match.id,
                  reason: 'Matches provider in library',
                  confidence: 0.95,
                }
                return {
                  ...item,
                  _libraryChecked: true,
                  providerMatch: updatedMatch,
                  attributes: {
                    ...(item.attributes || {}),
                    providerMatch: updatedMatch,
                  },
                }
              }
              return { ...item, _libraryChecked: true }
            }),
          )
        } catch {
          if (!isCancelled) {
            setEntities(prev =>
              prev.map(item => (item.id === targetEnt.id ? { ...item, _libraryChecked: true } : item)),
            )
          }
        }
      }
    })()

    return () => {
      isCancelled = true
    }
  }, [show, entities])

  // Dynamically enrich entities with accepted predecessor merge opportunities
  const enrichedEntities = useMemo(() => {
    const acceptedInCluster = new Map()
    entities.forEach(e => {
      if (e.entityClusterId && (e.status === 'accepted' || e.status === 'edited')) {
        if (!acceptedInCluster.has(e.entityClusterId)) {
          acceptedInCluster.set(e.entityClusterId, e)
        }
      }
    })

    return entities.map(e => {
      if (e.entityClusterId && e.occurrenceIndex > 1) {
        const predecessor = acceptedInCluster.get(e.entityClusterId)
        if (predecessor && predecessor.id !== e.id) {
          return {
            ...e,
            hasAcceptedPredecessor: true,
            acceptedPredecessorId: predecessor.id,
            acceptedPredecessorIndex: predecessor.occurrenceIndex,
          }
        }
      }
      return {
        ...e,
        hasAcceptedPredecessor: false,
      }
    })
  }, [entities])

  const activeEntity = enrichedEntities.find(e => e.id === activeEntityId) || enrichedEntities[0]

  // Auto-advance helper to next pending entity
  const advanceToNextPending = currentId => {
    const currIdx = enrichedEntities.findIndex(e => e.id === currentId)
    const nextPending =
      enrichedEntities.slice(currIdx + 1).find(e => e.id !== currentId && (!e.status || e.status === 'pending')) ||
      enrichedEntities.find(e => e.id !== currentId && (!e.status || e.status === 'pending'))
    if (nextPending) {
      setActiveEntityId(nextPending.id)
      if (nextPending.documentId) {
        setActiveDocumentId(nextPending.documentId)
      }
    }
  }

  const handleSelectEntity = entity => {
    if (entity?.id) {
      setActiveEntityId(entity.id)
      if (entity.documentId) {
        setActiveDocumentId(entity.documentId)
      }
    }
  }

  const handleAcceptEntity = id => {
    setEntities(prev => {
      const target = prev.find(e => e.id === id)
      if (!target) return prev

      if (target.isNegativeFinding) {
        return prev.map(e =>
          e.id === id
            ? {
                ...e,
                status: 'rejected',
                rejectionReason: 'Negative diagnostic finding',
              }
            : e,
        )
      }

      if (target.isConsolidated || target.entityClusterId) {
        return prev.map(e => {
          if (e.id === id) {
            return { ...e, status: 'accepted' }
          }
          // If cluster leader is accepted, resolve remaining pending occurrences in cluster as merged
          if (e.entityClusterId === target.entityClusterId && e.id !== id && (!e.status || e.status === 'pending')) {
            return {
              ...e,
              status: 'merged',
              mergedIntoId: id,
              mergedIntoTitle: target.extractedValue || target.field,
            }
          }
          return e
        })
      }

      return prev.map(e => (e.id === id ? { ...e, status: 'accepted' } : e))
    })
    advanceToNextPending(id)
  }

  const handleRejectEntity = (id, reason) => {
    setEntities(prev =>
      prev.map(e =>
        e.id === id
          ? {
              ...e,
              status: 'rejected',
              rejectionReason: reason || 'Skipped',
            }
          : e,
      ),
    )
    advanceToNextPending(id)
  }

  const handleMergeEntity = (id, targetPredecessorId) => {
    setEntities(prev => {
      const occurrence = prev.find(e => e.id === id)
      if (!occurrence) return prev

      const targetId =
        targetPredecessorId ||
        prev.find(
          e =>
            e.entityClusterId === occurrence.entityClusterId &&
            (e.status === 'accepted' || e.status === 'edited') &&
            e.id !== id,
        )?.id

      const target = prev.find(e => e.id === targetId)

      return prev.map(e => {
        if (e.id === id) {
          return {
            ...e,
            status: 'merged',
            mergedIntoId: targetId,
            mergedIntoTitle: target ? target.extractedValue || target.field : 'Accepted record',
          }
        }
        if (e.id === targetId) {
          const mergedAttrs = { ...(target.attributes || {}) }
          const occAttrs = occurrence.attributes || {}
          Object.keys(occAttrs).forEach(key => {
            if (!mergedAttrs[key] && occAttrs[key]) {
              mergedAttrs[key] = occAttrs[key]
            }
          })
          return {
            ...e,
            attributes: mergedAttrs,
          }
        }
        return e
      })
    })

    advanceToNextPending(id)
  }

  const handleAssociateOrganization = (entityId, organizationName) => {
    setEntities(prev =>
      prev.map(e => {
        if (e.id === entityId) {
          return {
            ...e,
            attributes: {
              ...(e.attributes || {}),
              practiceName: organizationName,
            },
          }
        }
        return e
      }),
    )
  }

  const handleUpdateMedicationFlags = (entityId, flags) => {
    setEntities(prev =>
      prev.map(e => {
        if (e.id === entityId) {
          return {
            ...e,
            attributes: {
              ...(e.attributes || {}),
              ...flags,
            },
          }
        }
        return e
      }),
    )
  }

  const handleEditEntity = (id, newValue) => {
    setEntities(prev =>
      prev.map(e =>
        e.id === id
          ? {
              ...e,
              originalExtractedValue:
                e.originalExtractedValue !== undefined ? e.originalExtractedValue : e.extractedValue,
              extractedValue: newValue,
              status: 'accepted',
              isEdited: true,
              attributes: e.attributes
                ? {
                    ...e.attributes,
                    results: newValue,
                    ...(e.field === 'Employer' ? { name: newValue } : {}),
                  }
                : e.attributes,
            }
          : e,
      ),
    )
    advanceToNextPending(id)
  }

  const handleUndoEntity = id => {
    setEntities(prev =>
      prev.map(e => {
        if (e.id !== id) return e
        const restoredVal = e.originalExtractedValue !== undefined ? e.originalExtractedValue : e.extractedValue
        return {
          ...e,
          extractedValue: restoredVal,
          status: 'pending',
          rejectionReason: null,
          mergedIntoId: null,
          mergedIntoTitle: null,
          attributes: e.attributes
            ? {
                ...e.attributes,
                results: restoredVal,
              }
            : e.attributes,
        }
      }),
    )
    setActiveEntityId(id)
  }

  const handleAcceptAllInCategory = category => {
    setEntities(prev =>
      prev.map(e => {
        if (matchesCategory(e.category, category)) {
          if (!e.status || e.status === 'pending') return { ...e, status: 'accepted' }
        }
        return e
      }),
    )
  }

  const handleAddManualEntity = ({ category, field, value, snippet, page, documentId }) => {
    if (!value || !value.trim()) return
    const cleanVal = value.trim()
    const newId = `manual-${category}-${Date.now()}`
    const newEntity = {
      id: newId,
      category: category || 'diagnosis',
      field:
        field ||
        (category === 'provider'
          ? 'Treatment Provider'
          : category === 'medication'
          ? 'Medication'
          : category === 'allergy'
          ? 'Allergy'
          : 'Diagnosis'),
      extractedValue: cleanVal,
      currentValue: '',
      sourceSnippet: snippet || cleanVal,
      page: page || activeEntity?.page || 1,
      documentId: documentId || activeDocumentId,
      status: 'accepted', // Auto-accepted per Decision 1 (Option A)
      isManual: true,
      totalOccurrences: 1,
      occurrenceIndex: 1,
      attributes: {
        source: 'Manual Selection',
        ...(category === 'provider' ? { classification: detectProviderClassification(cleanVal) } : {}),
      },
    }

    setEntities(prev => [...prev, newEntity])
    setActiveEntityId(newId)
    cogoToast.success(`Added ${newEntity.field} "${cleanVal.length > 25 ? cleanVal.slice(0, 23) + '…' : cleanVal}"`, {
      position: 'top-right',
    })
  }

  const handleApproveAndApply = async (timeOptions = {}) => {
    setIsApplying(true)
    try {
      const acceptedEntities = enrichedEntities.filter(e => e.status === 'accepted' || e.status === 'edited')
      const mergedEntities = enrichedEntities.filter(e => e.status === 'merged')
      const rejectedEntities = enrichedEntities.filter(e => e.status === 'rejected')

      if (targetCaseNumber) {
        await applyAcceptedTasks(acceptedEntities, targetCaseNumber, user)
        if (timeOptions?.logRecordReviewTime && Number(timeOptions.recordReviewHours) > 0) {
          try {
            const docLabel =
              activeDoc?.fileName || documentData?.fileName || resolvedDocuments?.[0]?.fileName || 'Medical Record'
            await CasesService.logBillableActivity(targetCaseNumber, {
              type: 'Record Review',
              time: Math.round(Number(timeOptions.recordReviewHours) * 10) / 10,
              date: new Date().toISOString().split('T')[0],
              description: `Medical record review and clinical entity verification (${docLabel}).`,
              sourceKey: `hitl-review:${activeDoc?.id || docLabel}`,
            })
          } catch (_timeErr) {
            // Non-blocking if billable activity logging fails
          }
        }
      }

      if (onApplyChanges) {
        await onApplyChanges(acceptedEntities, {
          allEntities: enrichedEntities,
          acceptedEntities,
          mergedEntities,
          rejectedEntities,
        })
      }
      onHide()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to commit accepted entities', err)
      cogoToast.error(err.message || 'Failed to apply accepted entities to case.', { position: 'top-right' })
    } finally {
      setIsApplying(false)
    }
  }

  const activeDoc = resolvedDocuments.find(d => d.id === activeDocumentId) || resolvedDocuments[0] || {}
  const activeSubtype = activeDoc.documentSubtype || null

  const handleUpdateDocumentMetadata = async (doc, updatedMeta) => {
    const props = updatedMeta?.properties || {}
    const nextOverride = {
      documentPurpose: props['Document Purpose'] || doc.documentPurpose || 'Medical Record',
      documentSubtype: props['Document Subtype'] || '',
      sequenceNumber: props['Sequence Number'] || '',
      startDate: props['Start Date'] || '',
      endDate: props['End Date'] || '',
      properties: props,
    }
    setDocPropertyOverrides(prev => ({
      ...prev,
      [doc.id]: nextOverride,
      ...(doc.fileName ? { [doc.fileName]: nextOverride } : {}),
    }))

    if (targetCaseNumber) {
      const fileTarget = doc.shareFileId || doc.fileName
      if (fileTarget) {
        try {
          await CasesService.updateFileMetadata(targetCaseNumber, fileTarget, {
            description: updatedMeta?.description || props.Description || '',
            properties: props,
          })
          cogoToast.success(`Updated properties for ${doc.fileName}`, { position: 'top-right' })
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to update document metadata from review modal', err)
          cogoToast.error('Failed to save document properties.', { position: 'top-right' })
        }
      }
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="modal-95w" centered backdrop="static">
      <Modal.Body className="p-0">
        <div className="hitl-review-workspace">
          {/* Left Pane: Document Viewer Canvas with Multi-Document Tabs & Synced Scrolling */}
          <DocumentViewerPane
            documents={resolvedDocuments}
            entities={enrichedEntities}
            caseNumber={targetCaseNumber}
            fileName={documentData.fileName || resolvedDocuments[0]?.fileName || 'Intake_Document.txt'}
            fileType={documentData.fileType || resolvedDocuments[0]?.fileType || 'txt'}
            textContent={documentData.textContent || resolvedDocuments[0]?.textContent || ''}
            url={activeDoc.url || resolvedDocuments[0]?.url || documentData.url || null}
            activeDocumentId={activeDocumentId}
            onSelectDocument={setActiveDocumentId}
            activeSnippet={activeEntity?.sourceSnippet || ''}
            activePage={activeEntity?.page || 1}
            activeLineNumber={activeEntity?.lineNumber}
            activeCategory={activeEntity?.category}
            activeEntityField={activeEntity?.field}
            activeEntityValue={activeEntity?.extractedValue}
            isAnalyzing={isAnalyzing}
            onClose={onHide}
            onAddEntity={handleAddManualEntity}
            onUpdateDocumentMetadata={targetCaseNumber ? handleUpdateDocumentMetadata : undefined}
            onSnippetClick={(snippet, entityId) => {
              if (entityId) {
                const directMatch = enrichedEntities.find(e => e.id === entityId)
                if (directMatch) {
                  setActiveEntityId(directMatch.id)
                  return
                }
              }
              if (!snippet) return
              const match = findMatchingEntity(snippet, enrichedEntities)
              if (match) setActiveEntityId(match.id)
            }}
          />

          {/* Right Pane: Review & Stepper Drawer */}
          <HitlReviewDrawer
            title={title}
            documentSubtype={activeSubtype}
            entities={enrichedEntities}
            activeEntityId={activeEntityId}
            onSelectEntity={handleSelectEntity}
            onAcceptEntity={handleAcceptEntity}
            onRejectEntity={handleRejectEntity}
            onMergeEntity={handleMergeEntity}
            onAssociateOrganization={handleAssociateOrganization}
            onEditEntity={handleEditEntity}
            onUndoEntity={handleUndoEntity}
            onUpdateMedicationFlags={handleUpdateMedicationFlags}
            onAcceptAllInCategory={handleAcceptAllInCategory}
            onApproveAndApply={handleApproveAndApply}
            onClose={onHide}
            onCancelAndDiscard={onCancelAndDiscard}
            availableOrganizations={availableOrganizations}
            user={user}
            isApplying={isApplying}
            isAnalyzing={isAnalyzing}
          />
        </div>
      </Modal.Body>
    </Modal>
  )
}

HitlReviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  documentData: PropTypes.shape({
    fileName: PropTypes.string,
    fileType: PropTypes.string,
    textContent: PropTypes.string,
    url: PropTypes.string,
    documents: PropTypes.arrayOf(PropTypes.object),
  }),
  initialEntities: PropTypes.arrayOf(PropTypes.object),
  rawExtractedData: PropTypes.object,
  existingCase: PropTypes.object,
  onApplyChanges: PropTypes.func.isRequired,
  onCancelAndDiscard: PropTypes.func,
  user: PropTypes.object,
  title: PropTypes.string,
  isAnalyzing: PropTypes.bool,
}

export async function applyAcceptedTasks(acceptedEntities, caseNumber, user) {
  if (!acceptedEntities || !Array.isArray(acceptedEntities) || !caseNumber) {
    return []
  }

  // Capability-based permission check
  if (user && !Permissions.can(user, CAPABILITIES.CREATE_TASK) && !Permissions.can(user, CAPABILITIES.UPDATE_CASE)) {
    // eslint-disable-next-line no-console
    console.warn('User does not have permission to create tasks (CAPABILITIES.CREATE_TASK required).')
    return []
  }

  const taskEntities = acceptedEntities.filter(
    e => (e.status === 'accepted' || e.status === 'edited') && e.category === 'task' && !e._appliedTask,
  )

  if (taskEntities.length === 0) {
    return []
  }

  const createdTasks = []

  for (const entity of taskEntities) {
    try {
      const title = String(entity.extractedValue || entity.attributes?.title || entity.field || 'Follow-up Task').trim()
      const description = entity.attributes?.description || entity.description || ''
      const leadDays = entity.attributes?.suggestedLeadDays || entity.attributes?.leadDays || 3
      const dueDate = entity.attributes?.dueDate
        ? new Date(entity.attributes.dueDate).toISOString()
        : new Date(Date.now() + leadDays * 86400000).toISOString()

      const providerNote = entity.attributes?.providerName
        ? `Associated Provider: ${entity.attributes.providerName}`
        : ''
      const sourceCitation = entity.sourceSnippet
        ? `Source: ${entity.documentName || 'Intake Document'}${entity.page ? ` (p. ${entity.page})` : ''} - "${
            entity.sourceSnippet
          }"`
        : ''
      const noteParts = [providerNote, sourceCitation].filter(Boolean)
      const note = noteParts.join(' | ')

      const instructions = entity.attributes?.providerName
        ? `<p>Associated Provider: <strong>${DOMPurify.sanitize(entity.attributes.providerName)}</strong></p>`
        : null

      const taskObj = {
        title: title.slice(0, 255),
        description: description ? description.slice(0, 2048) : null,
        dueDateUtc: dueDate,
        note: note ? note.slice(0, 2048) : null,
        instructions: instructions ? DOMPurify.sanitize(instructions) : null,
        activityType: entity.attributes?.activityType || entity.attributes?.category || 'Coordination of Services',
        relatedSection: entity.attributes?.relatedSection || 'documents',
        complete: false,
      }

      const res = await TasksService.create(taskObj, caseNumber)
      entity._appliedTask = true
      createdTasks.push(res)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to persist accepted task to case:', entity.extractedValue, err)
    }
  }

  return createdTasks
}

export function applyAcceptedContacts(acceptedEntities, updatedCase) {
  if (!updatedCase || typeof updatedCase !== 'object') return updatedCase
  const contactEntities = (acceptedEntities || []).filter(e => e.category === 'contact')
  if (contactEntities.length === 0) return updatedCase

  const existingContacts = Array.isArray(updatedCase.keyContacts) ? [...updatedCase.keyContacts] : []

  for (const contactEnt of contactEntities) {
    const attrs = contactEnt.attributes || {}
    const name = String(contactEnt.extractedValue || attrs.name || '').trim()
    if (!name) continue

    const role = String(attrs.role || 'Staff Contact').trim()
    const orgOrProv = String(attrs.organizationOrProviderName || attrs.organization || attrs.practiceName || '').trim()
    const phone = String(attrs.phone || '').trim()
    const extension = String(attrs.extension || '').trim()
    const fax = String(attrs.fax || '').trim()
    const email = String(attrs.email || '').trim()
    const directAddress = String(attrs.directAddress || '').trim()
    const notes = String(attrs.notes || '').trim()
    const addrObj = attrs.address || {}
    const line1 = String(attrs.line1 || addrObj.line1 || '').trim()
    const line2 = String(attrs.line2 || addrObj.line2 || '').trim()
    const city = String(attrs.city || addrObj.city || '').trim()
    const state = String(attrs.state || addrObj.state || '').trim()
    const zip = String(attrs.zip || addrObj.zip || '').trim()
    const hasAddr = Boolean(line1 || city || state || zip)

    const roleLower = role.toLowerCase()
    let inferredCategory = 'ANCILLARY'
    if (roleLower.includes('adjuster') || roleLower.includes('carrier') || roleLower.includes('claim')) {
      inferredCategory = 'CARRIER'
    } else if (roleLower.includes('employer') || roleLower.includes('hr') || roleLower.includes('supervisor')) {
      inferredCategory = 'EMPLOYER'
    } else if (roleLower.includes('attorney') || roleLower.includes('legal') || roleLower.includes('paralegal')) {
      inferredCategory = 'LEGAL'
    } else if (
      roleLower.includes('medical') ||
      roleLower.includes('nurse') ||
      roleLower.includes('doctor') ||
      roleLower.includes('provider') ||
      roleLower.includes('coordinator') ||
      roleLower.includes('scheduler') ||
      roleLower.includes('office manager') ||
      roleLower.includes('practice manager') ||
      roleLower.includes('clinic')
    ) {
      inferredCategory = 'MEDICAL'
    } else if (roleLower.includes('client') || roleLower.includes('family') || roleLower.includes('spouse')) {
      inferredCategory = 'CLIENT'
    }

    const contactItem = {
      id: contactEnt.duplicateMatch?.existingId || contactEnt.id || `kc-${Date.now()}`,
      name,
      role,
      category: attrs.category || inferredCategory,
      organization: orgOrProv,
      organizationOrProviderName: orgOrProv,
      phone,
      extension,
      fax,
      email,
      directAddress,
      address: hasAddr ? { line1, line2, city, state, zip } : null,
      notes,
    }

    const existingIdx = existingContacts.findIndex(
      kc => kc && ((kc.name || '').trim().toLowerCase() === name.toLowerCase() || isEquivalentName(kc.name, name)),
    )

    if (existingIdx >= 0) {
      const prev = existingContacts[existingIdx]
      existingContacts[existingIdx] = {
        ...prev,
        ...contactItem,
        id: prev.id || contactItem.id,
        organization: contactItem.organization || prev.organization || '',
        organizationOrProviderName:
          contactItem.organizationOrProviderName || prev.organizationOrProviderName || prev.organization || '',
        phone: contactItem.phone || prev.phone || '',
        extension: contactItem.extension || prev.extension || '',
        fax: contactItem.fax || prev.fax || '',
        email: contactItem.email || prev.email || '',
        directAddress: contactItem.directAddress || prev.directAddress || '',
        address: contactItem.address || prev.address || null,
        notes: contactItem.notes || prev.notes || '',
      }
    } else {
      existingContacts.push(contactItem)
    }
  }

  updatedCase.keyContacts = existingContacts
  return updatedCase
}

export async function applyAcceptedProviders(acceptedEntities, updatedCase, caseNumber) {
  applyAcceptedContacts(acceptedEntities, updatedCase)
  const providerEntities = [...(acceptedEntities || []).filter(e => e.category === 'provider')]
  const workStatusEntitiesWithProvider = (acceptedEntities || []).filter(
    e => e.category === 'status' && e.field === 'Work Status' && e.attributes?.treatmentProvider,
  )

  workStatusEntitiesWithProvider.forEach(wsEnt => {
    const wsProvName = String(wsEnt.attributes.treatmentProvider || '').trim()
    if (!wsProvName) return
    const alreadyInProviderEntities = providerEntities.some(
      pe =>
        (pe.extractedValue || '').trim().toLowerCase() === wsProvName.toLowerCase() ||
        isEquivalentName(pe.extractedValue, wsProvName),
    )
    if (!alreadyInProviderEntities) {
      providerEntities.push({
        category: 'provider',
        field: 'Treatment Provider',
        extractedValue: wsProvName,
        providerMatch: wsEnt.providerMatch || wsEnt.attributes?.providerMatch,
        attributes: {
          classification: detectProviderClassification(wsProvName),
          specialty: 'Treatment Provider',
        },
        sourceSnippet: wsEnt.sourceSnippet,
        _fromWorkStatusEntity: wsEnt,
      })
    }
  })

  if (providerEntities.length === 0) return updatedCase

  const orgEntities = providerEntities.filter(
    e =>
      !isCertificationOrCredential(e.extractedValue) &&
      (e.attributes?.classification || detectProviderClassification(e.extractedValue)) === 'Organization',
  )
  const indivEntities = providerEntities.filter(
    e =>
      !isCertificationOrCredential(e.extractedValue) &&
      (e.attributes?.classification || detectProviderClassification(e.extractedValue)) !== 'Organization',
  )

  const orgNameToId = new Map()

  // 1. Process Organizations First in Library
  for (const orgEnt of orgEntities) {
    const rawOrg = (orgEnt.extractedValue || '').trim()
    const orgName = stripCertificationOrTitle(rawOrg) || rawOrg
    if (!orgName || isCertificationOrCredential(orgName)) continue

    let orgId = orgEnt.providerMatch?.matchedId
    if (!orgId) {
      try {
        const searchRes = await ProviderService.search({ searchText: orgName, page: 1, perPage: 10 })
        const match = searchRes?.results?.find(
          p =>
            !p.isIndividual &&
            ((p.name && p.name.toLowerCase() === orgName.toLowerCase()) ||
              (p.organization && p.organization.toLowerCase() === orgName.toLowerCase()) ||
              isEquivalentName(p.name || p.organization, orgName)),
        )
        if (match?.id) {
          orgId = match.id
        } else {
          const created = await ProviderService.create({
            name: orgName,
            organizationName: orgName,
            classification: 'Organization',
            type: orgEnt.field || 'Treatment Provider',
            phone: orgEnt.attributes?.phone,
            fax: orgEnt.attributes?.fax,
            email: orgEnt.attributes?.email,
            website: orgEnt.attributes?.website,
            npi: orgEnt.attributes?.npi,
            addresses: orgEnt.attributes?.line1
              ? [
                  {
                    type: 'PrimaryPractice',
                    line1: orgEnt.attributes.line1,
                    line2: orgEnt.attributes.line2 || '',
                    city: orgEnt.attributes.city || '',
                    state: orgEnt.attributes.state || '',
                    zip: orgEnt.attributes.zip || '',
                  },
                ]
              : [],
          })
          if (created?.id) orgId = created.id
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Could not ensure organization in library:', err)
      }
    }
    if (orgId) {
      orgNameToId.set(orgName.toLowerCase(), orgId)
    }
  }

  // 2. Process Individuals Second
  for (const indivEnt of indivEntities) {
    const rawIndiv = (indivEnt.extractedValue || '').trim()
    const indivName = stripCertificationOrTitle(rawIndiv) || rawIndiv
    if (!indivName || isCertificationOrCredential(indivName)) continue

    const cleanName = indivName
      .replace(/,\s*(?:MD|M\.D\.|DO|D\.O\.|DC|PT|DPT|PA-C|PAC|PA|NP|FACS|FACR|PhD|CRNA)\b/i, '')
      .trim()
    const nameTokens = cleanName.split(/\s+/)
    const parsedFirst = nameTokens[0] || ''
    const parsedLast = nameTokens.length > 1 ? nameTokens.slice(1).join(' ') : ''

    const rawPractice = (indivEnt.attributes?.practiceName || '').trim()
    const cleanedPractice = stripCertificationOrTitle(rawPractice)
    const practiceName = isCertificationOrCredential(cleanedPractice) ? '' : cleanedPractice || rawPractice
    let parentOrgId = null
    if (practiceName) {
      parentOrgId = orgNameToId.get(practiceName.toLowerCase())
      if (!parentOrgId) {
        try {
          const searchRes = await ProviderService.search({ searchText: practiceName, page: 1, perPage: 10 })
          const match = searchRes?.results?.find(
            p =>
              !p.isIndividual &&
              ((p.name && p.name.toLowerCase() === practiceName.toLowerCase()) ||
                (p.organization && p.organization.toLowerCase() === practiceName.toLowerCase()) ||
                isEquivalentName(p.name || p.organization, practiceName)),
          )
          if (match?.id) {
            parentOrgId = match.id
          } else {
            const createdOrg = await ProviderService.create({
              name: practiceName,
              organizationName: practiceName,
              classification: 'Organization',
              type: indivEnt.field || 'Treatment Provider',
              addresses: indivEnt.attributes?.line1
                ? [
                    {
                      type: 'PrimaryPractice',
                      line1: indivEnt.attributes.line1,
                      line2: indivEnt.attributes.line2 || '',
                      city: indivEnt.attributes.city || '',
                      state: indivEnt.attributes.state || '',
                      zip: indivEnt.attributes.zip || '',
                    },
                  ]
                : [],
            })
            if (createdOrg?.id) parentOrgId = createdOrg.id
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Could not resolve parent org for individual:', err)
        }
        if (parentOrgId) orgNameToId.set(practiceName.toLowerCase(), parentOrgId)
      }
    }

    const existingTreatForMatch = updatedCase.treatmentProviders || []
    const existingProvForMatch = updatedCase.providers || []
    const existingOnCase =
      existingTreatForMatch.find(
        t => (t.name || '').toLowerCase() === indivName.toLowerCase() || isEquivalentName(t.name, indivName),
      ) ||
      existingProvForMatch.find(
        p => (p.name || '').toLowerCase() === indivName.toLowerCase() || isEquivalentName(p.name, indivName),
      )

    let indivId = indivEnt.providerMatch?.matchedId || existingOnCase?.id || existingOnCase?.providerId || null
    let canonicalIndivName = existingOnCase?.name || indivEnt.providerMatch?.matchedName || indivName

    if (!indivId && !existingOnCase && indivEnt.providerMatch?.type !== 'case') {
      try {
        const searchRes = await ProviderService.search({ searchText: indivName, page: 1, perPage: 10 })
        const match = searchRes?.results?.find(
          p =>
            p.isIndividual &&
            ((p.name && p.name.toLowerCase() === indivName.toLowerCase()) ||
              (p.sortName && p.sortName.toLowerCase() === indivName.toLowerCase()) ||
              isEquivalentName(p.name || p.sortName, indivName)),
        )
        if (match?.id) {
          indivId = match.id
          if (match.name) canonicalIndivName = match.name
          if (parentOrgId || practiceName) {
            try {
              const existing = await ProviderService.get(indivId)
              if (existing) {
                const currentAffs = existing.affiliations || []
                const hasAff = currentAffs.some(
                  a =>
                    (parentOrgId && a.organizationId === parentOrgId) ||
                    (practiceName &&
                      a.organizationName &&
                      a.organizationName.toLowerCase() === practiceName.toLowerCase()),
                )
                const updatedAffs = hasAff
                  ? currentAffs
                  : [
                      ...currentAffs,
                      {
                        organizationId: parentOrgId,
                        organizationName: practiceName,
                        role: indivEnt.field || 'Treatment Provider',
                        isPrimary: !existing.parentProviderId || currentAffs.length === 0,
                      },
                    ]
                await ProviderService.update(indivId, {
                  ...existing,
                  parentProviderId: existing.parentProviderId || parentOrgId,
                  organizationName: existing.organizationName || practiceName,
                  affiliations: updatedAffs,
                })
              }
            } catch {}
          }
        } else {
          const created = await ProviderService.create({
            name: indivName,
            firstName: parsedFirst,
            lastName: parsedLast,
            classification: 'Individual',
            isSoleProprietor: false,
            parentProviderId: parentOrgId,
            organizationName: practiceName,
            affiliations:
              parentOrgId || practiceName
                ? [
                    {
                      organizationId: parentOrgId,
                      organizationName: practiceName,
                      role: indivEnt.field || 'Treatment Provider',
                      isPrimary: true,
                    },
                  ]
                : [],
            type: indivEnt.field || 'Treatment Provider',
            specialties: indivEnt.attributes?.specialty,
            npi: indivEnt.attributes?.npi,
            phone: indivEnt.attributes?.phone,
            fax: indivEnt.attributes?.fax,
            email: indivEnt.attributes?.email,
            website: indivEnt.attributes?.website,
            addresses: indivEnt.attributes?.line1
              ? [
                  {
                    type: 'PrimaryPractice',
                    line1: indivEnt.attributes.line1,
                    line2: indivEnt.attributes.line2 || '',
                    city: indivEnt.attributes.city || '',
                    state: indivEnt.attributes.state || '',
                    zip: indivEnt.attributes.zip || '',
                  },
                ]
              : [],
          })
          if (created?.id) indivId = created.id
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Could not create/find individual provider in library:', err)
      }
    }

    if (caseNumber && indivId && !existingOnCase) {
      try {
        await ProviderService.linkToCase(caseNumber, indivId)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Could not link individual provider to case:', err)
      }
    }

    // Add/update in updatedCase.treatmentProviders
    const existingTreat = updatedCase.treatmentProviders || []
    const treatItem = {
      name: canonicalIndivName,
      companyName: practiceName || '',
      isSoleProprietor: false,
      phone: indivEnt.attributes?.phone,
      fax: indivEnt.attributes?.fax,
      email: indivEnt.attributes?.email,
      address: indivEnt.attributes?.line1
        ? {
            line1: indivEnt.attributes.line1,
            city: indivEnt.attributes.city,
            state: indivEnt.attributes.state,
            zip: indivEnt.attributes.zip,
          }
        : null,
      type: 'Treatment Provider',
    }
    const idx = existingTreat.findIndex(
      t =>
        (t.name || '').toLowerCase() === canonicalIndivName.toLowerCase() ||
        (t.name || '').toLowerCase() === indivName.toLowerCase() ||
        isEquivalentName(t.name, indivName),
    )
    if (idx >= 0) {
      existingTreat[idx] = { ...existingTreat[idx], ...treatItem, name: existingTreat[idx].name || canonicalIndivName }
      canonicalIndivName = existingTreat[idx].name
    } else {
      existingTreat.push(treatItem)
    }
    updatedCase.treatmentProviders = existingTreat

    // Add/update in updatedCase.providers
    const existingProvList = updatedCase.providers || []
    const provItem = {
      id: indivId || null,
      providerId: indivId || null,
      parentProviderId: parentOrgId || null,
      name: canonicalIndivName,
      firstName: parsedFirst || null,
      lastName: parsedLast || null,
      providerType: indivEnt.field || 'Treatment Provider',
      npi: indivEnt.attributes?.npi,
      classification: 'Individual',
      isSoleProprietor: false,
      specialty: indivEnt.attributes?.specialty || 'Treatment Provider',
      practiceName,
      phone: indivEnt.attributes?.phone,
      fax: indivEnt.attributes?.fax,
      email: indivEnt.attributes?.email,
      website: indivEnt.attributes?.website,
      address: indivEnt.attributes?.line1
        ? {
            line1: indivEnt.attributes.line1,
            line2: indivEnt.attributes.line2 || '',
            city: indivEnt.attributes.city || '',
            state: indivEnt.attributes.state || '',
            zip: indivEnt.attributes.zip || '',
          }
        : null,
      isNpiVerified: indivEnt.isNpiVerified,
      sourceTextSnippet: indivEnt.sourceSnippet,
    }
    const pIdx = existingProvList.findIndex(
      p =>
        (p.name || '').toLowerCase() === canonicalIndivName.toLowerCase() ||
        (p.name || '').toLowerCase() === indivName.toLowerCase() ||
        isEquivalentName(p.name, indivName),
    )
    if (pIdx >= 0) {
      existingProvList[pIdx] = {
        ...existingProvList[pIdx],
        ...provItem,
        name: existingProvList[pIdx].name || canonicalIndivName,
      }
    } else {
      existingProvList.push(provItem)
    }
    updatedCase.providers = existingProvList

    // Sync canonical provider name onto updatedCase.status if applicable
    if (updatedCase.status && typeof updatedCase.status === 'object') {
      if (
        updatedCase.status.treatmentProvider &&
        (updatedCase.status.treatmentProvider.toLowerCase() === indivName.toLowerCase() ||
          isEquivalentName(updatedCase.status.treatmentProvider, indivName))
      ) {
        updatedCase.status.treatmentProvider = canonicalIndivName
      }
      if (Array.isArray(updatedCase.status.history)) {
        updatedCase.status.history = updatedCase.status.history.map(h =>
          h?.treatmentProvider &&
          (h.treatmentProvider.toLowerCase() === indivName.toLowerCase() ||
            isEquivalentName(h.treatmentProvider, indivName))
            ? { ...h, treatmentProvider: canonicalIndivName }
            : h,
        )
      }
    }
  }

  // 3. Process standalone organizations (only link if no child individual is on the case)
  for (const orgEnt of orgEntities) {
    const orgName = (orgEnt.extractedValue || '').trim()
    if (!orgName) continue

    const hasAssociatedIndiv = indivEntities.some(
      ind => (ind.attributes?.practiceName || '').trim().toLowerCase() === orgName.toLowerCase(),
    )
    if (hasAssociatedIndiv) {
      continue
    }

    const orgId = orgNameToId.get(orgName.toLowerCase())
    if (caseNumber && orgId) {
      try {
        await ProviderService.linkToCase(caseNumber, orgId)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Could not link organization provider to case:', err)
      }
    }

    const existingTreat = updatedCase.treatmentProviders || []
    if (!existingTreat.some(t => (t.name || t.companyName || '').toLowerCase() === orgName.toLowerCase())) {
      existingTreat.push({
        name: orgName,
        companyName: orgName,
        phone: orgEnt.attributes?.phone,
        fax: orgEnt.attributes?.fax,
        email: orgEnt.attributes?.email,
        address: orgEnt.attributes?.line1
          ? {
              line1: orgEnt.attributes.line1,
              city: orgEnt.attributes.city,
              state: orgEnt.attributes.state,
              zip: orgEnt.attributes.zip,
            }
          : null,
        type: 'Treatment Provider',
      })
      updatedCase.treatmentProviders = existingTreat
    }

    const existingProvList = updatedCase.providers || []
    if (!existingProvList.some(p => (p.name || p.practiceName || '').toLowerCase() === orgName.toLowerCase())) {
      existingProvList.push({
        name: orgName,
        providerType: orgEnt.field || 'Treating Facility',
        npi: orgEnt.attributes?.npi,
        classification: 'Organization',
        specialty: orgEnt.attributes?.specialty || 'Treating Facility',
        practiceName: orgName,
        phone: orgEnt.attributes?.phone,
        fax: orgEnt.attributes?.fax,
        email: orgEnt.attributes?.email,
        website: orgEnt.attributes?.website,
        address: orgEnt.attributes?.line1
          ? {
              line1: orgEnt.attributes.line1,
              line2: orgEnt.attributes.line2 || '',
              city: orgEnt.attributes.city || '',
              state: orgEnt.attributes.state || '',
              zip: orgEnt.attributes.zip || '',
            }
          : null,
        isNpiVerified: orgEnt.isNpiVerified,
        sourceTextSnippet: orgEnt.sourceSnippet,
      })
      updatedCase.providers = existingProvList
    }
  }

  // 4. Deduplicate treatmentProviders to ensure standalone organization is not duplicated
  if (Array.isArray(updatedCase.treatmentProviders)) {
    const indivCompanyNames = new Set(
      updatedCase.treatmentProviders
        .filter(t => t.name && t.companyName && t.name.toLowerCase() !== t.companyName.toLowerCase())
        .map(t => t.companyName.toLowerCase()),
    )
    updatedCase.treatmentProviders = updatedCase.treatmentProviders.filter(t => {
      if (!t.name) return false
      const isStandaloneOrg = (t.name || '').toLowerCase() === (t.companyName || '').toLowerCase()
      if (isStandaloneOrg && indivCompanyNames.has(t.name.toLowerCase())) {
        return false
      }
      return true
    })
  }

  return updatedCase
}

/**
 * Normalizes Review of Systems and Functional Status fields against the CaseSchema.json definition,
 * fixing LLM spelling errors (e.g. musculeskeletal -> musculoskeletal) and pruning unrecognized keys.
 */
export function sanitizeCaseSystemsAndFunctional(updatedCase) {
  if (!updatedCase || typeof updatedCase !== 'object') return updatedCase

  if (updatedCase.systems && typeof updatedCase.systems === 'object') {
    const cleanSystems = {}
    const ALLOWED_SYSTEMS = [
      'general',
      'eyes',
      'skin',
      'nose',
      'head',
      'ears',
      'throat',
      'neurological',
      'cardiac',
      'pulmonary',
      'gigu',
      'musculoskeletal',
    ]
    Object.entries(updatedCase.systems).forEach(([key, val]) => {
      const lowerKey = key.toLowerCase()
      if (lowerKey === 'musculeskeletal' || lowerKey === 'musculoskeletal') {
        cleanSystems.musculoskeletal = cleanSystems.musculoskeletal || val
      } else if (ALLOWED_SYSTEMS.includes(lowerKey)) {
        cleanSystems[lowerKey] = val
      }
    })
    updatedCase.systems = cleanSystems
  }

  if (updatedCase.functional && typeof updatedCase.functional === 'object') {
    const ALLOWED_FUNCTIONAL = [
      'selfcare',
      'adls',
      'behavior',
      'psychological',
      'mobility',
      'limitations',
      'sleep',
      'other',
      'estimatedDateOfMMI',
      'mmiNarrative',
      'bbmanagement',
      'nutrition',
      'medication',
      'cognition',
      'pain',
      'household',
      'communication',
    ]
    const cleanFunc = {}
    Object.entries(updatedCase.functional).forEach(([key, val]) => {
      const lowerKey = key.toLowerCase()
      const match = ALLOWED_FUNCTIONAL.find(f => f.toLowerCase() === lowerKey)
      if (match) {
        cleanFunc[match] = val
      }
    })
    updatedCase.functional = cleanFunc
  }

  return updatedCase
}
